/**
 * Les appels à l'API.
 *
 * Tout passe par `/api/...` sans jamais nommer d'hôte : en développement, Vite
 * fait suivre ; en production, le proxy inverse. Une adresse écrite ici obligerait
 * à recompiler l'interface pour chaque société qui l'installe.
 *
 * Les formes reçues viennent du contrat partagé (`@planif/moteur`) : elles ne sont
 * pas redéclarées ici, sans quoi une évolution de l'API se découvrirait à l'écran
 * plutôt qu'à la compilation.
 */
import type {
  BaremePeriode,
  Generique,
  GroupeAdmin,
  ReponseChantiers,
  ReponseComparaison,
  ReponseGeneriques,
  ReponseGroupes,
  ReponseImport,
  ReponseVersionCreee,
  TypeGenerique,
  ZonesChantier,
} from "@planif/moteur";
import { envoiJson } from "./corpsJson";

/**
 * Un appel qui a échoué doit dire POURQUOI, en français.
 *
 * « Failed to fetch » dans une console de navigateur n'aide personne : le cas le
 * plus fréquent, de loin, est que l'API n'est pas démarrée. Le message le dit.
 */
async function obtenir<T>(chemin: string): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, { headers: { Accept: "application/json" } });
  } catch {
    throw new Error(
      "Le serveur ne répond pas. Est-il démarré ? (pnpm dev:api, dans une autre console)",
    );
  }

  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a répondu ${reponse.status}.`);
  }

  return (await reponse.json()) as T;
}

export function chargerChantiers(): Promise<ReponseChantiers> {
  return obtenir<ReponseChantiers>("/api/chantiers");
}

/** Ce qu'une suppression de chantier a effacé. */
export type ChantierSupprime = {
  readonly apo: string;
  readonly nom: string;
  readonly versions: number;
  readonly debourses: number;
  readonly postes: number;
  readonly lignes: number;
  readonly barres: number;
  readonly livraisons: number;
  readonly transports: number;
  readonly affectations: number;
};

/**
 * Supprimer un chantier : devis figé, copies de travail, planning, équipe.
 *
 * Le n° d'affaire repart dans le corps et le serveur le revérifie. Ce n'est pas une
 * redondance avec la confirmation de l'écran : une suppression que la seule URL
 * suffirait à déclencher partirait un jour d'un onglet resté ouvert.
 */
export function supprimerChantier(chantierId: string, apo: string): Promise<ChantierSupprime> {
  return envoyer<ChantierSupprime>(`/api/chantiers/${chantierId}`, "DELETE", { apo });
}

/* ---------------------------------------------------------------------------
 * Les études du bureau d'études (095)
 * ------------------------------------------------------------------------- */

/** Le code de la prochaine étude (ETUDE0001, ETUDE0002…), pour l'écran d'import. */
export function chargerProchainCodeEtude(): Promise<{ readonly code: string }> {
  return obtenir("/api/etudes/prochain-code");
}

/** Rattacher l'APO à une étude : elle devient une affaire, et passe à la référente. */
export function rattacherApo(
  chantierId: string,
  apo: string,
): Promise<{ readonly apo: string; readonly etude: string }> {
  return envoyer(`/api/chantiers/${chantierId}/apo`, "POST", { apo });
}

/** La version demandée ET celle dont elle est issue : le vis-à-vis du §4.2. */
export function chargerVersion(versionId: string): Promise<ReponseComparaison> {
  return obtenir<ReponseComparaison>(`/api/versions/${versionId}/comparaison`);
}

/* ---------------------------------------------------------------------------
 * L'import d'un déboursé
 * ------------------------------------------------------------------------- */

/**
 * Envoie un classeur DEPO et rend le compte rendu de son import.
 *
 * Le fichier part en `FormData`, donc en multipart : c'est ce que le navigateur
 * sait faire d'un `<input type="file">` sans le recoder, et le contenu binaire
 * traverse tel quel. L'encoder en base64 dans un JSON coûterait un tiers de
 * volume et une conversion de plus, pour rien.
 *
 * Aucun `Content-Type` n'est posé à la main : c'est le navigateur qui écrit
 * l'en-tête, avec la frontière qu'il vient de tirer au sort. Le fixer ici
 * produirait un corps que le serveur ne saurait pas découper.
 */
export async function importerDepo(demande: {
  fichier: File;
  apo: string;
  nom: string;
  client: string;
  rang: string;
}): Promise<ReponseImport> {
  const corps = new FormData();
  // Les champs partent AVANT le fichier : le serveur lit les parties dans
  // l'ordre d'arrivée, et une erreur de saisie se voit alors sans avoir attendu
  // le téléversement complet du classeur.
  corps.append("apo", demande.apo);
  corps.append("nom", demande.nom);
  corps.append("client", demande.client);
  corps.append("rang", demande.rang);
  corps.append("fichier", demande.fichier, demande.fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch("/api/imports", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error(
      "Le serveur ne répond pas : rien n'a été importé. Est-il démarré ? " +
        "(pnpm dev:api, dans une autre console)",
    );
  }

  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé l'import (${reponse.status}).`);
  }
  return (await reponse.json()) as ReponseImport;
}

/* ---------------------------------------------------------------------------
 * Le référentiel des ressources génériques
 * ------------------------------------------------------------------------- */

/**
 * Le classeur d'une des deux listes de ressources génériques.
 *
 * *Demande du métier, 01/09/2026 : « il faut donner la possibilité d'exporter les listes de
 * matériels, main d'œuvre et tarif sur excel, mettre un bouton export excel sur chaque liste. »*
 *
 * Un lien nu, comme pour les carrières et la grille tarifaire : c'est le navigateur qui
 * enregistre, avec le nom que l'en-tête annonce.
 */
export function adresseClasseurGeneriques(type: "MO" | "MAT"): string {
  return `/api/generiques/${type}/classeur`;
}

export function chargerGeneriques(): Promise<ReponseGeneriques> {
  return obtenir<ReponseGeneriques>("/api/generiques");
}

/**
 * Ce qui distingue deux absences, sans rien dire de médical.
 *
 * *« Distingue absence maladie et congé sans parler de motif » (métier, 16/08/2026).*
 * Le serveur reclasse la valeur du référentiel dans cette liste fermée ; le libellé
 * d'origine ne traverse jamais.
 */
export type NatureAbsence = "arret" | "conge" | "autre";

/**
 * La capacité d'un code de main-d'œuvre, un jour donné — aucune identité.
 *
 * Les heures sont **nettes** : congés et arrêts déjà déduits par le référentiel, comme
 * le classeur du métier le demande (« NET (BRUT − maladie et congé) »). La répartition
 * interne / externe est reconstituée sur le serveur, à partir du détail nominatif que
 * l'écran ne voit jamais.
 */
export type CapaciteJour = {
  readonly date: string;
  readonly codeMo: string;
  readonly heuresInterne: string;
  readonly heuresExterne: string;
  readonly effectifInterne: number;
  readonly effectifExterne: number;
  readonly indisponibles: number;
};

/** La capacité de la période. Séparée du plan : une panne du référentiel ne l'emporte pas. */
export function chargerCapacite(
  debut: string,
  fin: string,
): Promise<{ capacite: readonly CapaciteJour[] }> {
  const parametres = new URLSearchParams({ debut, fin });
  return obtenir<{ capacite: readonly CapaciteJour[] }>(`/api/capacite?${parametres.toString()}`);
}

/** Une absence : qui, quel jour, de quelle nature, validée ou non. */
export type Indisponibilite = {
  readonly salarieId: string;
  readonly date: string;
  readonly nature: NatureAbsence;
  /**
   * Faux = absence saisie, pas encore validée par la hiérarchie. Comptée quand même.
   *
   * *« La précision congé non validé ou validé est importante » (métier, 16/08/2026).* Un
   * congé validé est acquis — il faut remplacer la personne ; un congé demandé et non
   * encore validé est une décision qui appartient encore à l'entreprise.
   */
  readonly valide: boolean;
};

/**
 * Les absences de la période — congés et arrêts (métier, 16/08/2026).
 *
 * Un appel à part de celui du plan : une panne du référentiel maître ne doit pas emporter
 * le tableau de bord, seulement le marquage « absent ».
 */
export function chargerIndisponibilites(
  debut: string,
  fin: string,
): Promise<{
  readonly indisponibilites: readonly Indisponibilite[];
  /**
   * Les absences que le référentiel n'impute à personne — donc invisibles ici.
   *
   * Elles ne peuvent être ni affichées ni signalées : c'est le seul chiffre qui distingue
   * « aucune absence » de « des absences que je n'ai pas reçues ».
   */
  readonly sansSalarie: number;
}> {
  const parametres = new URLSearchParams({ debut, fin });
  return obtenir(`/api/indisponibilites?${parametres.toString()}`);
}

/**
 * Envoie une modification et rend ce que le serveur a réellement enregistré.
 *
 * Le retour n'est pas décoratif : le serveur normalise (un coût saisi « 40,5 »
 * revient « 40.5000 »), et afficher la saisie plutôt que la réponse laisserait
 * l'écran montrer une valeur qui n'est pas celle de la base.
 */
async function envoyer<T>(
  chemin: string,
  // DELETE en fait partie depuis la suppression de chantier : elle porte un corps, la
  // confirmation par n° d'affaire, et suit donc le même chemin que les deux autres.
  // `PUT` sert à nommer le responsable d'une fonction : le geste REMPLACE le titulaire, il
  // n'ajoute pas une ligne — c'est exactement ce que la méthode annonce.
  methode: "POST" | "PATCH" | "PUT" | "DELETE",
  corps: unknown,
): Promise<T> {
  let reponse: Response;
  // L'en-tête suit le corps, jamais l'inverse : annoncer du JSON sans en envoyer fait
  // refuser la requête par Fastify AVANT la route, en 400. Voir `corpsJson.ts`.
  const { headers, body } = envoiJson(corps);
  try {
    reponse = await fetch(chemin, { method: methode, headers, body });
  } catch {
    throw new Error("Le serveur ne répond pas : la modification n'a pas été enregistrée.");
  }

  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé la demande (${reponse.status}).`);
  }
  return (await reponse.json()) as T;
}

export function creerGenerique(demande: {
  type: TypeGenerique;
  code: string;
  libelle: string;
  coutHoraire: string | null;
}): Promise<Generique> {
  return envoyer<Generique>("/api/generiques", "POST", demande);
}

export function modifierGenerique(
  id: string,
  champs: Record<string, unknown>,
): Promise<Generique> {
  return envoyer<Generique>(`/api/generiques/${id}`, "PATCH", champs);
}

/* ---------------------------------------------------------------------------
 * Les paramètres d'un chantier
 * ------------------------------------------------------------------------- */

/**
 * Enregistre l'adresse, la date de démarrage ou le statut d'un chantier.
 *
 * Les champs absents ne sont pas touchés, et une chaîne vide efface. L'appelant
 * envoie donc ce que la fiche affiche, pas seulement ce qui a changé : c'est le
 * serveur qui compare et n'écrit que la différence.
 */
export function modifierChantier(
  id: string,
  champs: Record<string, unknown>,
): Promise<{ chantier: unknown; modifie: boolean }> {
  return envoyer<{ chantier: unknown; modifie: boolean }>(
    `/api/chantiers/${id}`,
    "PATCH",
    champs,
  );
}

/**
 * Une commune proposée pour la ville du chantier (058).
 *
 * La liste est **fermée** *(métier, 02/09/2026 : « si on ne trouve pas la ville cela veut dire
 * qu'il faut aller créer la ville dans les tables distance »)*, donc ce que cette recherche ne
 * rend pas ne se saisit pas — et l'écran doit le dire, sans quoi on croirait à une panne de
 * frappe.
 */
export type CommuneProposee = {
  readonly id: string;
  readonly nom: string;
  readonly codePostal: string;
  readonly codeDepartement: string | null;
  readonly latitude: string | null;
  readonly longitude: string | null;
};

/**
 * Les communes qui correspondent à ce qu'on tape, dans le référentiel des tables de distances.
 *
 * Le serveur borne et trie — 4 052 communes ne descendent pas dans un navigateur pour une liste
 * de propositions.
 */
export function chercherCommunes(recherche: string): Promise<{ communes: CommuneProposee[] }> {
  return obtenir<{ communes: CommuneProposee[] }>(
    `/api/communes?recherche=${encodeURIComponent(recherche)}`,
  );
}

/**
 * Corrige la référence d'un déboursé — le `DEPO…` de l'ERP.
 *
 * Elle vaut pour les imports où l'extraction n'a rien trouvé et a posé
 * « SANS-REFERENCE-0 ». Ce n'est qu'une étiquette : aucun calcul ne la lit, et la corriger ne
 * touche ni la contre-étude, ni le planning — c'est ce qui permet de la réparer sans réimporter
 * le chantier, ce qui emporterait tout le travail déjà fait.
 */
export function renommerDepo(
  id: string,
  numeroDepo: string,
): Promise<{ numeroDepo: string; modifie: boolean }> {
  return envoyer<{ numeroDepo: string; modifie: boolean }>(`/api/depos/${id}`, "PATCH", {
    numeroDepo,
  });
}

/* ---------------------------------------------------------------------------
 * Les salariés et les affectations
 * ------------------------------------------------------------------------- */

/**
 * Un salarié affectable, tel que le serveur le relaie.
 *
 * Le navigateur n'appelle **jamais** le référentiel maître directement : sa clé
 * d'accès ne quitte pas le serveur (`.env.example` : « variable SERVEUR
 * uniquement »). Une clé dans un bundle est publique.
 */
export type Salarie = {
  readonly id: string;
  readonly nom: string;
  readonly prenom: string;
  readonly codeMo: string | null;
  readonly poste: string | null;
  readonly societe: string | null;
};

export type Affectation = {
  readonly id: string;
  /** Le code MO du rôle, ou `null` pour « responsable ». C'est le FILTRE, plus l'identité. */
  readonly codeMo: string | null;
  /**
   * Le rôle précis, quand le code en porte plusieurs (056). `null` = le rôle unique du code.
   *
   * Deux fonctions peuvent partager un code — l'assistante administrative et l'assistante
   * référente sur `_MOIADMTRX` : sans ce champ, on saurait que deux personnes sont nommées et
   * pas laquelle tient quel rôle.
   */
  readonly fonctionId: string | null;
  readonly salarieId: string;
  readonly nom: string;
  readonly prenom: string;
  readonly poste: string | null;
  readonly societe: string | null;
};

export function chargerSalaries(): Promise<{ salaries: readonly Salarie[] }> {
  return obtenir<{ salaries: readonly Salarie[] }>("/api/salaries");
}

export function chargerAffectations(
  chantierId: string,
): Promise<{ affectations: readonly Affectation[] }> {
  return obtenir<{ affectations: readonly Affectation[] }>(
    `/api/chantiers/${chantierId}/affectations`,
  );
}

/** `deja` vrai = cette personne tenait déjà ce rôle : ce n'est pas une erreur. */
/**
 * Nomme quelqu'un sur un rôle du chantier.
 *
 * `fonctionId` désigne le rôle précis quand le code en déclare plusieurs (056) ; `null` vaut pour
 * le rôle unique d'un code. Le serveur refuse les combinaisons qui ne se rangeraient nulle part :
 * un code à plusieurs rôles sans rôle donné, un rôle sur un code qui n'en déclare aucun, ou un
 * rôle qui appartient à un autre code.
 */
export function affecter(
  chantierId: string,
  codeMo: string | null,
  salarieId: string,
  fonctionId: string | null = null,
): Promise<{ affectation?: Affectation; deja?: boolean }> {
  return envoyer(`/api/chantiers/${chantierId}/affectations`, "POST", {
    codeMo,
    salarieId,
    fonctionId,
  });
}

/* ---------------------------------------------------------------------------
 * Les fonctions à nommer (056)
 * -------------------------------------------------------------------------
 *
 * *Demande de Chris, 02/09/2026 : trois lignes de plus dans « Équipe du chantier », dont deux qui
 * partagent le filtre `_MOIADMTRX` — l'assistante administrative et l'assistante référente.*
 *
 * Le code de main-d'œuvre n'est donc plus l'identité du rôle, il n'en est que le **filtre** :
 * celui qui décide quels salariés se proposent. Un code coché « à nommer » sans rôle déclaré en
 * porte **un**, libellé par le référentiel — le comportement d'avant, mot pour mot.
 */

export type FonctionANommer = {
  /** `null` = le rôle unique d'un code qui ne déclare rien. */
  readonly id: string | null;
  readonly codeMo: string;
  readonly libelle: string;
  /** Vrai quand plusieurs rôles partagent ce code : le vivier de candidats est le même. */
  readonly partageLeCode: boolean;
  /**
   * L'étape du circuit des heures que cette ligne d'équipe tient, ou nul (085).
   *
   * La personne nommée à ce rôle sur un chantier valide les heures de ce chantier à cette étape.
   * Un seul rôle par étape pour toute l'installation. **Une ligne sans `id` peut la tenir** : un
   * code qui ne déclare qu'un rôle est désignable comme les autres.
   */
  readonly valideLesHeures: "conducteur" | "assistante" | null;
};

export function chargerFonctionsANommer(): Promise<{ fonctions: readonly FonctionANommer[] }> {
  return obtenir("/api/generiques/fonctions");
}

export function declarerFonction(codeMo: string, libelle: string): Promise<FonctionANommer> {
  return envoyer<FonctionANommer>("/api/generiques/fonctions", "POST", { codeMo, libelle });
}

/** Le code d'un rôle ne se change pas : ce serait un autre rôle, sur un autre vivier. */
export function renommerFonction(id: string, libelle: string): Promise<FonctionANommer> {
  return envoyer<FonctionANommer>(`/api/generiques/fonctions/${id}`, "PATCH", { libelle });
}

export function retirerFonction(id: string): Promise<void> {
  return sansCorps(`/api/generiques/fonctions/${id}`, "DELETE");
}

/**
 * Désigne — ou retire — le rôle qui valide les heures à une étape (085).
 *
 * La ligne se nomme par **(codeMo, fonctionId)**, et `fonctionId` vaut `null` pour un code qui ne
 * déclare qu'un rôle. C'est la correction de la 084 : elle adressait la ligne par son
 * identifiant, or le conducteur de travaux n'en a pas — son étape était indésignable.
 *
 * `etape: null` retire la désignation. Placée **après** `retirerFonction` et non à côté du
 * renommage : un cas de test lit le source qui suit `renommerFonction` pour garantir qu'un
 * renommage n'emporte jamais de code, et cette fonction-ci en porte un.
 */
export function designerEtapeDesHeures(
  cible: { codeMo: string; fonctionId: string | null },
  etape: "conducteur" | "assistante" | null,
): Promise<FonctionANommer> {
  return envoyer<FonctionANommer>("/api/generiques/etapes-heures", "PUT", {
    codeMo: cible.codeMo,
    fonctionId: cible.fonctionId,
    etape,
  });
}

export async function retirerAffectation(
  chantierId: string,
  affectationId: string,
): Promise<void> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/chantiers/${chantierId}/affectations/${affectationId}`, {
      method: "DELETE",
    });
  } catch {
    throw new Error("Le serveur ne répond pas : l'affectation n'a pas été retirée.");
  }
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refusé la demande (${reponse.status}).`);
  }
}

/* ---------------------------------------------------------------------------
 * Le référentiel des carrières
 * ------------------------------------------------------------------------- */

/**
 * Les deux natures d'un site de provenance (039).
 *
 * `carriere` produit du matériau — elle a une colonne dans la grille tarifaire. `depot`
 * stocke sans produire : une plateforme, un stock d'articles. Les deux se proposent de la
 * même façon sur une ligne de contre-étude et sur une pastille.
 */
export type NatureSite = "carriere" | "depot";

export type Carriere = {
  readonly id: string;
  readonly code: string;
  readonly denomination: string;
  readonly nature: NatureSite;
  readonly adresse: string | null;
  readonly codePostal: string | null;
  readonly commune: string | null;
  /**
   * Le point de CHARGEMENT, saisi (045) — pas forcément l'adresse postale.
   *
   * *« Dans la fiche de la carrière et site rajouter les champs latitude et longitude »*
   * (métier, 21/08/2026). Sur une carrière, le point utile est la bascule ou l'entrée du site,
   * qui n'a pas de numéro dans la rue.
   */
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly note: string | null;
  readonly actif: boolean;
  /**
   * Ce site tient un magasin (078).
   *
   * Indépendant de `nature`, qui commande la grille tarifaire : une carrière peut tenir un
   * magasin, un dépôt peut n'en tenir aucun. C'est cette case qui ouvre le site au module de
   * stock — entrées, sorties, réservations, inventaires.
   */
  readonly stocke: boolean;
  readonly modifieLe: string;
};

export type BilanImportCarrieres = {
  readonly ajoutees: number;
  readonly modifiees: number;
  readonly inchangees: number;
  readonly refusees: readonly { ligne: number; raison: string }[];
};

export function chargerCarrieres(): Promise<{ carrieres: readonly Carriere[] }> {
  return obtenir<{ carrieres: readonly Carriere[] }>("/api/carrieres");
}

export function creerCarriere(demande: {
  code: string;
  denomination: string;
  nature?: NatureSite;
  commune?: string;
}): Promise<Carriere> {
  return envoyer<Carriere>("/api/carrieres", "POST", demande);
}

export function modifierCarriere(id: string, champs: Record<string, unknown>): Promise<Carriere> {
  return envoyer<Carriere>(`/api/carrieres/${id}`, "PATCH", champs);
}

export async function supprimerCarriere(id: string): Promise<void> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/carrieres/${id}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été supprimé.");
  }
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refusé la suppression (${reponse.status}).`);
  }
}

/**
 * Le classeur du référentiel, tel que le serveur le fabrique.
 *
 * Le lien de téléchargement est simplement l'adresse de la route : c'est le
 * navigateur qui enregistre le fichier, avec le nom que l'en-tête annonce. Passer
 * par `fetch` obligerait à recréer un objet URL et un clic simulé, pour le même
 * résultat.
 */
export const ADRESSE_CLASSEUR_CARRIERES = "/api/carrieres/classeur";

/**
 * Le classeur des plannings d'un chantier — **toutes ses versions**.
 *
 * *Demande du métier, 16/08/2026 : « il faut avoir un bouton pour faire un export
 * Excel des plannings sur toutes les versions. »*
 *
 * La maille est le CHANTIER et non la version affichée : ce qu'on veut comparer, ce
 * sont les scénarios entre eux, et deux téléchargements faits à deux instants ne se
 * comparent pas. Un onglet par version, dans un seul classeur pris d'une seule lecture.
 *
 * Un lien nu, comme pour le référentiel des carrières : c'est le navigateur qui
 * enregistre, avec le nom que l'en-tête annonce.
 */
export function adresseClasseurPlanning(chantierId: string): string {
  return `/api/chantiers/${chantierId}/planning.xlsx`;
}

export async function importerCarrieres(fichier: File): Promise<BilanImportCarrieres> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(ADRESSE_CLASSEUR_CARRIERES, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanImportCarrieres;
}

/* ---------------------------------------------------------------------------
 * La grille tarifaire des carrières
 * ------------------------------------------------------------------------- */

export type PeriodeTarif = {
  readonly id: string;
  /** `AAAA-MM-JJ` : ce que `<input type="date">` lit et écrit, sans conversion. */
  readonly debut: string;
  readonly fin: string;
  readonly libelle: string;
};

export type ArticleTarif = {
  readonly id: string;
  readonly code: string;
  readonly libelle: string;
  readonly actif: boolean;
};

export type PrixGrille = {
  readonly articleId: string;
  readonly carriereId: string;
  readonly prix: string;
};

export type Grille = {
  readonly periodes: readonly PeriodeTarif[];
  readonly periode: PeriodeTarif | null;
  readonly articles: readonly ArticleTarif[];
  readonly prix: readonly PrixGrille[];
  /**
   * Les bornes que le formulaire « nouvelle période » propose.
   *
   * Elles viennent du **serveur** et ne se calculent pas ici : la même règle que le panneau
   * de période (`Periode.tsx`). Un poste réglé sur la veille proposerait une année tarifaire
   * décalée sans que rien ne le dise.
   */
  readonly periodeProposee: { readonly debut: string; readonly fin: string; readonly libelle: string };
};

export type BilanGrille = {
  readonly articlesAjoutes: number;
  readonly articlesModifies: number;
  readonly prixPoses: number;
  readonly prixInchanges: number;
  readonly casesVides: number;
  readonly refusees: readonly { ligne: number; raison: string }[];
};

export function chargerGrille(periodeId?: string | null): Promise<Grille> {
  const suffixe = periodeId === undefined || periodeId === null ? "" : `?periode=${periodeId}`;
  return obtenir<Grille>(`/api/tarifs${suffixe}`);
}

export function creerPeriodeTarif(demande: {
  libelle: string;
  debut: string;
  fin: string;
}): Promise<PeriodeTarif> {
  return envoyer<PeriodeTarif>("/api/tarifs/periodes", "POST", demande);
}

export function modifierPeriodeTarif(
  id: string,
  champs: Record<string, unknown>,
): Promise<PeriodeTarif> {
  return envoyer<PeriodeTarif>(`/api/tarifs/periodes/${id}`, "PATCH", champs);
}

export function dupliquerPeriodeTarif(
  id: string,
  demande: { libelle: string; debut: string; fin: string },
): Promise<{ periode: PeriodeTarif; prix: number }> {
  return envoyer<{ periode: PeriodeTarif; prix: number }>(
    `/api/tarifs/periodes/${id}/duplication`,
    "POST",
    demande,
  );
}

export function supprimerPeriodeTarif(id: string): Promise<{ supprimee: true }> {
  return envoyer<{ supprimee: true }>(`/api/tarifs/periodes/${id}`, "DELETE", {});
}

export function creerArticleTarif(demande: {
  code: string;
  libelle: string;
}): Promise<ArticleTarif> {
  return envoyer<ArticleTarif>("/api/tarifs/articles", "POST", demande);
}

export function modifierArticleTarif(
  id: string,
  champs: Record<string, unknown>,
): Promise<ArticleTarif> {
  return envoyer<ArticleTarif>(`/api/tarifs/articles/${id}`, "PATCH", champs);
}

export function supprimerArticleTarif(id: string): Promise<{ supprime: true }> {
  return envoyer<{ supprime: true }>(`/api/tarifs/articles/${id}`, "DELETE", {});
}

/** `prix` vide retire le prix : la case redevient vide, elle ne passe pas à zéro. */
export function fixerPrixTarif(demande: {
  periodeId: string;
  articleId: string;
  carriereId: string;
  prix: string | null;
}): Promise<{ pose: boolean }> {
  return envoyer<{ pose: boolean }>("/api/tarifs/prix", "PUT", demande);
}

export function adresseClasseurGrille(periodeId?: string | null): string {
  const suffixe = periodeId === undefined || periodeId === null ? "" : `?periode=${periodeId}`;
  return `/api/tarifs/classeur${suffixe}`;
}

export type LigneSansTarif = {
  readonly poste: string;
  readonly rang: number;
  readonly code: string;
  readonly libelle: string | null;
  readonly carriereCode: string;
  readonly motif: MotifSansTarif;
};

/**
 * Les lignes qui annoncent une carrière sans que la grille donne un prix.
 *
 * Lue au serveur et non déduite ici : la règle qui pose le prix et celle qui dit qu'il
 * manque doivent être la même, sans quoi l'écran finirait par signaler un prix que le
 * serveur vient de poser.
 */
export function chargerTarifsManquants(
  versionId: string,
): Promise<{ lignes: readonly LigneSansTarif[] }> {
  return obtenir<{ lignes: readonly LigneSansTarif[] }>(
    `/api/versions/${versionId}/tarifs-manquants`,
  );
}

export async function importerGrille(periodeId: string, fichier: File): Promise<BilanGrille> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/tarifs/classeur?periode=${periodeId}`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanGrille;
}

/**
 * Ce que le planning a suivi quand les heures d'une ligne ont changé (25/08/2026).
 *
 * Nuls quand rien n'a bougé : une correction de prix ne déplace aucune heure, et une ligne
 * sans barre ni pastille n'a rien à recaler.
 */
export type PlanningRecale = {
  readonly barreAjustee: { de: number; a: number } | null;
  readonly pastillesAjustees: {
    readonly modifiees: readonly { jourRelatif: number; de: string; a: string }[];
    readonly retirees: readonly { jourRelatif: number; heures: string }[];
    readonly reliquat: string;
  } | null;
};

/**
 * Modifie une ligne de ressource de la contre-étude.
 *
 * La ligne est désignée par son poste et son rang, jamais par un identifiant
 * technique — c'est la règle du produit, et elle a l'avantage de désigner la
 * même chose d'une version à l'autre.
 *
 * **Rend ce que le planning a suivi.** Corriger des heures ici recale la barre et les
 * pastilles de la ligne : l'écran le dit, parce que l'effet porte sur une vue que
 * l'utilisateur n'a pas sous les yeux au moment où il saisit.
 */
export async function modifierLigne(
  versionId: string,
  poste: string,
  rang: number,
  modification: {
    quantite?: string;
    prixUnitaire?: string;
    supprimee?: boolean;
    remplacerPar?: string;
  },
): Promise<PlanningRecale> {
  const chemin = `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/ressources/${rang}`;

  let reponse: Response;
  // Même enveloppe que `envoyer` : l'en-tête suit le corps. Ici le corps est toujours un
  // objet, donc rien ne change — mais deux façons de composer une requête finiraient par
  // diverger, et c'est précisément une divergence qui a coûté le 400 du 26/08.
  const { headers, body } = envoiJson(modification);
  try {
    reponse = await fetch(chemin, { method: "PATCH", headers, body });
  } catch {
    throw new Error("Le serveur ne répond pas : la modification n'a pas été enregistrée.");
  }

  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refusé la modification (${reponse.status}).`);
  }

  // Un corps illisible ne fait pas échouer la modification : elle est enregistrée, seule
  // l'annonce du recalage manquerait. Perdre l'écriture pour un compte rendu serait absurde.
  const corps = (await reponse.json().catch(() => null)) as Partial<PlanningRecale> | null;
  return {
    barreAjustee: corps?.barreAjustee ?? null,
    pastillesAjustees: corps?.pastillesAjustees ?? null,
  };
}

/**
 * Inscrit d'où vient un matériau — **et, depuis le 18/08/2026, à quel prix**.
 *
 * Désigner la carrière pose le prix de la grille tarifaire sur la ligne, et la marque donc
 * comme retravaillée. Ce que le serveur rend en plus est décrit par `ProvenanceEcrite` : le
 * type d'envoi, lui, ne bouge pas — l'écran n'envoie jamais un prix par cette route.
 */
export type Provenance = {
  duGroupe: boolean;
  carriereId: string | null;
  carriereCode: string | null;
  modeLivraison: string | null;
  fournisseur: string | null;
  /**
   * Les deux villes, par leur identifiant de commune (072).
   *
   * Le nom et le code postal n'y sont pas : ils se DÉDUISENT du référentiel, et le retour
   * de l'écriture ne les porte donc pas — c'est la relecture de la version qui les donne,
   * à jour. Un nom rendu ici serait celui d'avant le geste.
   */
  communeProvenanceId: string | null;
  /** La ville de CHARGEMENT saisie, hors référentiel des distances (073). */
  villeProvenanceLibre: string | null;
  communeLivraisonId: string | null;
  /** Le kilométrage SAISI (073), qui l'emporte sur la déduction. */
  kmForce: string | null;
  /**
   * Le site a-t-il été PROPOSÉ par la coche « groupe », et non choisi ? *(08/09/2026)*
   *
   * L'écran l'annonce : un site apparu sans clic — et un prix de grille avec lui — passerait
   * sinon pour un défaut, et le conducteur ne saurait pas qu'il peut en choisir un autre.
   */
  sitePropose: boolean;
};

/**
 * Pourquoi la grille n'a pas donné de prix.
 *
 * Ces trois motifs remontent jusqu'à l'écran, qui les nomme : « du groupe » sans prix est le
 * cas courant, et un écran qui ne le dit pas laisse croire que le chiffrage est fait.
 */
export type MotifSansTarif = "article_hors_grille" | "hors_periode" | "prix_absent";

export type EtatTarif =
  | { readonly etat: "applique"; readonly prix: string }
  | { readonly etat: "absent"; readonly motif: MotifSansTarif }
  /**
   * Un **article** hors grille : le prix du devis est gardé.
   *
   * *« Uniquement pour les articles qui commencent par 100 »* (métier, 21/08/2026). À dire
   * autant que le zéro : sans cela, désigner un site sur un article semblerait n'avoir aucun
   * effet.
   */
  | { readonly etat: "prix_conserve"; readonly prix: string }
  | { readonly etat: "rendu_au_devis"; readonly prix: string }
  | null;

/** Ce que le serveur rend après avoir posé la provenance : la ligne telle qu'elle est devenue. */
export type ProvenanceEcrite = Provenance & {
  readonly prixUnitaire: string;
  readonly statut: string;
  readonly tarif: EtatTarif;
  /**
   * La pastille de livraison que cette écriture a fait naître, ou `null` (migration 041).
   *
   * *« Quand on passe en mode planning une pastille est automatiquement créée si on a indiqué
   * des informations dans la vue contre-étude »* (métier, 21/08/2026). Rendue plutôt que
   * laissée à deviner : une pastille apparue sans clic doit être annoncée, sinon elle passe
   * pour un défaut — ou, pire, pour rien du tout.
   */
  readonly livraisonPosee: {
    readonly id: string;
    readonly jourRelatif: number;
    readonly quantite: string;
  } | null;
  /**
   * Combien de pastilles **déjà posées et muettes** ont reçu cet acheminement.
   *
   * Même motif que `livraisonPosee` : une pastille modifiée sans clic doit s'annoncer.
   * *Anomalie de Chris du 21/08/2026 — la ligne disait « par nos soins, groupe, et un site »,
   * les quatre pastilles déjà posées ne disaient rien, et le planning paraissait ne rien
   * reprendre.*
   */
  readonly pastillesReprises: number;
};

export async function fixerProvenance(
  versionId: string,
  poste: string,
  rang: number,
  // Chaque champ est facultatif : ce qui n'est pas envoyé n'est pas touché. Le panneau
  // enregistre ce qu'il affiche — une ligne de matériau n'a pas à effacer un mode de
  // livraison qu'elle ne montre pas.
  provenance: Partial<Omit<Provenance, "carriereCode">>,
): Promise<ProvenanceEcrite> {
  const chemin = `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/ressources/${rang}/provenance`;
  return envoyer(chemin, "PATCH", provenance);
}

/* ---------------------------------------------------------------------------
 * Les pastilles de transport
 * ------------------------------------------------------------------------- */

/** Ce que le serveur rend après une écriture de pastille. */
export type TransportEcrit = {
  id: string;
  poste: string;
  ligneRang: number;
  ordre: number;
  jourRelatif: number;
  heures: string;
  heureLimite: string | null;
  /**
   * Ce que l'écriture a fait au déboursé, ou `null` si rien n'a bougé.
   *
   * Même forme que le recalcul d'un étirement de barre, et pour que l'écran les annonce
   * avec le même bandeau : les deux gestes déplacent des euros de la même façon.
   */
  recalcul: { facteur: string; code: string; heuresAvant: string; heuresApres: string } | null;
  /**
   * Combien de barres de durée la pose a retirées de cette ligne.
   *
   * Non nul au premier transport posé sur une ligne qui portait une barre avant que son
   * code passe transport. L'écran le dit : une barre a disparu, et un geste qui efface
   * sans le dire ne se rattrape pas.
   */
  barresRetirees: number;
};

/**
 * Ce qu'une écriture de pastille peut porter.
 *
 * Tout est facultatif : **ce qui n'est pas envoyé n'est pas touché.** La carte enregistre le
 * champ qu'on vient de quitter, pas les neuf autres — sans quoi corriger une heure effacerait
 * le fournisseur saisi la veille.
 */
export type DemandeTransport = {
  heures?: string;
  jourRelatif?: number;
  /** L'effectif de CETTE rotation. `null` remet « celui de la ligne ». */
  unites?: number | null;
  heureLimite?: string | null;
  duGroupe?: boolean;
  carriereId?: string | null;
  modeLivraison?: string | null;
  fournisseur?: string | null;
  /** La ville de CHARGEMENT, choisie au référentiel des communes (072). */
  communeProvenanceId?: string | null;
  /** La ville de CHARGEMENT saisie, hors référentiel des distances (073). */
  villeProvenanceLibre?: string | null;
  /** La ville de LIVRAISON, au même référentiel (072). Nouvelle le 08/09/2026. */
  communeLivraisonId?: string | null;
  /** Le kilométrage SAISI (073), qui l'emporte sur la déduction. */
  kmForce?: string | null;
};

/** Pose une pastille. Sans heures, le serveur propose le reste ; sans jour, après le planifié. */
export async function poserTransport(
  versionId: string,
  poste: string,
  rang: number,
  demande: DemandeTransport = {},
): Promise<TransportEcrit> {
  return envoyer(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/lignes/${rang}/transports`,
    "POST",
    demande,
  );
}

/* ---------------------------------------------------------------------------
 * Organiser le transport des matériaux de nos carrières (09/09/2026)
 * ------------------------------------------------------------------------- */

/** Une journée dimensionnée : combien de tours, combien de camions, combien d'heures. */
export type JourneeDeTransport = {
  readonly jourRelatif: number;
  readonly pastilleId: string;
  readonly heureLimite: string | null;
  readonly tonnes: string;
  /**
   * Les TOURS du jour, entiers — arrondis AU PLUS PROCHE (règle de Chris, 10/09/2026).
   *
   * Un seul nombre depuis cette date : les tours *comptés* (10,588, qui faisaient les heures) et
   * les *chargements* au supérieur (11, qui dimensionnaient la flotte) donnent la même réponse
   * une fois arrondis au plus proche, et deux champs pour la même valeur seraient deux endroits
   * pour la même réponse.
   */
  readonly chargements: number;
  /** Ce que les tours coûtent, hors trajet du dépôt. */
  readonly heuresDesTours: string;
  /**
   * L'approche dépôt → carrière, portée par le PREMIER jour de l'ouvrage et nulle sur les autres.
   *
   * Le camion part une fois et ne rentre pas : le retour est imputé sur le chantier suivant.
   */
  readonly trajetDepotHeures: string;
  readonly heuresTotales: string;
  readonly camions: number;
  /** Combien de tours COMPLETS un camion tient dans sa journée : le « pourquoi » du nombre. */
  readonly toursParCamion: number;
  /** Le remplissage réel, camion par camion : `[4, 4, 3]` et non « 3 × la moyenne ». */
  readonly chargementsParCamion: readonly number[];
  /** Ce que chaque camion roule vraiment, ses chargements entiers. */
  readonly heuresReellesParCamion: readonly string[];
  /**
   * Les pastilles réellement posées : les journées remplies, plus jamais une moyenne.
   *
   * Au plus deux par jour — les camions pleins en partagent une, celui qui prend le reste a la
   * sienne. `heures` est ce qu'UN camion roule, `unites` l'effectif qui multiplie.
   */
  readonly pastilles: readonly {
    readonly unites: number;
    readonly heures: string;
    readonly chargements: number;
    readonly porteLeTrajetDuDepot: boolean;
  }[];
  readonly journeePleine: boolean;
};

/** Ce qu'un tour coûte, terme par terme — la même décomposition que le contrôle. */
export type DetailDuVoyage = {
  readonly routeHeures: string;
  readonly manoeuvreHeures: string;
  readonly chargementHeures: string;
  readonly heures: string;
  readonly kmAllerRetour: string;
};

export type OuvrageOrganise = {
  readonly poste: string;
  readonly posteLibelle: string;
  readonly materiau: string;
  /**
   * L'attelage dont il s'agit : `TRACTEUR 4X2 + BENNE ALU`, ou vide s'il n'y en a pas.
   *
   * Un poste peut en porter plusieurs, chacun avec ses pastilles et ses heures : le bilan a donc
   * une entrée par attelage, et sans son nom deux lignes seraient indistinguables.
   */
  readonly attelage: string;
  readonly organisation: {
    readonly journees: readonly JourneeDeTransport[];
    readonly chargeUtile: string | null;
    /**
     * Les codes des maillons sans tonnage déclaré : ils n'ont rien pesé dans la charge utile.
     *
     * Un tracteur y figure normalement, un tonnage oublié au paramétrage aussi — d'où l'affichage
     * systématique : les données ne distinguent pas les deux, un humain oui.
     */
    readonly maillonsSansCharge: readonly string[];
    readonly parVoyage: DetailDuVoyage | null;
    readonly distanceKm: string | null;
    readonly distanceDepotCarriereKm: string | null;
    readonly distanceChantierDepotKm: string | null;
    readonly heuresTotales: string;
    readonly heuresChiffrees: string;
    readonly motifs: readonly string[];
    /** Ce qui manque, quand le refus peut le nommer : « J4, J5 » pour des pastilles non désignées. */
    readonly detail?: string;
  };
  readonly lignes: readonly string[];
  /** Les lignes laissées intactes parce qu'elles sont figées (092). */
  readonly lignesFigees: readonly string[];
  readonly rotationsPosees: number;
  readonly rotationsRetirees: number;
};

export type BilanDuTransport = {
  readonly ouvrages: readonly OuvrageOrganise[];
  readonly rotationsPosees: number;
  readonly rotationsRetirees: number;
};

/**
 * Pose les rotations de camion et de chauffeur à partir des pastilles de tonnage.
 *
 * *« Pour déclencher ce calcul tu positionneras un bouton en haut à côté de livraison par nos
 * soins »* (Chris, 09/09/2026). Le geste **remplace** les rotations des ouvrages qu'il organise :
 * ajouter doublerait les pastilles au second clic, et le second clic est le cas normal.
 */
export async function organiserLeTransportDesMateriaux(
  versionId: string,
): Promise<BilanDuTransport> {
  return envoyer(`/api/planning/${versionId}/transport-materiaux`, "POST", {});
}

export async function modifierTransport(
  versionId: string,
  transportId: string,
  demande: DemandeTransport,
): Promise<TransportEcrit> {
  return envoyer(`/api/versions/${versionId}/transports/${transportId}`, "PATCH", demande);
}

/**
 * Coupe une rotation en deux : la moitié reste, l'autre part le jour suivant.
 *
 * `POST` et non `PATCH` : le résultat est déjà décidé — la moitié, et le reste. La somme
 * est préservée par construction, donc aucun euro ne bouge, et l'acheminement comme
 * l'équipe sont recopiés sur la seconde.
 */
export async function repartirLeTransport(
  versionId: string,
  transportId: string,
): Promise<{ poste: string; premiere: string; seconde: string }> {
  return envoyer(`/api/versions/${versionId}/transports/${transportId}/repartir`, "POST", {});
}

export async function retirerTransport(
  versionId: string,
  transportId: string,
): Promise<{ poste: string; recalcul: TransportEcrit["recalcul"] }> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/versions/${versionId}/transports/${transportId}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne répond pas : le transport n'a pas été retiré.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le retrait (${reponse.status}).`);
  }
  return (await reponse.json()) as { poste: string; recalcul: TransportEcrit["recalcul"] };
}

/**
 * Déplace une ligne d'un cran, vers le haut ou vers le bas.
 *
 * Seule la POSITION change. L'identité de la ligne — ce à quoi elle correspond
 * dans le devis — ne bouge pas, sinon le vis-à-vis comparerait deux ressources
 * différentes.
 */
export async function deplacerLigne(
  versionId: string,
  poste: string,
  rang: number,
  sens: "haut" | "bas",
): Promise<void> {
  await envoyer(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/ressources/${rang}/deplacer`,
    "POST",
    { sens },
  );
}

/**
 * Coche ou décoche un poste : le compter, ou le sortir du chiffrage.
 *
 * Un poste décoché ne pèse ni dans les charges, ni dans les heures, ni dans le
 * chiffre d'affaires — c'est la règle posée avec Régis le 09/08/2026 sur les
 * options et les variantes non retenues.
 */
export async function retenirPoste(
  versionId: string,
  poste: string,
  retenu: boolean,
): Promise<void> {
  await envoyer(`/api/versions/${versionId}/postes/${encodeURIComponent(poste)}`, "PATCH", {
    retenu,
  });
}

/**
 * Nomme un poste **créé** dans la contre-étude — le chapitre, ou son sous-chapitre.
 *
 * Le serveur refuse sur un poste du devis : son libellé est celui du classeur, et c'est à lui
 * que la contre-étude se compare. L'écran n'offre donc le champ que sur ce qui a été créé,
 * mais la règle est tenue par le serveur, pas par l'écran.
 */
export async function nommerPoste(
  versionId: string,
  poste: string,
  libelle: string,
): Promise<void> {
  await envoyer(`/api/versions/${versionId}/postes/${encodeURIComponent(poste)}`, "PATCH", {
    libelle,
  });
}

/** Ce que le serveur rend après avoir créé la trame : les deux numéros qu'il a posés. */
export type ChapitreCree = {
  readonly chapitre: string;
  readonly sousChapitre: string;
};

/**
 * Crée un chapitre vierge à la suite du dernier, avec son sous-chapitre.
 *
 * Rien à envoyer : le numéro se déduit de l'existant, et les libellés se saisissent sur la
 * trame une fois qu'elle est là.
 */
export function creerChapitre(versionId: string): Promise<ChapitreCree> {
  return envoyer<ChapitreCree>(`/api/versions/${versionId}/chapitres`, "POST", {});
}

/** Retire un chapitre créé par erreur. Le serveur refuse s'il porte des lignes ou un planning. */
export async function supprimerChapitreCree(versionId: string, poste: string): Promise<void> {
  await envoyer(
    `/api/versions/${versionId}/chapitres/${encodeURIComponent(poste)}`,
    "DELETE",
    undefined,
  );
}

/** Un article trouvé par la recherche, réduit à ce qu'une liste de choix affiche. */
export type ArticleTrouve = {
  /** L'identifiant : c'est par lui qu'un mouvement de stock se rattache, jamais par le code. */
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly unite: string | null;
  readonly coutEtude: string | null;
};

export type RechercheArticles = {
  readonly articles: readonly ArticleTrouve[];
  /**
   * Vrai quand d'autres articles correspondaient sans être rendus.
   *
   * À AFFICHER, jamais à taire : une liste pleine et une liste coupée se ressemblent exactement,
   * et on chercherait une ligne qui existe, qui correspond, et qui n'a pas été rendue.
   */
  readonly tronque: boolean;
  /** Combien correspondaient en tout : « 425 trouvés, 40 affichés » dit quoi faire. */
  readonly total: number;
  readonly plafond: number;
};

/**
 * Cherche un article par code ou par libellé, pour l'ajouter à une contre-étude.
 *
 * La recherche porte sur le **code ET la description**, en sous-chaîne : « oude » trouve tous les
 * coudes, « 10021 » tous les codes qui contiennent 10021 *(demande de Chris, 08/09/2026)*.
 *
 * Une route à part de la liste complète : celle-ci rend les trente colonnes des ~3 000 fiches,
 * ce qui n'a pas sa place derrière un champ de saisie. Moins de deux caractères ne cherchent
 * rien — la réponse serait tronquée au plafond, donc arbitraire.
 */
export function chercherArticles(cherche: string): Promise<RechercheArticles> {
  return obtenir<RechercheArticles>(
    `/api/articles/recherche?q=${encodeURIComponent(cherche)}`,
  );
}

/**
 * Crée une version à partir d'une autre, et rend celle qui vient d'être créée.
 *
 * La source est n'importe quelle version du chantier — le devis figé pour repartir
 * du vendu, ou une version de travail pour essayer une variante sans perdre ce
 * qu'on a déjà fait. C'est l'écran qui laisse choisir ; l'API ne présume de rien.
 */
export function deriverVersion(
  sourceId: string,
  libelle: string,
): Promise<ReponseVersionCreee> {
  return envoyer<ReponseVersionCreee>(`/api/versions/${sourceId}/deriver`, "POST", { libelle });
}

/** Ce que la suppression d'une version a emporté. */
export type VersionSupprimee = {
  readonly libelle: string;
  readonly rang: number | null;
  readonly postes: number;
  readonly lignes: number;
  readonly barres: number;
  readonly livraisons: number;
  readonly transports: number;
  /** Les versions qui en descendaient et qui ne savent plus d'où elles viennent. */
  readonly detachees: number;
};

/**
 * Supprime une version de travail, avec son chiffrage et son planning.
 *
 * Le serveur refuse les versions figées, la CE de suivi et celles qui ont été soumises — et
 * il rend le motif en clair. L'écran l'affiche tel
 * quel : chaque refus dit ce qu'il protège, ce qu'un « suppression impossible » ne dit pas.
 */
export function supprimerVersion(versionId: string): Promise<VersionSupprimee> {
  return envoyer<VersionSupprimee>(`/api/versions/${versionId}`, "DELETE", {});
}

/** Ce que porte une version : le décompte de ce qui est rendu, et de ce qui part. */
export type ContenuVersion = {
  readonly postes: number;
  readonly lignes: number;
  readonly barres: number;
  readonly livraisons: number;
  readonly transports: number;
  /** Du travail saisi, et non du chiffrage : à annoncer, jamais à laisser disparaître. */
  readonly affectationsReelles: number;
};

/**
 * Le bilan de l'annulation, en deux moitiés qui ne se confondent pas : ce qui revient et ce
 * qui part. Les additionner annoncerait comme perdu le travail qui est justement rendu.
 */
export type ValidationAnnulee = {
  readonly rendue: {
    readonly versionId: string;
    readonly libelle: string;
    readonly rang: number | null;
    readonly contenu: ContenuVersion;
    readonly demandesRouvertes: number;
  };
  readonly suiviEfface: {
    readonly existait: boolean;
    readonly contenu: ContenuVersion;
    readonly detachees: number;
  };
};

/**
 * Annule la validation : la copie de travail est rendue telle qu'elle a été validée, la CE de
 * suivi part.
 *
 * Le n° d'affaire voyage dans le corps et le serveur le revérifie — l'écran n'est pas la
 * garde. Réservé à l'administrateur : le geste retire le chantier du plan de charge.
 */
export function annulerValidation(
  versionId: string,
  apo: string,
): Promise<ValidationAnnulee> {
  return envoyer<ValidationAnnulee>(`/api/versions/${versionId}/validation`, "DELETE", { apo });
}

/* ---------------------------------------------------------------------------
 * Les regroupements G1-G10
 * ------------------------------------------------------------------------- */

export function chargerGroupes(): Promise<ReponseGroupes> {
  return obtenir<ReponseGroupes>("/api/groupes");
}

/**
 * Créer un regroupement.
 *
 * Le rang n'est pas envoyé : le serveur place le nouveau venu en dernier, ce qui
 * l'empêche de voler des postes déjà classés par les autres groupes.
 */
export function creerGroupe(champs: {
  code: string;
  libelle: string;
  couleur: string;
}): Promise<GroupeAdmin> {
  return envoyer<GroupeAdmin>("/api/groupes", "POST", champs);
}

/**
 * Renommer, recolorer, retirer ou remettre un regroupement.
 *
 * `code` est modifiable depuis la migration 022, et sans conséquence sur les
 * chantiers : postes, mémoire et mots-clés désignent l'identifiant du groupe.
 */
export function modifierGroupe(
  id: string,
  champs: { code?: string; libelle?: string; couleur?: string; actif?: boolean },
): Promise<GroupeAdmin> {
  return envoyer<GroupeAdmin>(`/api/groupes/${id}`, "PATCH", champs);
}

/**
 * Monte ou descend un regroupement d'un cran.
 *
 * Une seule requête, et le serveur renumérote toute la liste : le rang ne sert pas
 * qu'au tri du tableau, il décide aussi quel mot-clé gagne quand un libellé en
 * contient plusieurs. Un échange fait de deux écritures laisserait entre les deux un
 * référentiel où deux groupes ont la même priorité.
 */
export function deplacerGroupe(
  id: string,
  sens: "monter" | "descendre",
): Promise<ReponseGroupes> {
  return envoyer<ReponseGroupes>(`/api/groupes/${id}/deplacer`, "POST", { sens });
}

/**
 * Ajoute un mot-clé au dictionnaire.
 *
 * C'est par là qu'entre le vocabulaire de travaux publics que rien ne devine —
 * « AEP », « LIBAGES », « SEMELLES FILANTES ». Le serveur le normalise avant de le
 * poser, donc la casse et les accents de la saisie n'ont pas d'importance.
 */
export function ajouterMotCle(groupeId: string, mot: string): Promise<ReponseGroupes> {
  return envoyer<ReponseGroupes>("/api/groupes/mots", "POST", { groupeId, mot });
}

export async function retirerMotCle(mot: string): Promise<ReponseGroupes> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/groupes/mots/${encodeURIComponent(mot)}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne répond pas : le mot-clé n'a pas été retiré.");
  }
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refusé la demande (${reponse.status}).`);
  }
  return (await reponse.json()) as ReponseGroupes;
}

/**
 * Affecte un regroupement à un poste, ou le remet en héritage avec `null`.
 *
 * `null` n'est pas une absence de valeur mais un geste : sur un chapitre
 * contractuel (« TRANCHE FERME »), c'est même l'état juste — ce sont ses enfants
 * qui portent une nature de travaux, pas lui.
 */
export function affecterGroupe(
  versionId: string,
  poste: string,
  groupeId: string | null,
): Promise<void> {
  return envoyer(`/api/versions/${versionId}/postes/${encodeURIComponent(poste)}`, "PATCH", {
    groupeId,
  });
}

/* ---------------------------------------------------------------------------
 * Le planning : les barres du Gantt
 * ------------------------------------------------------------------------- */

/**
 * Ce qu une ecriture de barre a produit.
 *
 * `recalcul` non nul = la duree a change, donc les heures et le debourse aussi.
 * C est l information que l ecran doit montrer : un planning qui modifie le
 * chiffrage sans le dire serait le pire des deux mondes.
 */
export type BarreEcrite = {
  readonly id: string;
  readonly poste: string;
  /** Le rang de la ligne qui porte la barre, ou `null` pour une barre de titre. */
  readonly ligneRang: number | null;
  readonly demiJournees: number;
  readonly debutRelatif: number;
  readonly position: "matin" | "apres_midi";
  readonly recalcul: {
    readonly facteur: string;
    /** Le code de la ressource etiree — l ecran nomme ce qui a bouge. */
    readonly code: string;
    readonly heuresAvant: string;
    readonly heuresApres: string;
  } | null;
};

/**
 * Pose une barre sur une LIGNE de la contre-etude.
 *
 * **Sans `debutRelatif`, elle atterrit apres ce qui est deja planifie** — c est le
 * serveur qui le decide, avec la meme regle que la proposition automatique du planning.
 */
export function poserBarre(
  versionId: string,
  poste: string,
  rang: number,
  barre: { demiJournees: number; debutRelatif?: number; position?: "matin" | "apres_midi" },
): Promise<BarreEcrite> {
  return envoyer<BarreEcrite>(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/lignes/${rang}/phases`,
    "POST",
    { position: "matin", ...barre },
  );
}

/**
 * Pose une barre sur le TITRE d une tache.
 *
 * N a de sens que sur un poste sans aucune ligne en heures — un panneau, un dos d ane :
 * il n a rien a quoi accrocher sa barre. Partout ailleurs le serveur refuse, le titre
 * n etant qu un resume de son detail.
 */
export function poserBarreDeTitre(
  versionId: string,
  poste: string,
  barre: { demiJournees: number; debutRelatif?: number; position?: "matin" | "apres_midi" },
): Promise<BarreEcrite> {
  return envoyer<BarreEcrite>(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/phases`,
    "POST",
    { position: "matin", ...barre },
  );
}

/**
 * Hache une ligne — ou le titre d'un poste sans ligne en heures : une reprise de plus, en
 * parts égales. Un clic coupe en deux, le suivant en trois, et ainsi de suite.
 *
 * **La durée totale ne change pas, donc aucune heure ni aucun euro** : `recalcul` est
 * toujours nul. Les départs posés à la main sont gardés ; la reprise ajoutée se colle à
 * la dernière.
 */
export function hacherBarres(
  versionId: string,
  poste: string,
  rang: number | null,
): Promise<{ reprises: number; demiJournees: number; recalcul: null }> {
  const chemin =
    rang === null
      ? `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/hachage`
      : `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/lignes/${rang}/hachage`;
  return envoyer(chemin, "POST", {});
}

/** Ce que le hachage d'une barre grise a fait. */
export type BlocHache = {
  readonly perimetre: { readonly quoi: "ouvrage" | "chapitre"; readonly numero: string };
  readonly coupees: number;
  /** Les lignes laissées telles quelles, trop courtes pour une reprise de plus. */
  readonly tropCourtes: readonly { readonly poste: string; readonly code: string | null }[];
  readonly recalcul: null;
};

/**
 * Hache la barre grise d'un chapitre ou d'une tâche : chaque ligne de son contenu qui porte
 * des barres reçoit une reprise de plus. Les pastilles (transport, livraisons) ne bougent
 * pas. Aucune durée totale, aucun euro ne change.
 */
export function hacherBloc(
  versionId: string,
  bloc: { quoi: "ouvrage" | "chapitre"; numero: string },
): Promise<BlocHache> {
  return envoyer<BlocHache>(`/api/versions/${versionId}/hachage-bloc`, "POST", bloc);
}

export function modifierBarre(
  versionId: string,
  phaseId: string,
  changement: { demiJournees?: number; debutRelatif?: number; position?: "matin" | "apres_midi" },
): Promise<BarreEcrite> {
  return envoyer<BarreEcrite>(`/api/versions/${versionId}/phases/${phaseId}`, "PATCH", changement);
}

export async function retirerBarre(versionId: string, phaseId: string): Promise<void> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/versions/${versionId}/phases/${phaseId}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne repond pas : la barre n a pas ete retiree.");
  }
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refuse la demande (${reponse.status}).`);
  }
}

/**
 * Inscrit combien d'unites de cette ressource travaillent EN MEME TEMPS.
 *
 * Deux ouvriers, un chef, deux niveleuses : depuis le 12/08/2026 le nombre vit sur
 * la LIGNE et vaut pour les hommes comme pour les machines. `null` = non renseigne,
 * ce qui n'est pas « une » : la duree se calcule pareil, mais l'ecran doit pouvoir
 * montrer que personne ne s'est prononce.
 *
 * **Aucune heure n'est touchee** : le nombre partage les heures deja chiffrees, il
 * ne les multiplie pas. C'est ce qui le distingue de l'etirement, qui coute de
 * l'argent. La barre de la ligne, en revanche, se raccourcit — `barreAjustee` le dit.
 */
/** Figer ou libérer une ligne de transport face à « Organiser le transport » (092). */
export function figerLeTransport(
  versionId: string,
  poste: string,
  rang: number,
  fige: boolean,
): Promise<{ poste: string; rang: number; fige: boolean }> {
  return envoyer(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/lignes/${rang}/transport-fige`,
    "PATCH",
    { fige },
  );
}

export function fixerUnites(
  versionId: string,
  poste: string,
  rang: number,
  unites: number | null,
): Promise<{
  poste: string;
  rang: number;
  unites: number | null;
  barreAjustee: { de: number; a: number } | null;
}> {
  return envoyer(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/lignes/${rang}/unites`,
    "PATCH",
    { unites },
  );
}

/** Ce qu'un retour aux heures du devis a rendu a la tache. */
export type HeuresRestaurees = {
  readonly poste: string;
  readonly lignesRestaurees: number;
  /** Les lignes en heures que le devis fige ne connait pas : elles gardent les leurs. */
  readonly lignesSansReference: number;
  readonly heuresAvant: string;
  readonly heuresApres: string;
  /** Une entree par ligne dont la barre a suivi les heures retrouvees. */
  readonly barresAjustees: readonly { rang: number; code: string; de: number; a: number }[];
};

/**
 * Rend a une tache les heures que le devis fige lui chiffre.
 *
 * Le corps est vide : la valeur d'arrivee n'est pas choisie par l'ecran, elle est
 * lue dans le devis. C'est l'inverse exact d'un etirement, et le seul geste qui
 * ramene EXACTEMENT la valeur d'origine — etirer puis de-etirer laisse un residu.
 */
export function rendreLesHeuresDuDevis(
  versionId: string,
  poste: string,
): Promise<HeuresRestaurees> {
  return envoyer<HeuresRestaurees>(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/heures-du-devis`,
    "POST",
    {},
  );
}

/* ---------------------------------------------------------------------------
 * Les livraisons
 * -------------------------------------------------------------------------
 *
 * La seconde nature de barre : un jour, un volume, une heure LIMITE. **Aucune de ces
 * ecritures ne deplace un euro** — la quantite de la ligne appartient a la contre-etude, et
 * une livraison dit seulement QUAND le materiau arrive.
 *
 * L'heure est une echeance portant sur le volume entier (metier, 12/08/2026 : « si 1 000
 * tonnes a livrer et que l'heure est 11h00, il faut les 1 000 tonnes avant 11h00 »).
 */

/** Ce qu une ecriture de livraison a produit. */
export type LivraisonEcrite = {
  readonly id: string;
  readonly poste: string;
  readonly ligneRang: number;
  readonly ordre: number;
  readonly jourRelatif: number;
  readonly quantite: string;
  readonly heureLimite: string | null;
  /** L'acheminement de la pastille (039) : les mêmes six champs qu'une rotation. */
  readonly duGroupe: boolean;
  readonly carriereId: string | null;
  readonly modeLivraison: string | null;
  readonly fournisseur: string | null;
  readonly communeProvenanceId: string | null;
  readonly villeProvenanceLibre: string | null;
  readonly communeLivraisonId: string | null;
  readonly kmForce: string | null;
};

/**
 * Ce qu'une modification de livraison peut porter.
 *
 * Les six champs d'acheminement s'y ajoutent depuis la 039 — *« exactement les mêmes champs
 * que sur une pastille de transport »* — et la même règle qu'ailleurs tient : **un champ
 * absent ne demande rien**, ce qui n'est pas la même chose qu'un champ à `null`, lequel
 * efface.
 */
export type DemandeLivraison = {
  quantite?: string;
  jourRelatif?: number;
  heureLimite?: string | null;
  duGroupe?: boolean;
  carriereId?: string | null;
  modeLivraison?: string | null;
  fournisseur?: string | null;
  /** La ville de CHARGEMENT, choisie au référentiel des communes (072). */
  communeProvenanceId?: string | null;
  /** La ville de CHARGEMENT saisie, hors référentiel des distances (073). */
  villeProvenanceLibre?: string | null;
  /** La ville de LIVRAISON, au même référentiel (072). Nouvelle le 08/09/2026. */
  communeLivraisonId?: string | null;
  /** Le kilométrage SAISI (073), qui l'emporte sur la déduction. */
  kmForce?: string | null;
};

/**
 * Pose une livraison sur une ligne qui se livre.
 *
 * **Sans `quantite`, c'est le RESTE a livrer** — la totalite sur une ligne vierge : le
 * geste naturel est « pose-moi ces 150 T », puis on repartit. **Sans `jourRelatif`, elle
 * atterrit apres ce qui est deja planifie**, comme une barre de duree.
 */
export function poserLivraison(
  versionId: string,
  poste: string,
  rang: number,
  livraison: { quantite?: string; jourRelatif?: number; heureLimite?: string | null } = {},
): Promise<LivraisonEcrite> {
  return envoyer<LivraisonEcrite>(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/lignes/${rang}/livraisons`,
    "POST",
    livraison,
  );
}

/**
 * Deplace une livraison, change son volume ou son heure.
 *
 * `heureLimite: null` efface l'heure — « ce jour-la, elle m'est indifferente » —, ce qui
 * n'est pas la meme chose qu'un champ absent, lequel ne demande rien.
 */
/** Ce qu'un décalage en bloc a déplacé — de quoi l'annoncer sans relire la frise. */
export type BlocDecaleEcrit = {
  readonly perimetre: { readonly quoi: "ouvrage" | "chapitre"; readonly numero: string };
  readonly demiJournees: number;
  readonly deltaJours: number;
  readonly barres: number;
  readonly livraisons: number;
  readonly transports: number;
  /** Combien de pastilles se sont arrêtées au premier jour au lieu de suivre le bloc. */
  readonly pastillesRetenues: number;
};

/**
 * Décaler un bloc entier : la barre grise d'un chapitre ou d'un ouvrage, et tout son détail.
 *
 * *Demande de Chris, 19/08/2026.* Le glissement est **signé**, en demi-journées : négatif
 * pour avancer le bloc, positif pour le repousser. Rien d'autre ne part — ni durée, ni
 * quantité : le serveur ne lit que des positions.
 */
export function decalerBloc(
  versionId: string,
  bloc: {
    quoi: "ouvrage" | "chapitre";
    numero: string;
    demiJournees: number;
    /** Une seule période d'une tâche hachée, en demi-journées, bornes incluses (25/09/2026). */
    periode?: { debut: number; fin: number };
  },
): Promise<BlocDecaleEcrit> {
  return envoyer<BlocDecaleEcrit>(`/api/versions/${versionId}/decalage`, "PATCH", bloc);
}

export function modifierLivraison(
  versionId: string,
  livraisonId: string,
  changement: DemandeLivraison,
): Promise<LivraisonEcrite> {
  return envoyer<LivraisonEcrite>(
    `/api/versions/${versionId}/livraisons/${livraisonId}`,
    "PATCH",
    changement,
  );
}

/**
 * Coupe une livraison en deux, en repartissant son volume.
 *
 * Une seule requete : un PATCH suivi d'un POST laisserait entre les deux un etat ou la
 * somme des livraisons est fausse. La seconde moitie part le jour suivant.
 */
export function repartirLaLivraison(
  versionId: string,
  livraisonId: string,
): Promise<{ premiere: LivraisonEcrite; seconde: LivraisonEcrite }> {
  return envoyer(`/api/versions/${versionId}/livraisons/${livraisonId}/repartir`, "POST", {});
}

/** Retire une livraison. **La quantite de la ligne ne bouge pas** : elle retourne au reste. */
export async function retirerLivraison(versionId: string, livraisonId: string): Promise<void> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/versions/${versionId}/livraisons/${livraisonId}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne repond pas : la livraison n a pas ete retiree.");
  }
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refuse la demande (${reponse.status}).`);
  }
}

/** Pose une barre sur chaque tache non planifiee, a la duree que ses heures impliquent. */
export function proposerPlanning(
  versionId: string,
): Promise<{ posees: number; ignorees: number; dejaPlanifiees: number }> {
  return envoyer(`/api/versions/${versionId}/planning/proposer`, "POST", {});
}

/** Ajoute une ressource du référentiel à un ouvrage de la contre-étude. */
export async function ajouterLigne(
  versionId: string,
  poste: string,
  code: string,
  quantite: string,
): Promise<void> {
  await envoyer(
    `/api/versions/${versionId}/postes/${encodeURIComponent(poste)}/ressources`,
    "POST",
    { code, quantite },
  );
}

/* ---------------------------------------------------------------------------
 * Les accès : connexion, invitation, mot de passe, administration des membres
 * ------------------------------------------------------------------------- */

/**
 * Les rôles, dans le même ordre qu'en base (025, 067, puis 096).
 *
 * Les trois premiers voient les mêmes écrans et ne se distinguent que par les écritures
 * d'administration. `magasin` n'ouvre que la gestion des stocks et le référentiel des articles —
 * c'est une autre application, servie par `Magasin.tsx`. `be` (096) est le bureau d'études : il
 * voit les études et l'écran ne lui montre que quatre menus (`MENUS_DU_BE`, `App.tsx`).
 *
 * La liste des rôles proposés à l'écran des membres vient du SERVEUR (`chargerMembres`), pas
 * d'ici : ce type dit ce qu'on sait recevoir, jamais ce qu'on a le droit de poser.
 */
export type Role = "admin" | "membre" | "utilisateur" | "magasin" | "be";
export type Statut = "en_attente" | "actif" | "suspendu";

export type Compte = {
  readonly id: string;
  readonly nom: string;
  readonly prenom: string;
  readonly courriel: string;
  readonly role: Role;
  readonly statut: Statut;
  readonly salarieId: string | null;
  readonly creeLe: string;
  readonly approuveLe: string | null;
  readonly derniereConnexion: string | null;
};

/**
 * Qui est connecté, ou `null`.
 *
 * Ne lève pas quand personne ne l'est : ne pas être connecté est un état normal au
 * chargement de l'application, pas une erreur à afficher.
 */
export function chargerMonCompte(): Promise<{
  compte: Compte | null;
  /** Les fonctions de validation tenues : « transport », « exploitation », « carrieres ». */
  fonctions: readonly string[];
}> {
  return obtenir<{ compte: Compte | null; fonctions: readonly string[] }>("/api/acces/moi");
}

export function seConnecter(courriel: string, motDePasse: string): Promise<{ compte: Compte }> {
  return envoyer<{ compte: Compte }>("/api/acces/connexion", "POST", { courriel, motDePasse });
}

export function seDeconnecter(): Promise<{ deconnecte: boolean }> {
  return envoyer<{ deconnecte: boolean }>("/api/acces/deconnexion", "POST", {});
}

export function changerSonMotDePasse(
  ancien: string,
  nouveau: string,
): Promise<{ message: string }> {
  return envoyer<{ message: string }>("/api/acces/mot-de-passe", "POST", { ancien, nouveau });
}

/** La réponse est volontairement la même que le compte existe ou non. */
export function demanderReinitialisation(courriel: string): Promise<{ message: string }> {
  return envoyer<{ message: string }>("/api/acces/mot-de-passe-oublie", "POST", { courriel });
}

export function poserMotDePasse(jeton: string, motDePasse: string): Promise<{ message: string }> {
  return envoyer<{ message: string }>("/api/acces/reinitialiser", "POST", { jeton, motDePasse });
}

export type InvitationLue = {
  readonly courriel: string;
  readonly nom: string;
  readonly prenom: string;
};

export function lireInvitation(jeton: string): Promise<{ invitation: InvitationLue }> {
  return obtenir<{ invitation: InvitationLue }>(
    `/api/acces/invitation/${encodeURIComponent(jeton)}`,
  );
}

export function accepterInvitation(
  jeton: string,
  motDePasse: string,
): Promise<{ compte: Compte; message: string }> {
  return envoyer<{ compte: Compte; message: string }>(
    `/api/acces/invitation/${encodeURIComponent(jeton)}`,
    "POST",
    { motDePasse },
  );
}

/** Une invitation en cours, pour la suivre depuis l ecran d administration. */
export type InvitationSuivie = {
  readonly id: string;
  readonly courriel: string;
  readonly rolePropose: Role;
  readonly creeLe: string;
  readonly expireLe: string;
  readonly etat: "en_attente" | "utilisee" | "expiree" | "annulee";
};

export function chargerInvitations(): Promise<{ invitations: readonly InvitationSuivie[] }> {
  return obtenir<{ invitations: readonly InvitationSuivie[] }>("/api/membres/invitations");
}

/** Annuler une invitation : son lien cesse aussitot de valoir quelque chose. */
export function annulerInvitation(id: string): Promise<{ annulee: boolean }> {
  return envoyer<{ annulee: boolean }>(`/api/membres/invitations/${id}`, "DELETE", {});
}

export function chargerMembres(): Promise<{ membres: readonly Compte[]; roles: readonly Role[] }> {
  return obtenir<{ membres: readonly Compte[]; roles: readonly Role[] }>("/api/membres");
}

/** Une personne du référentiel dont l'adresse ne sert qu'à elle, et qui n'a pas de compte. */
export type Invitable = {
  readonly courriel: string;
  readonly salarieId: string;
  readonly nom: string;
  readonly prenom: string;
  readonly codeMo: string | null;
  readonly societe: string | null;
};

export function chargerInvitables(): Promise<{ invitables: readonly Invitable[] }> {
  return obtenir<{ invitables: readonly Invitable[] }>("/api/membres/invitables");
}

export type InvitationEnvoyee = {
  readonly courriel: string;
  readonly expireLe: string;
  readonly envoye: boolean;
  /** Renseigné SEULEMENT quand le courriel n'a pas pu partir : à transmettre à la main. */
  readonly lien: string | null;
  readonly message: string;
};

export function inviter(courriel: string, role: Role): Promise<InvitationEnvoyee> {
  return envoyer<InvitationEnvoyee>("/api/membres/inviter", "POST", { courriel, role });
}

export function approuverMembre(id: string, role: Role): Promise<{ membre: Compte }> {
  return envoyer<{ membre: Compte }>(`/api/membres/${id}/approuver`, "POST", { role });
}

export function modifierMembre(
  id: string,
  champs: { role?: Role; statut?: Statut },
): Promise<{ membre: Compte }> {
  return envoyer<{ membre: Compte }>(`/api/membres/${id}`, "PATCH", champs);
}

/* ---------------------------------------------------------------------------
 * Le circuit VAL-CE : soumettre une contre-étude, la faire valider
 * ------------------------------------------------------------------------- */

/**
 * Ces formes sont déclarées ici et non dans `@planif/moteur`.
 *
 * Le moteur porte le vocabulaire du **calcul**, partagé parce que les deux côtés le font. Une
 * demande de validation ne se calcule pas : elle se lit. L'y installer donnerait au paquet une
 * raison de plus de changer, alors que sa stabilité est ce qui permet au client et au serveur de
 * compter sur le même chiffre.
 */
/**
 * Les quatre étapes du circuit VAL-CE, dans leur ordre.
 *
 * **`assistante` manquait ici depuis la 057**, livrée le matin du 02/09/2026 : le serveur
 * envoyait bien quatre états, l'écran n'en connaissait que trois, et l'étape de l'assistante
 * s'affichait donc **sans explication** — `EXPLICATION[circuit]` rendait `undefined`, que React
 * dessine comme rien du tout. Chris l'a vu à l'usage.
 *
 * La leçon est dans le type lui-même : `Record<Circuit, string>` fait échouer la compilation
 * quand une étape n'a pas sa phrase. C'est cette liste-ci qu'il faut tenir à jour, et le reste
 * suit — l'ordre et les libellés, eux, viennent du serveur, parce que ce sont des règles métier.
 */
export type Circuit = "assistante" | "carrieres" | "transport" | "exploitation";
export type StatutSoumission = "en_attente" | "accepte" | "refuse" | "annule";

export type PersonneSoumission = {
  readonly id: string;
  readonly nom: string;
  readonly prenom: string;
  readonly courriel: string;
};

export type Soumission = {
  readonly id: string;
  readonly chantierId: string;
  readonly versionId: string;
  readonly etape: string;
  readonly circuit: Circuit;
  readonly statut: StatutSoumission;
  readonly message: string | null;
  readonly motif: string | null;
  readonly debourse: string | null;
  readonly vente: string | null;
  readonly debourseDevis: string | null;
  readonly venteDevis: string | null;
  readonly creeLe: string;
  readonly reponduLe: string | null;
  readonly relanceLe: string | null;
  readonly demandeur: PersonneSoumission;
  readonly destinataire: PersonneSoumission;
  readonly chantierApo: string;
  readonly chantierNom: string;
  readonly versionLibelle: string | null;
  readonly versionRang: number | null;
  readonly versionFigee: boolean;
  /** Ce que le conducteur a prévu pour CE responsable — 051, distinct du mot de l'envoi. */
  readonly commentaire: string | null;
};

export type TableauSoumissions = {
  readonly soumissions: readonly Soumission[];
  readonly etapes: Record<string, string>;
  readonly circuits: Record<Circuit, string>;
  /** Ce que ce compte a le droit de faire, décidé par le SERVEUR. L'écran s'y conforme. */
  readonly moi: { readonly id: string; readonly role: Role; readonly estAdmin: boolean };
};

export function chargerSoumissions(): Promise<TableauSoumissions> {
  return obtenir<TableauSoumissions>("/api/soumissions");
}

/**
 * Les fonctions à titulaire unique.
 *
 * Les trois premières répondent aux soumissions de contre-étude. `articles` (068) valide les
 * créations d'articles demandées par le magasin : elle ne répond à aucune version, mais elle se
 * nomme au même endroit et par le même geste.
 */
export type Fonction = "transport" | "exploitation" | "carrieres" | "articles";

export type FonctionTenue = {
  readonly fonction: Fonction;
  readonly libelle: string;
  /** L'adresse électronique n'en fait pas partie : elle ne sort pas du serveur. */
  readonly titulaire: {
    readonly utilisateurId: string;
    readonly nom: string;
    readonly prenom: string;
    readonly statut: Statut;
  } | null;
};

export function chargerResponsables(): Promise<{ fonctions: readonly FonctionTenue[] }> {
  return obtenir<{ fonctions: readonly FonctionTenue[] }>("/api/responsables");
}

export function nommerResponsable(
  fonction: Fonction,
  utilisateurId: string,
): Promise<{ titulaire: unknown }> {
  return envoyer<{ titulaire: unknown }>(`/api/responsables/${fonction}`, "PUT", { utilisateurId });
}

export function retirerResponsable(fonction: Fonction): Promise<{ retire: boolean }> {
  return envoyer<{ retire: boolean }>(`/api/responsables/${fonction}`, "DELETE", {});
}

export function soumettreVersion(
  versionId: string,
  circuit: Circuit,
  message: string | null,
): Promise<{ soumission: Soumission }> {
  return envoyer<{ soumission: Soumission }>(`/api/versions/${versionId}/soumettre`, "POST", {
    circuit,
    message,
  });
}

export function repondreSoumission(
  soumissionId: string,
  accepte: boolean,
  motif: string | null,
): Promise<{ soumission: Soumission }> {
  return envoyer<{ soumission: Soumission }>(`/api/soumissions/${soumissionId}/reponse`, "POST", {
    accepte,
    motif,
  });
}

export function annulerSoumission(soumissionId: string): Promise<{ soumission: Soumission }> {
  return envoyer<{ soumission: Soumission }>(`/api/soumissions/${soumissionId}/annuler`, "POST", {});
}

export function relancerSoumission(soumissionId: string): Promise<{ soumission: Soumission }> {
  return envoyer<{ soumission: Soumission }>(
    `/api/soumissions/${soumissionId}/relancer`,
    "POST",
    {},
  );
}

/** Un point de l'historique : où en était le chiffrage à la fin d'une journée de travail. */
export type PointTotal = {
  readonly jour: string;
  readonly debourse: string;
  readonly vente: string;
};

/**
 * Où en est une étape du circuit, et si elle se soumet — **décidé par le serveur**.
 *
 * L'écran n'en refait pas le raisonnement : c'est la même règle qui refuse la soumission côté API
 * et qui décide du bouton, donc l'un ne peut pas proposer ce que l'autre refuse. `empechement`
 * arrive rédigé, pour la même raison : la phrase est écrite là où la règle est écrite.
 */
export type EtatCircuit = {
  readonly circuit: Circuit;
  /** L'étape carrières ne s'applique qu'aux versions portant des matériaux du groupe. */
  readonly applicable: boolean;
  readonly statut: StatutSoumission | "absent" | "perime";
  readonly soumissible: boolean;
  readonly empechement: string | null;
};

export type SuiviVersion = {
  readonly soumissions: readonly Soumission[];
  readonly historique: readonly PointTotal[];
  /** Les versions de travail du même déboursé et leur numéro — V1, V2, V3. */
  readonly rangs: readonly RangDeVersion[];
  readonly circuits: Record<Circuit, string>;
  /** Les trois étapes, dans l'ordre du circuit : c'est le serveur qui donne cet ordre. */
  readonly etats: readonly EtatCircuit[];
  readonly materiauxDuGroupe: boolean;
  /** Ce que le conducteur a prévu pour chaque responsable — un par circuit, au plus. */
  readonly commentaires: readonly CommentaireCircuit[];
};

/** Une consigne attachée à la VERSION, que son responsable relit à chaque envoi. */
export type CommentaireCircuit = {
  readonly circuit: Circuit;
  readonly texte: string;
  readonly modifieLe: string;
  readonly auteur: string | null;
};

/**
 * Écrire ou effacer le commentaire d'un circuit.
 *
 * `PUT` : le couple (version, circuit) n'en porte qu'un, et rejouer la requête laisse le même
 * état — ce qui rend sûr un champ qui s'enregistre à sa sortie. Un texte blanc EFFACE.
 */
export function ecrireCommentaireCircuit(
  versionId: string,
  circuit: Circuit,
  texte: string,
): Promise<{ commentaires: readonly CommentaireCircuit[] }> {
  return envoyer<{ commentaires: readonly CommentaireCircuit[] }>(
    `/api/versions/${versionId}/commentaires/${circuit}`,
    "PUT",
    { texte },
  );
}

export function chargerSuivi(versionId: string): Promise<SuiviVersion> {
  return obtenir<SuiviVersion>(`/api/versions/${versionId}/suivi`);
}

export type RangDeVersion = {
  readonly id: string;
  readonly type: string;
  readonly libelle: string | null;
  readonly rang: number | null;
  readonly figee: boolean;
  readonly soumise: boolean;
};

export function designerV1(versionId: string): Promise<{ versions: readonly RangDeVersion[] }> {
  return envoyer<{ versions: readonly RangDeVersion[] }>(
    `/api/versions/${versionId}/en-v1`,
    "POST",
    {},
  );
}

/* ---------------------------------------------------------------------------
 * Le planning général et les tableaux de bord (PRD §4.7, §4.7.1)
 * ------------------------------------------------------------------------- */

/**
 * Une machine du parc, telle que le serveur la relaie du référentiel maître.
 *
 * Les quatre champs par lesquels le responsable la reconnaît (demande de Chris,
 * 15/08/2026) : le code ERP la désigne sans ambiguïté, le type dit ce que c'est, la
 * description ce qu'on lit sur le parc, l'immatriculation ce qu'on lit sur la machine.
 */
export type Materiel = {
  readonly id: string;
  /** `code_materiel` du référentiel — unique au parc. */
  readonly codeErp: string;
  /** Le code générique du déboursé : c'est par lui que les listes se filtrent. */
  readonly generique: string | null;
  readonly type: string | null;
  readonly description: string | null;
  /** Absente sur un engin non routier — 72 machines du parc n'en ont pas. */
  readonly immatriculation: string | null;
  /**
   * La **famille** de la machine au référentiel maître — `04-03-18` pour une niveleuse.
   *
   * Elle a la même granularité que `famille_code` des ressources génériques, ce qui permet de
   * rapprocher les deux référentiels **par identifiant de famille** quand aucune machine ne
   * porte le code exact du besoin. Mesuré le 03/09/2026 : renseignée sur les 227 machines, et
   * concordant avec le code générique sur les 227.
   *
   * Elle traverse déjà — la fiche entière est rendue depuis le 20/08 pour l'écran du parc ;
   * ce champ n'était simplement pas déclaré ici.
   */
  readonly codeCategorie: string | null;
};

export function chargerMateriels(): Promise<{
  readonly materiels: readonly Materiel[];
  readonly baseQr: string;
}> {
  return obtenir<{ materiels: readonly Materiel[]; baseQr: string }>("/api/materiels");
}

/** Ailleurs où une ressource travaille le même jour, et à quels moments. */
export type Ailleurs = {
  readonly chantierId: string;
  readonly apo: string;
  /** « matin », « apres_midi », les deux, ou vide quand le besoin ne porte pas de moment. */
  readonly positions: readonly ("matin" | "apres_midi")[];
};

export type AffectationReelle = {
  readonly id: string;
  readonly nature: "salarie" | "materiel";
  readonly ressourceId: string;
  readonly libelle: string;
  readonly detail: string | null;
  /**
   * Les autres chantiers où cette ressource est affectée le même jour.
   *
   * Signalé, jamais interdit — pour les machines comme pour les hommes (décision de Chris
   * du 15/08/2026). Les moments sont ce qui rend le signalement utile : un partage
   * matin/après-midi est tenable, deux fois le même matin ne l'est pas.
   */
  readonly conflit: readonly Ailleurs[];
};

export type ChantierAuPlan = {
  readonly chantierId: string;
  readonly apo: string;
  readonly nom: string;
  readonly client: string | null;
  readonly ville: string | null;
  readonly statut: string;
  readonly dateDebut: string;
  readonly dateDebutStatut: string;
  readonly versionId: string;
  readonly versionLibelle: string | null;
};

export type BesoinDate = {
  readonly chantierId: string;
  readonly versionId: string;
  readonly ligneId: string | null;
  /**
   * L'identifiant de la pastille — la livraison ou la rotation d'où vient ce besoin.
   *
   * Nul pour un besoin en heures, qui agrège les barres du jour et ne désigne aucun objet.
   *
   * C'est **ce à quoi une réalisation se rattachera** quand le chauffeur saisira sa livraison
   * (contrainte de Chris, 03/09/2026) : une même ligne porte deux pastilles le même jour dès
   * qu'un volume se répartit en deux échéances, et (ligne, jour) ne suffit donc pas à les
   * distinguer. C'est aussi ce qui rend chaque ligne de l'écran unique.
   */
  readonly pastilleId: string | null;
  readonly jourRelatif: number;
  readonly date: string;
  readonly poste: string;
  readonly posteLibelle: string;
  readonly rang: number;
  readonly code: string;
  readonly libelle: string;
  readonly unite: string;
  readonly nature: "heures" | "transport" | "livraison";
  readonly heures: string | null;
  readonly quantite: string | null;
  readonly unites: number | null;
  readonly heureLimite: string | null;
  /** Les moments occupés. Vide pour une livraison et une rotation — elles n'en portent pas. */
  readonly positions: readonly ("matin" | "apres_midi")[];
  /**
   * Les heures de CHAQUE demi-journée occupée, telles que le serveur les a réparties.
   *
   * Et non le total divisé par leur nombre : depuis le 04/09/2026 les heures chiffrées se
   * répartissent en remplissant, donc une matinée peut porter 4 h quand l'après-midi n'en
   * porte que 2.
   */
  readonly moments: readonly { readonly position: "matin" | "apres_midi"; readonly heures: string }[];
  /**
   * Le contrôle du transport de cette matière — voyages, heures attendues, écart.
   *
   * Nul sur tout ce qui n'est pas une livraison. C'est un contrôle de LIGNE, affiché sur
   * chacune de ses pastilles.
   */
  readonly controle: ControleDuTransport | null;
  /** Les heures chiffrées qui ne tiennent pas dans les barres posées. « 0 » le plus souvent. */
  readonly heuresNonPlacees: string;
  readonly estMateriel: boolean;
  readonly estTransport: boolean;
  readonly duGroupe: boolean;
  /**
   * Ce code désigne un ARTICLE, et non un matériau (16/09/2026).
   *
   * Les deux sortent des carrières du groupe et portent `duGroupe` ; seul le préfixe du code les
   * sépare, et c'est le serveur qui le sait — `prefixe_article` est un paramètre d'installation.
   */
  readonly estArticle: boolean;
  readonly carriereCode: string | null;
  readonly modeLivraison: string | null;
  readonly fournisseur: string | null;
  /**
   * Le TRAJET : d'où, vers où, et combien de km (09/09/2026).
   *
   * Les trois dernières valeurs manquaient — le tableau de bord transport nommait le départ et
   * s'arrêtait là. Elles sont prises **ensemble** côté serveur (`trajetDuBesoin`) : le km est la
   * distance entre les deux villes, et le mélanger avec celles d'un autre porteur afficherait
   * une mesure qui ne correspond à aucun trajet réel.
   *
   * `villeProvenanceLibre` est exclusive de `villeProvenance` : c'est la ville de chargement
   * hors référentiel de la 073. `km` est nul quand la distance n'est pas calculable, et
   * `motifSansKm` dit alors pourquoi — `expliquerLAbsenceDeKm` le met en phrase.
   */
  readonly villeProvenance: string | null;
  readonly villeProvenanceLibre: string | null;
  readonly villeLivraison: string | null;
  readonly km: string | null;
  readonly motifSansKm: string | null;
  readonly kmEstForce: boolean;
  readonly affectations: readonly AffectationReelle[];
};

export type PlanGeneral = {
  readonly debut: string;
  readonly fin: string;
  readonly chantiers: readonly ChantierAuPlan[];
  readonly besoins: readonly BesoinDate[];
  readonly sansDate: readonly { chantierId: string; apo: string; nom: string }[];
};

/**
 * Le planning général sur une période, ou sur l'horizon par défaut.
 *
 * Sans bornes, le serveur applique J-7 → J+80 (métier, 16/08/2026) et **rend les dates
 * qu'il a retenues** dans `plan.debut` / `plan.fin`. L'écran remplit ses champs avec
 * celles-là : il n'y a qu'un seul endroit qui sache quel jour on est.
 */
export function chargerPlan(bornes?: {
  debut?: string;
  /** Une date, ou `derniere` — jusqu'au dernier jour planifié, quel qu'il soit. */
  fin?: string;
}): Promise<{ plan: PlanGeneral; aujourdhui: string }> {
  const parametres = new URLSearchParams();
  // Les deux bornes sont INDÉPENDANTES : n'en poser qu'une garde l'autre par défaut,
  // ce qui évite à l'écran de fabriquer une date qu'il ne veut pas changer.
  if (bornes?.debut !== undefined && bornes.debut !== "") parametres.set("debut", bornes.debut);
  if (bornes?.fin !== undefined && bornes.fin !== "") parametres.set("fin", bornes.fin);
  const requete = parametres.toString();
  return obtenir<{ plan: PlanGeneral; aujourdhui: string }>(
    `/api/plan${requete === "" ? "" : `?${requete}`}`,
  );
}

/* ---------------------------------------------------------------------------
 * Les anomalies du plan de charge (16/09/2026)
 * ------------------------------------------------------------------------- */

/** Ce qui empêchera une saisie de tomber juste. Liste fermée, côté serveur comme ici. */
export type SorteAnomalie =
  | "noeud_sans_saisisseur"
  | "personne_sans_compte"
  | "affecte_absent"
  | "besoin_sans_ressource"
  | "ressource_sur_deux_chantiers"
  | "livraison_sans_vehicule"
  | "chantier_sans_date";

/** Ce que le délai de deux jours ouvrés rend d'une anomalie. */
export type GraviteAnomalie = "depassee" | "critique" | "a_corriger";

export type Anomalie = {
  readonly sorte: SorteAnomalie;
  readonly gravite: GraviteAnomalie;
  readonly date: string | null;
  readonly joursOuvresRestants: number | null;
  readonly chantierId: string;
  readonly apo: string;
  readonly nomChantier: string;
  readonly ouvrage: string | null;
  readonly ouvrageLibelle: string | null;
  readonly quoi: string;
  readonly correction: string;
};

export type LectureAnomalies = {
  readonly debut: string;
  readonly fin: string;
  readonly aujourdhui: string;
  readonly anomalies: readonly Anomalie[];
  /** Faux = les absences n'ont pas pu être cherchées. L'écran doit le dire. */
  readonly absencesConnues: boolean;
  readonly absencesSansSalarie: number;
};

/**
 * Les anomalies d'affectation du plan de charge, sur une période.
 *
 * Réservé à l'administrateur et au responsable exploitation : le serveur rend 403 aux autres, et
 * l'onglet n'est pas proposé — mais c'est le serveur qui ferme, pas l'onglet caché.
 */
export function chargerAnomalies(bornes?: {
  debut?: string;
  fin?: string;
}): Promise<LectureAnomalies> {
  const parametres = new URLSearchParams();
  if (bornes?.debut !== undefined && bornes.debut !== "") parametres.set("debut", bornes.debut);
  if (bornes?.fin !== undefined && bornes.fin !== "") parametres.set("fin", bornes.fin);
  const requete = parametres.toString();
  return obtenir<LectureAnomalies>(
    `/api/plan/anomalies${requete === "" ? "" : `?${requete}`}`,
  );
}

/**
 * Ce que le contrôle du transport rend, ou ne peut pas dire.
 *
 * Les trois grandeurs ne se remplacent pas : les **voyages** demandent le tonnage et la charge
 * utile ; les **heures attendues** demandent en plus la distance ; la **distance implicite** ne
 * demande que les heures chiffrées — c'est celle qui marche partout, et aujourd'hui la seule
 * disponible sur la plupart des lignes.
 */
export type ControleDuTransport = {
  readonly voyages: number | null;
  readonly chargeUtile: string | null;
  readonly heuresAttendues: string | null;
  readonly heuresChiffrees: string;
  readonly ecartHeures: string | null;
  readonly distanceImplicite: string | null;
  readonly distanceKm: string | null;
  readonly parVoyage: {
    readonly routeHeures: string;
    readonly manoeuvreHeures: string;
    readonly heures: string;
    readonly kmAllerRetour: string;
  } | null;
  readonly parVoyageChiffre: string | null;
  readonly tonnage: string | null;
  readonly incapacites: readonly (
    | "unite_non_convertible"
    | "charge_utile_inconnue"
    | "distance_inconnue"
    | "pas_par_nos_soins"
    | "mode_non_renseigne"
  )[];
};

/* ---------------------------------------------------------------------------
 * Amené et repli de matériel
 * ------------------------------------------------------------------------- */

/** Un mouvement de machine, déduit du planning et corrigé par le responsable. */
export type Mouvement = {
  readonly cle: string;
  readonly chantierId: string;
  readonly apo: string;
  readonly chantierNom: string;
  readonly ville: string | null;
  readonly ressourceId: string;
  readonly code: string;
  readonly libelle: string;
  readonly postes: readonly string[];
  readonly fenetre: number;
  readonly sens: "amene" | "repli";
  readonly jourRelatif: number;
  readonly date: string | null;
  readonly jourDeduit: number;
  readonly dateDeduite: string | null;
  readonly decalageJours: number;
  readonly retire: boolean;
  readonly ajoute: boolean;
  readonly remarque: string | null;
  readonly correctionId: string | null;
  readonly apparieA: string | null;
  readonly fenetreDebut: number;
  readonly fenetreFin: number;
  /** Où livrer la machine : le chantier pour un amené, le site de repli pour un repli. */
  readonly destination: string | null;
  /** Le jour où le travail commence -- ou finit, pour un repli. */
  readonly dateTravail: string | null;
  /** Laquelle des deux moitiés du jour : « à quelle heure la machine arrête de travailler ». */
  readonly positionTravail: "matin" | "apres_midi" | null;
  /** La demi-journée du VOYAGE lui-même — `null` sur un mouvement ajouté à la main. */
  readonly position: "matin" | "apres_midi" | null;
  /** Celle que la RÈGLE implique, avant correction : l'écart se voit en les comparant. */
  readonly positionDeduite: "matin" | "apres_midi" | null;
  /** Celle que le responsable a ÉCRITE, telle quelle — `null` si la règle décide encore. */
  readonly moitieChoisie: "matin" | "apres_midi" | null;
  /** Les transferts possibles : le même engin attendu sur un autre chantier. */
  readonly transferts: readonly TransfertPossible[];
  /** Le transfert RETENU sur ce mouvement, s'il y en a un. Porté par le repli seul. */
  readonly transfertRetenu: TransfertRetenu | null;
};

/** Un transfert que le responsable peut décider, à la place d'un repli suivi d'un amené. */
export type TransfertPossible = {
  readonly cle: string;
  /**
   * Ce qu'il faut pour ÉCRIRE l'appariement, et non seulement pour le lire.
   *
   * La clé identifie une ligne à l'écran ; ces trois champs désignent le mouvement côté
   * serveur. Les porter évite au navigateur de découper la clé pour retrouver une adresse —
   * une clé est une identité d'affichage, et la faire servir d'adresse la figerait.
   */
  readonly chantierId: string;
  readonly ressourceId: string;
  readonly fenetre: number;
  readonly apo: string;
  readonly chantierNom: string;
  readonly ville: string | null;
  readonly date: string;
  /** Jours de calendrier entre les deux mouvements. Zéro = le même jour. */
  readonly ecartJours: number;
  /** Les demi-journées où ce transfert peut se faire — de la libération au besoin, incluses. */
  readonly creneaux: readonly CreneauDeTransfert[];
  /** Quand le travail démarre (ou finit) de l'autre côté : ce qui justifie le créneau. */
  readonly dateTravail: string | null;
  readonly positionTravail: "matin" | "apres_midi" | null;
  readonly role: "depart" | "arrivee";
};

/** Une demi-journée de calendrier : un transfert relie deux chantiers, donc deux frises. */
export type CreneauDeTransfert = {
  readonly date: string;
  readonly position: "matin" | "apres_midi";
};

/**
 * Un transfert DÉCIDÉ : un seul voyage, à la demi-journée que le responsable a choisie.
 *
 * Il n'est porté que par la moitié départ — le repli. La moitié arrivée ne s'affiche plus : un
 * transfert est **un** voyage, et en montrer deux lignes est la confusion que Chris a signalée.
 */
export type TransfertRetenu = {
  readonly cle: string;
  /** L'adresse de la moitié arrivée, pour changer de créneau sans défaire l'appariement. */
  readonly chantierId: string;
  readonly ressourceId: string;
  readonly fenetre: number;
  readonly apo: string;
  readonly chantierNom: string;
  readonly ville: string | null;
  /** Où la machine est attendue : le chantier receveur, et sa ville. */
  readonly destination: string;
  /** Quand le travail commence là-bas — ce qui justifie la date retenue. */
  readonly dateTravail: string | null;
  readonly positionTravail: "matin" | "apres_midi" | null;
  /** Les créneaux encore possibles, pour changer de date sans défaire l'appariement. */
  readonly creneaux: readonly CreneauDeTransfert[];
  /** Ce qui ne va PLUS, en clair, ou `null`. Un transfert devenu impossible doit se voir. */
  readonly alerte: string | null;
};

/**
 * Retenir un transfert, à la demi-journée choisie.
 *
 * *« Le responsable transport doit pouvoir organiser lui-même à quelle date il veut opérer le
 * transfert »* (Chris, 04/09/2026). Rappeler avec un autre créneau change la date sans défaire
 * l'appariement.
 */
export function retenirLeTransfert(demande: {
  depart: { chantierId: string; ressourceId: string; fenetre: number };
  arrivee: { chantierId: string; ressourceId: string; fenetre: number };
  date: string;
  position: "matin" | "apres_midi";
}): Promise<{ transfert: { idDepart: string; idArrivee: string } }> {
  return envoyer("/api/plan/transferts", "POST", demande);
}

/** Défaire un transfert retenu : les deux voyages redeviennent deux voyages. */
export function defaireLeTransfert(depart: {
  chantierId: string;
  ressourceId: string;
  fenetre: number;
}): Promise<{ defait: boolean }> {
  return envoyer("/api/plan/transferts", "DELETE", { depart });
}


/** Ce que le tableau de bord ne sait pas, et qu'il doit dire plutôt que de le taire. */
export type CeQuOnIgnore = {
  readonly machinesSansEngin: number;
  readonly chantiersSansDate: number;
  readonly correctionsOrphelines: number;
  /** Les machines dont le référentiel ne dit pas si elles montent sur porte-engin. */
  readonly machinesNonTranchees: readonly string[];
  /** Des engins affectés un jour où aucune barre ne travaille. */
  readonly affectationsSansTravail: number;
  /** Des transferts décidés dont l'appariement ne se retrouve plus, ou ne tient plus. */
  readonly appariementsARevoir: number;
};

export type PlanDesMouvements = {
  readonly debut: string;
  readonly fin: string;
  readonly mouvements: readonly Mouvement[];
  readonly ignore: CeQuOnIgnore;
};

export function chargerMouvements(bornes?: {
  debut?: string;
  fin?: string;
}): Promise<{ plan: PlanDesMouvements; aujourdhui: string }> {
  const parametres = new URLSearchParams();
  if (bornes?.debut !== undefined && bornes.debut !== "") parametres.set("debut", bornes.debut);
  if (bornes?.fin !== undefined && bornes.fin !== "") parametres.set("fin", bornes.fin);
  const requete = parametres.toString();
  return obtenir<{ plan: PlanDesMouvements; aujourdhui: string }>(
    `/api/plan/mouvements${requete === "" ? "" : `?${requete}`}`,
  );
}

/**
 * Pose ou modifie la correction d'un mouvement.
 *
 * **Rien du mouvement n'est écrit** : il se déduit du planning à chaque affichage. Ce qui part
 * ici est la décision du responsable — décaler, retirer, remarquer. Elle n'a aucune garde de
 * figeage : organiser n'est pas chiffrer.
 */
export function corrigerMouvement(demande: {
  chantierId: string;
  ressourceId: string;
  fenetre: number;
  sens: "amene" | "repli";
  decalageJours: number;
  retire: boolean;
  remarque: string | null;
  /**
   * La demi-journée choisie à la main, ou `null` pour rendre la main à la règle.
   *
   * **Toujours envoyée**, même quand ce n'est pas elle qu'on change : l'écriture est un
   * `UPSERT` et réécrit la ligne entière. L'omettre effacerait, d'un clic sur « +1 j », le
   * créneau qu'un transfert avait retenu.
   */
  positionChoisie: "matin" | "apres_midi" | null;
}): Promise<{
  correction: {
    id: string | null;
    decalageJours: number;
    retire: boolean;
    remarque: string | null;
    positionChoisie: "matin" | "apres_midi" | null;
  };
}> {
  return envoyer("/api/plan/mouvements", "PATCH", demande);
}

export function affecterRessource(demande: {
  chantierId: string;
  ligneId: string;
  jourRelatif: number;
  nature: "salarie" | "materiel";
  ressourceId: string;
}): Promise<{
  affectation: { id: string | null; libelle: string };
  /**
   * Ce que la table d'association a posé avec la personne, et ce qu'elle n'a pas pu poser (077).
   *
   * `null` quand la question ne se pose pas — une machine qu'on affecte, un double-clic. Les
   * **écarts** comptent autant que les poses : sans eux, une association qui ne trouve aucune
   * ligne ce jour-là se lit comme une fonction en panne.
   */
  associe: {
    poses: readonly { libelle: string; ligne: string }[];
    ecartes: readonly { libelle: string; raison: string }[];
  } | null;
  /**
   * Les journées SUIVANTES de la même ligne, remplies dans la foulée (15/09/2026).
   *
   * *Chris : « quand j'affecte sur la première journée une personne, tu peux l'affecter sur
   * toutes les journées suivantes, et appliquer aussi au matériel associé ».*
   *
   * Les jours **écartés** comptent autant que les jours posés : une case restée vide au milieu
   * de dix-neuf cases pleines se lit comme un oubli du produit, alors que c'est une absence ou
   * un besoin déjà couvert. `absencesNonVerifiees` dit qu'on a posé sans avoir pu regarder qui
   * était là — un silence se lirait « personne n'est absent ».
   */
  serie: {
    poses: readonly { jourRelatif: number; date: string | null }[];
    ecartes: readonly { jourRelatif: number; date: string | null; raison: string }[];
    materiel: {
      poses: readonly { libelle: string; ligne: string }[];
      ecartes: readonly { libelle: string; raison: string }[];
    };
    absencesNonVerifiees: boolean;
  } | null;
}> {
  return envoyer("/api/affectations", "POST", demande);
}

/**
 * Retire une ressource réelle d'un besoin daté.
 *
 * Nommée `…Reelle` pour ne pas se confondre avec `retirerAffectation`, qui retire
 * quelqu'un de l'**équipe du chantier** (017). Les deux gestes se ressemblent et ne
 * disent pas la même chose : l'un dit « il n'est plus sur ce chantier », l'autre « il
 * n'est pas là ce jour-là ».
 */
export function retirerAffectationReelle(id: string): Promise<{ retiree: boolean }> {
  return envoyer(`/api/affectations/${id}`, "DELETE", {});
}

/* ---------------------------------------------------------------------------
 * Le calendrier ouvré (PRD §4.4)
 * ------------------------------------------------------------------------- */

export type JourException = {
  readonly jour: string;
  /** Vrai = chômé en plus des fériés. Faux = travaillé exceptionnellement. */
  readonly chome: boolean;
  readonly motif: string | null;
};

export function chargerCalendrier(): Promise<{ exceptions: readonly JourException[] }> {
  return obtenir<{ exceptions: readonly JourException[] }>("/api/calendrier");
}

export function poserException(
  exception: JourException,
): Promise<{ exceptions: readonly JourException[] }> {
  return envoyer("/api/calendrier", "PUT", exception);
}

export function retirerException(jour: string): Promise<{ exceptions: readonly JourException[] }> {
  return envoyer(`/api/calendrier/${jour}`, "DELETE", {});
}

/* ---------------------------------------------------------------------------
 * Les ordres de réparation (031)
 * ------------------------------------------------------------------------- */

/**
 * La machine telle que le formulaire la préremplit, lue au référentiel maître.
 *
 * Plus large que le type `Materiel` ci-dessus, et c'est voulu : celui-là sert les listes de
 * choix du plan de charge et ne transporte que de quoi reconnaître une machine. Ici on décrit
 * l'engin à quelqu'un qui est debout devant lui. **Rien de financier ne traverse** — ni valeur
 * d'achat, ni mode de financement.
 */
export type FicheMachine = {
  readonly id: string;
  readonly codeErp: string;
  readonly description: string | null;
  readonly type: string | null;
  readonly generique: string | null;
  readonly categorie: string | null;
  readonly immatriculation: string | null;
  readonly numSerie: string | null;
  readonly societe: string | null;
  readonly societeProprietaire: string | null;
  readonly fournisseur: string | null;
  readonly etatParc: string | null;
  readonly etatAchat: string | null;
  readonly affectation: string | null;
  readonly poids: string | null;
  readonly essieux: string | null;
  readonly dateEntree: string | null;
  readonly dateSortie: string | null;
  /** Sortie du parc, fiche inactive ou date de sortie passée. Signalé, jamais masqué. */
  readonly horsParc: boolean;
  readonly aPhoto1: boolean;
  readonly aPhoto2: boolean;
};

/**
 * Un type de remontée d'incident (088).
 *
 * Les cinq de Chris — casse, panne, préventif, signalement, accident — mais lus en base et non
 * écrits ici : *« chacun déclenche un circuit différent, à valider et à compléter »*, donc un
 * sixième ne doit être ni une migration ni un déploiement du navigateur.
 */
export type TypeIncident = {
  readonly code: string;
  readonly libelle: string;
  readonly exemple: string | null;
  /** `aucun | a_qualifier | immediate` — ce que le type PROPOSE à la qualification. */
  readonly effetMateriel: string;
  readonly actif: boolean;
};

export type OrdreReparation = {
  readonly id: string;
  readonly numero: number;
  readonly materielId: string;
  readonly materielCode: string;
  readonly materielDescription: string | null;
  readonly materielType: string | null;
  readonly materielImmatriculation: string | null;
  readonly materielNumSerie: string | null;
  readonly materielSociete: string | null;
  readonly materielEtatParc: string | null;
  readonly constat: string;
  readonly typeCode: string | null;
  readonly chantierId: string | null;
  readonly horsChantier: boolean;
  readonly immobiliseeConstatee: boolean | null;
  readonly statut: string;
  readonly issue: string | null;
  /** Le nom de l'encadrant en copie. Son adresse ne traverse pas : le serveur seul en a l'usage. */
  readonly nPlus1Libelle: string | null;
  /** `fiche | courriel | libelle | inconnu` — jusqu'où la copie au N+1 a pu aller. */
  readonly nPlus1Resolu: string;
  readonly saisiParLibelle: string;
  readonly creeLe: string;
};

/** Une remontée telle que la pile SAV la montre, avec son immobilisation s'il y en a une. */
export type Incident = {
  readonly id: string;
  readonly numero: number;
  readonly materielId: string;
  readonly materielCode: string;
  readonly materielDescription: string | null;
  readonly materielImmatriculation: string | null;
  readonly constat: string;
  readonly typeCode: string | null;
  readonly typeLibelle: string | null;
  readonly effetMateriel: string | null;
  readonly chantierId: string | null;
  readonly chantierApo: string | null;
  readonly chantierNom: string | null;
  readonly horsChantier: boolean;
  readonly immobiliseeConstatee: boolean | null;
  readonly statut: string;
  readonly issue: string | null;
  readonly qualificationMotif: string | null;
  readonly qualifieParLibelle: string | null;
  readonly qualifieLe: string | null;
  readonly nPlus1Libelle: string | null;
  readonly nPlus1Resolu: string;
  readonly saisiParLibelle: string;
  readonly creeLe: string;
  readonly photos: number;
  readonly immobilisation: {
    readonly id: string;
    readonly debut: string | null;
    readonly remisePrevue: string | null;
    readonly remiseReelle: string | null;
    readonly soldee: boolean;
    readonly besoinLe: string | null;
    readonly besoinMotif: string | null;
  } | null;
};

/**
 * Cherche la machine que la caméra vient de lire.
 *
 * **Le texte scanné part TEL QUEL** : c'est le serveur qui en extrait le code ERP. L'étiquette
 * encode une adresse du référentiel maître, et la règle « que faire d'une adresse qui ne désigne
 * pas une machine » est une règle métier, vérifiée par des cas de test — pas un bout de code
 * d'écran.
 */
export function chercherMachine(scan: string): Promise<{ machine: FicheMachine; code: string }> {
  return obtenir<{ machine: FicheMachine; code: string }>(
    `/api/reparations/machine?scan=${encodeURIComponent(scan)}`,
  );
}

/**
 * Les adresses des photos d'engin, redemandées au moment d'afficher.
 *
 * Un appel à part de la fiche : une URL signée ne vaut que 15 minutes, la garder avec la fiche
 * en cache servirait des adresses périmées. `perimetreManquant` distingue « pas de photo » de
 * « photos présentes mais la clé n'a pas le droit de les ouvrir » — sans quoi un cadre vide se
 * lirait comme une machine sans photo.
 */
export function chargerPhotosMachine(materielId: string): Promise<{
  readonly photos: Record<string, string>;
  readonly perimetreManquant: boolean;
  readonly message: string | null;
}> {
  return obtenir(`/api/reparations/machine/${materielId}/photos`);
}

export function chargerOrdres(materielId?: string): Promise<{
  readonly ordres: readonly OrdreReparation[];
}> {
  const requete = materielId === undefined ? "" : `?materielId=${materielId}`;
  return obtenir(`/api/reparations${requete}`);
}

/**
 * Enregistre l'ordre.
 *
 * Seuls le code et la saisie partent : **la fiche machine est relue au référentiel par le
 * serveur**, elle n'est pas reprise de l'écran. Accepter les champs affichés laisserait écrire
 * n'importe quel numéro de série sur n'importe quelle machine.
 */
export function creerOrdre(demande: {
  scan: string;
  constat: string;
  typeCode: string;
  chantierId?: string;
  horsChantier: boolean;
  immobiliseeConstatee: boolean;
  /** Le kilometrage ou le compteur horaire au moment de l'incident (091). Facultatif. */
  compteur?: string;
  compteurUnite?: 'km' | 'heures';
}): Promise<{ ordre: OrdreReparation }> {
  return envoyer<{ ordre: OrdreReparation }>("/api/reparations", "POST", demande);
}

/* ---------------------------------------------------------------------------
 * Le module SAV : remontée, qualification, immobilisation (088 à 090)
 * ------------------------------------------------------------------------- */

export function chargerTypesIncident(): Promise<{ readonly types: readonly TypeIncident[] }> {
  return obtenir("/api/reparations/types");
}

/** Une machine telle que la liste de recherche la propose : de quoi la reconnaître, rien de plus. */
export type MachineTrouvee = {
  readonly id: string;
  readonly codeErp: string;
  readonly description: string | null;
  readonly type: string | null;
  readonly immatriculation: string | null;
  /** Sortie du parc, fiche inactive ou date de sortie passée. Proposée quand même, et signalée. */
  readonly horsParc: boolean;
};

/**
 * Cherche une machine par code OU par libellé, au fur et à mesure de la frappe.
 *
 * *Demande de Chris, 21/09/2026 : « il faut proposer la liste au fur et à mesure de la saisie, ou
 * il faut pouvoir aussi saisir le libellé, par exemple pelle ».* La recherche porte sur le code
 * ERP, la description, le type et l'immatriculation — « pelle » est dans l'un ou l'autre selon les
 * fiches, et une plaque est le seul repère qu'on lit de loin sur un camion.
 */
export function chercherMachines(
  q: string,
): Promise<{
  readonly machines: readonly MachineTrouvee[];
  /** Vrai quand la liste est coupée : une liste pleine et une liste coupée se ressemblent. */
  readonly tronque: boolean;
  readonly plafond: number;
}> {
  return obtenir(`/api/reparations/machines?q=${encodeURIComponent(q)}`);
}

/**
 * Ce compte peut-il déclarer, et sinon pourquoi.
 *
 * L'écran s'en sert pour **expliquer**, jamais pour garder : le contrôle qui compte est celui de
 * la route d'écriture — un menu caché n'empêche pas d'appeler l'API.
 */
export function chargerDroitsIncident(): Promise<{
  readonly peutDeclarer: boolean;
  readonly motif: string | null;
}> {
  return obtenir("/api/reparations/droits");
}

/**
 * Joint une photo du dégât.
 *
 * En **multipart**, comme les photos d'article : c'est ce qu'un `<input type="file">` sait faire
 * sans recodage, et le binaire traverse tel quel.
 */
export async function joindrePhotoIncident(
  incidentId: string,
  fichier: File,
): Promise<{ readonly photo: { readonly id: string; readonly ordre: number } }> {
  const corps = new FormData();
  corps.append("photo", fichier);
  const reponse = await fetch(`/api/reparations/${incidentId}/photo`, {
    method: "POST",
    body: corps,
    credentials: "same-origin",
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => ({}))) as { erreur?: string };
    throw new Error(erreur.erreur ?? `La photo n'a pas pu être jointe (${String(reponse.status)}).`);
  }
  return (await reponse.json()) as { photo: { id: string; ordre: number } };
}

export function chargerPhotosIncident(
  incidentId: string,
): Promise<{ readonly photos: readonly { readonly id: string; readonly ordre: number }[] }> {
  return obtenir(`/api/reparations/${incidentId}/photos`);
}

export function chargerIncidents(ouvertesSeules = false): Promise<{
  readonly incidents: readonly Incident[];
  readonly peutQualifier: boolean;
  /** Faux tant qu'aucune assistante ni responsable n'est désigné : l'écran doit le DIRE. */
  readonly pileDesignee: boolean;
}> {
  return obtenir(`/api/incidents${ouvertesSeules ? "?ouvertes=1" : ""}`);
}

export function qualifierIncident(
  id: string,
  decision: {
    issue: "sans_suite" | "atelier" | "immobilisation";
    motif?: string;
    debut?: string;
    remisePrevue?: string;
  },
): Promise<{ readonly qualifie: boolean }> {
  return envoyer(`/api/incidents/${id}/qualification`, "POST", decision);
}

export function deplacerRemise(
  immobilisationId: string,
  remisePrevue: string,
  motif?: string,
): Promise<{ readonly remisePrevue: string }> {
  return envoyer(`/api/immobilisations/${immobilisationId}`, "PATCH", { remisePrevue, motif });
}

export function solderImmobilisation(
  immobilisationId: string,
  remiseReelle?: string,
): Promise<{ readonly soldee: boolean; readonly remiseReelle: string }> {
  return envoyer(`/api/immobilisations/${immobilisationId}/solde`, "POST", { remiseReelle });
}

/** « J'ai besoin de cette machine le 19 » — une date vide retire la demande. */
export function poserLeBesoin(
  immobilisationId: string,
  besoinLe: string,
  motif?: string,
): Promise<{ readonly besoinLe: string | null }> {
  return envoyer(`/api/immobilisations/${immobilisationId}/besoin`, "POST", { besoinLe, motif });
}

export type ImmobilisationEnCours = {
  readonly id: string;
  readonly materielId: string;
  readonly materielCode: string;
  readonly debut: string | null;
  readonly remisePrevue: string | null;
  readonly numero: number;
  readonly typeLibelle: string | null;
  readonly constat: string;
  readonly besoinLe: string | null;
};

export function chargerImmobilisations(): Promise<{
  readonly immobilisations: readonly ImmobilisationEnCours[];
}> {
  return obtenir("/api/immobilisations");
}

export type DestinataireSav = {
  readonly utilisateurId: string;
  readonly role: "assistante" | "responsable";
  readonly nom: string;
  readonly prenom: string;
  readonly courriel: string;
  readonly statut: string;
};

export function chargerDestinatairesSav(): Promise<{
  readonly destinataires: readonly DestinataireSav[];
}> {
  return obtenir("/api/destinataires-sav");
}

export function poserDestinataireSav(
  utilisateurId: string,
  role: "assistante" | "responsable",
): Promise<{ readonly destinataires: readonly DestinataireSav[] }> {
  return envoyer("/api/destinataires-sav", "PUT", { utilisateurId, role });
}

export function retirerDestinataireSav(
  utilisateurId: string,
  role: "assistante" | "responsable",
): Promise<{ readonly destinataires: readonly DestinataireSav[] }> {
  return envoyer(
    `/api/destinataires-sav?utilisateurId=${utilisateurId}&role=${role}`,
    "DELETE",
    undefined,
  );
}

/* ---------------------------------------------------------------------------
 * Le référentiel des articles (038)
 * ------------------------------------------------------------------------- */

/**
 * La fiche article, telle que le serveur la rend.
 *
 * Les 27 champs du classeur de l'ERP, plus la présence des deux photos. Les montants sont des
 * CHAÎNES et jamais des nombres : le serveur les lit en `::text` pour ne pas perdre de centimes,
 * et les convertir ici reperdrait ce qu'il a préservé.
 */
export type Article = {
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly uniteBase: string | null;
  readonly codeFamille: string | null;
  readonly coutUnitaire: string | null;
  readonly coutEtude: string | null;
  readonly prixUnitaire: string | null;
  readonly procedureAppro: string | null;
  readonly fournisseurNumero: string | null;
  readonly fournisseurNom: string | null;
  readonly categorieDemandePrix: string | null;
  readonly codeCategorieArticle: string | null;
  /** La colonne « Stocks » DU CLASSEUR de l'ERP, écrasée à chaque réimport. */
  readonly stocks: string | null;
  /**
   * Le stock que l'application tient, somme des mouvements (066) — jamais nul, au pire « 0.000 ».
   *
   * *Question de Chris, 09/09/2026 : « pourquoi la quantité entrée en stock n'a pas été
   * reprise ? »* Elle l'avait été. L'écran n'affichait que `stocks`, sous un en-tête qui dit
   * « Stocks » — le reflet de l'ERP, vide sur un article que l'application vient de créer. Deux
   * grandeurs, un seul nom visible, et la seule qui bouge était celle qu'on ne voyait pas.
   *
   * **En lecture seule** : elle se recalcule depuis les mouvements. Elle se corrige à l'écran
   * Stocks, en posant le mouvement qui manque.
   */
  readonly stockMagasin: string;
  readonly bloque: boolean;
  readonly centreCode: string | null;
  readonly activiteCode: string | null;
  readonly natureCode: string | null;
  readonly description2: string | null;
  readonly modeleEchelonnement: string | null;
  readonly code1: string | null;
  readonly libelleCode1: string | null;
  readonly code2: string | null;
  readonly libelleCode2: string | null;
  readonly code3: string | null;
  readonly libelleCode3: string | null;
  readonly code4: string | null;
  readonly libelleCode4: string | null;
  readonly photo1: boolean;
  readonly photo2: boolean;
  readonly modifieLe: string;
};

export type BilanImportArticles = {
  readonly ajoutes: number;
  readonly modifies: number;
  readonly inchanges: number;
  readonly colonnesAbsentes: readonly string[];
  readonly refuses: readonly { ligne: number; raison: string }[];
};

/**
 * Les articles, et le DÉBUT de ce que leur QR code encode.
 *
 * `baseQr` vaut `https://…/article/` : le domaine vient de la configuration du serveur et n'est
 * pas connu du navigateur. On y ajoute le code pour obtenir l'adresse exacte d'une fiche — voir
 * `adresseEncodeeDansLeQr`.
 */
export function chargerArticles(): Promise<{
  readonly articles: readonly Article[];
  readonly baseQr: string;
}> {
  return obtenir<{ articles: readonly Article[]; baseQr: string }>("/api/articles");
}

/**
 * L'adresse qu'un QR code contient, pour l'AFFICHER sous l'image.
 *
 * Composée exactement comme le serveur la compose — préfixe plus code encodé —, et pas autrement :
 * cette ligne sert à vérifier ce qu'une étiquette porte avant d'en imprimer deux mille. Une
 * version précédente affichait un texte à trous jamais rempli (`…/article/%7Bcode%7D`), donc un
 * contrôle qui ne contrôlait rien.
 */
export function adresseEncodeeDansLeQr(baseQr: string, code: string): string {
  return baseQr + encodeURIComponent(code);
}

export function creerArticle(demande: {
  code: string;
  description: string;
}): Promise<Article> {
  return envoyer<Article>("/api/articles", "POST", demande);
}

export function modifierArticle(id: string, champs: Record<string, unknown>): Promise<Article> {
  return envoyer<Article>(`/api/articles/${id}`, "PATCH", champs);
}

export async function supprimerArticle(id: string): Promise<void> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/articles/${id}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été supprimé.");
  }
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(corps?.erreur ?? `Le serveur a refusé la suppression (${reponse.status}).`);
  }
}

export const ADRESSE_CLASSEUR_ARTICLES = "/api/articles/classeur";

export async function importerArticles(fichier: File): Promise<BilanImportArticles> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(ADRESSE_CLASSEUR_ARTICLES, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanImportArticles;
}

/** L'image du QR code d'un article. Une adresse, pas un appel : c'est un `<img src>`. */
/**
 * Une empreinte courte et stable d'une chaîne, pour distinguer deux adresses d'image.
 *
 * Écrite à la main plutôt que prise à `crypto.subtle` : celui-ci est asynchrone et indisponible
 * hors HTTPS, ce qui obligerait à rendre asynchrone la construction d'un attribut `src`. Il ne
 * s'agit pas de sécurité — seulement de faire changer une adresse quand son contenu change.
 */
function empreinteCourte(texte: string): string {
  let h = 2166136261;
  for (let i = 0; i < texte.length; i++) {
    h ^= texte.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/**
 * L'adresse de l'image du QR code d'un article.
 *
 * **L'empreinte de ce que le QR encode fait partie de l'adresse**, et c'est la seule chose qui
 * protège vraiment d'une image périmée. *Mesuré en production le 20/08/2026 : après avoir changé
 * ce que le QR contient — le code seul, puis l'adresse de la fiche —, les téléphones ont continué
 * de servir l'ANCIENNE image pendant vingt-quatre heures. Le serveur avait été corrigé pour faire
 * revalider, mais une réponse déjà gardée avec un `max-age` ne redemande rien : il n'existe aucun
 * moyen, côté serveur, de rattraper une image déjà distribuée à une adresse fixe.*
 *
 * Changer l'adresse, si. Le navigateur n'a rien en mémoire pour celle-ci, donc il la demande.
 * C'est ce qu'il fallait faire dès le début, et ce qu'il faudra garder : demander à quelqu'un de
 * vider le cache de son téléphone n'est pas une correction, c'est un aveu.
 */
export function adresseQrArticle(id: string, baseQr: string): string {
  return `/api/articles/${id}/qr.svg?v=${empreinteCourte(baseQr)}`;
}

/**
 * La planche d'étiquettes à imprimer, pour une sélection de codes ou pour tout le référentiel.
 *
 * Une page HTML que le navigateur ouvre et imprime : le serveur ne fabrique pas de PDF, faute
 * du service conteneurisé que l'autre application utilise et que Docker, écarté ici, interdit.
 */
export function adresseEtiquettesArticles(codes: readonly string[] = []): string {
  const choix = codes.length === 0 ? "" : `?codes=${codes.map(encodeURIComponent).join(",")}`;
  return `/api/articles/etiquettes${choix}`;
}

/**
 * L'adresse d'une photo.
 *
 * `modifieLe` est ajouté en paramètre pour que l'image affichée suive la fiche : le serveur
 * met la photo en cache pour un an — son nom de fichier change à chaque dépôt —, mais
 * l'adresse vue par le navigateur, elle, doit changer aussi.
 */
export function adressePhotoArticle(article: Article, numero: 1 | 2): string {
  return `/api/articles/${article.id}/photos/${numero}?v=${encodeURIComponent(article.modifieLe)}`;
}

export async function deposerPhotoArticle(
  id: string,
  numero: 1 | 2,
  fichier: File,
): Promise<Article> {
  const corps = new FormData();
  corps.append("photo", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/articles/${id}/photos/${numero}`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : la photo n'a pas été déposée.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé la photo (${reponse.status}).`);
  }
  return (await reponse.json()) as Article;
}

export async function retirerPhotoArticle(id: string, numero: 1 | 2): Promise<Article> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/articles/${id}/photos/${numero}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Le serveur ne répond pas : la photo n'a pas été retirée.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le retrait (${reponse.status}).`);
  }
  return (await reponse.json()) as Article;
}

/* ---------------------------------------------------------------------------
 * Le parc matériel — consultation, QR et photos
 * ------------------------------------------------------------------------- */

/**
 * Une machine, **toute sa fiche**.
 *
 * *Chris, 20/08/2026 : « je veux tout afficher sans exception, donc tu ne décides pas pour moi de
 * ce qu'il faut rapatrier ».* Les 33 champs que le référentiel maître rend, à trois exceptions
 * qu'il ferme lui-même : la carte grise (elle porte le nom et l'adresse du titulaire), la plaque
 * de tare, et l'adresse des photos — celle-là se redemande au moment d'afficher, une URL signée
 * ne vivant que quinze minutes.
 *
 * **Rien ne se modifie ici.** archive-roy est le référentiel maître : une correction se fait
 * là-bas et se voit ici au rafraîchissement suivant. Il n'y a donc aucune fonction d'écriture
 * dans ce fichier pour le matériel, par construction.
 */
export type FicheParc = {
  readonly id: string;
  readonly codeErp: string;
  readonly generique: string | null;
  readonly type: string | null;
  readonly description: string | null;
  readonly immatriculation: string | null;
  readonly numSerie: string | null;
  readonly fournisseur: string | null;
  readonly sousCategorie: string | null;
  readonly categorie: string | null;
  readonly codeCategorie: string | null;
  readonly societe: string | null;
  readonly societeProprietaire: string | null;
  readonly poids: string | null;
  readonly essieux: string | null;
  readonly affectationVehicule: string | null;
  /** Vrai, faux, ou **inconnu** — les trois se distinguent, et une case vide n'est pas un « non ». */
  readonly assurance: boolean | null;
  readonly bdm: boolean | null;
  readonly cbImmo: string | null;
  readonly dateEntree: string | null;
  readonly dateSortie: string | null;
  readonly dateRachat: string | null;
  readonly modeFinancement: string | null;
  readonly etatAchat: string | null;
  readonly etatParc: string | null;
  readonly valeurAchat: string | null;
  readonly statut: string | null;
  readonly actif: boolean | null;
  readonly creeLe: string | null;
  readonly modifieLe: string | null;
  readonly aPhoto1: boolean;
  readonly aPhoto2: boolean;
};

/**
 * TOUT le parc — sorties du parc comprises — et le début de ce que les QR encodent.
 *
 * Différent de `chargerMateriels`, qui ne rend que les machines **affectables** : une liste de
 * choix ne doit pas proposer un engin vendu, mais une étiquette collée sur une machine doit
 * retrouver sa fiche même si le référentiel l'a sortie du parc.
 */
export function chargerParc(): Promise<{
  readonly materiels: readonly FicheParc[];
  readonly baseQr: string;
}> {
  return obtenir<{ materiels: readonly FicheParc[]; baseQr: string }>("/api/materiels/parc");
}

/** L'adresse de l'image du QR d'une machine, empreinte comprise — voir `adresseQrArticle`. */
export function adresseQrMateriel(id: string, baseQr: string): string {
  return `/api/materiels/${encodeURIComponent(id)}/qr.svg?v=${empreinteCourte(baseQr)}`;
}

/** L'adresse encodée dans le QR d'une machine, pour l'afficher sous l'image. */
export function adresseEncodeeDansLeQrMateriel(baseQr: string, codeErp: string): string {
  return baseQr + encodeURIComponent(codeErp);
}

/** La planche d'étiquettes 6 × 6 cm, pour une sélection de codes ou pour tout le parc. */
export function adresseEtiquettesMateriel(codes: readonly string[] = []): string {
  const choix = codes.length === 0 ? "" : `?codes=${codes.map(encodeURIComponent).join(",")}`;
  return `/api/materiels/etiquettes${choix}`;
}

/**
 * Les deux photos d'engin, **redemandées au moment d'afficher**.
 *
 * Le référentiel rend des URL signées valables quinze minutes : les transporter avec la liste
 * servirait parfois des adresses périmées, donc des vignettes cassées sans qu'on sache pourquoi.
 *
 * `perimetreManquant` distingue « pas de photo » de « photo inaccessible » : la seconde n'est pas
 * une panne, c'est une clé d'accès à laquelle il manque le périmètre `materiels_photos`. Un cadre
 * vide se lirait « il n'y a pas de photo », ce qui serait faux.
 */
export function chargerPhotosMateriel(id: string): Promise<{
  readonly photos: Record<string, string | null>;
  readonly perimetreManquant: boolean;
  readonly message: string | null;
}> {
  return obtenir(`/api/materiels/${encodeURIComponent(id)}/photos`);
}

/* ---------------------------------------------------------------------------
 * Le paramétrage du transport (052)
 * -------------------------------------------------------------------------
 *
 * *Demande de Chris, 01/09/2026 : « j'aimerai créer un nouveau menu paramétrage transport dans
 * l'appli avec ces deux tables […] les deux tables doivent être indépendantes. »*
 *
 * Les deux moitiés partagent leurs formes et leurs appels, parce que l'écran les dessine avec
 * le même composant : ce qui les distingue vit côté serveur (le tonnage n'existe que sur les
 * couples) et se lit ici au champ facultatif.
 */

/** Laquelle des deux tables. Le mot voyage dans l'URL — c'est le vocabulaire de Chris. */
export type TableTransport = "couples" | "combinaisons";

export type LigneTransport = {
  readonly id: string;
  readonly ressourceId: string;
  readonly code: string;
  readonly libelle: string;
  readonly familleCode: string | null;
  readonly familleLibelle: string | null;
  readonly codeAnalytique: string | null;
  readonly coutHoraire: string | null;
  readonly estTransport: boolean;
  readonly porteEngin: boolean | null;
  readonly actif: boolean;
  /** La charge utile en tonnes. Absent côté combinaisons ; `null` = pas renseigné. */
  readonly tonnage?: string | null;
};

export type GroupeTransport = {
  readonly id: string;
  readonly numero: number;
  readonly actif: boolean;
  /** Les identifiants de LIGNE qui le composent — pas les identifiants de ressource. */
  readonly membres: readonly string[];
};

export type ContenuTransport = {
  readonly lignes: readonly LigneTransport[];
  readonly groupes: readonly GroupeTransport[];
};

export function chargerTransport(table: TableTransport): Promise<ContenuTransport> {
  return obtenir<ContenuTransport>(`/api/transport/${table}`);
}

export function ajouterLigneTransport(
  table: TableTransport,
  ressourceId: string,
  tonnage: string | null,
): Promise<LigneTransport> {
  return envoyer<LigneTransport>(`/api/transport/${table}/lignes`, "POST", { ressourceId, tonnage });
}

/** Le tonnage n'existe que sur les couples : l'adresse le dit, plutôt qu'un paramètre ignoré. */
export function fixerTonnageTransport(ligneId: string, tonnage: string | null): Promise<LigneTransport> {
  return envoyer<LigneTransport>(`/api/transport/couples/lignes/${ligneId}`, "PATCH", { tonnage });
}

export function creerGroupeTransport(table: TableTransport): Promise<GroupeTransport> {
  return envoyer<GroupeTransport>(`/api/transport/${table}/groupes`, "POST", {});
}

/** Une réponse sans corps : `envoyer` lirait un JSON qui n'existe pas. */
async function sansCorps(chemin: string, methode: "DELETE" | "PUT", corps?: unknown): Promise<void> {
  let reponse: Response;
  const envoi =
    corps === undefined
      ? { method: methode, headers: { Accept: "application/json" } }
      : { method: methode, ...envoiJson(corps) };
  try {
    reponse = await fetch(chemin, envoi);
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été enregistré.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé la demande (${reponse.status}).`);
  }
}

export function retirerLigneTransport(table: TableTransport, ligneId: string): Promise<void> {
  return sansCorps(`/api/transport/${table}/lignes/${ligneId}`, "DELETE");
}

export function supprimerGroupeTransport(table: TableTransport, groupeId: string): Promise<void> {
  return sansCorps(`/api/transport/${table}/groupes/${groupeId}`, "DELETE");
}

export function cocherTransport(
  table: TableTransport,
  groupeId: string,
  ligneId: string,
  present: boolean,
): Promise<void> {
  return sansCorps(`/api/transport/${table}/groupes/${groupeId}/lignes/${ligneId}`, "PUT", { present });
}

export function adresseClasseurTransport(table: TableTransport): string {
  return `/api/transport/${table}/classeur`;
}

export type BilanTransport = {
  readonly lignesAjoutees: number;
  readonly lignesConnues: number;
  readonly tonnagesPoses: number;
  readonly groupesAvant: number;
  readonly groupesApres: number;
  readonly colonnesVides: number;
  readonly refusees: readonly { readonly ligne: number; readonly raison: string }[];
};

export async function importerTransport(table: TableTransport, fichier: File): Promise<BilanTransport> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/transport/${table}/classeur`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanTransport;
}

/* ---------------------------------------------------------------------------
 * Les indemnités par zone de transport (053)
 * -------------------------------------------------------------------------
 *
 * *Demande de Chris, 02/09/2026 : « je veux que tu crées une table de référence avec le tableau
 * des indemnités (Feuil1) dans le menu Paramétrage transport, avant le tableau transport de
 * matériaux. Attention, cette table doit avoir une date de validité du 01/01/2026 au 31/12/2026 ;
 * tous les ans nous modifierons les montants. Il faudra historiser ces montants. »*
 *
 * Les nombres voyagent en **chaînes**, comme partout ailleurs : `1.97` repassé par un flottant
 * de JavaScript n'est plus tout à fait `1.97`.
 */

export type PeriodeIndemnite = {
  readonly id: string;
  readonly libelle: string;
  readonly debut: string;
  readonly fin: string;
};

export type ZoneIndemnite = {
  readonly id: string;
  readonly libelle: string;
  /** Borne basse EXCLUE, sauf pour la première zone qui part de 0. */
  readonly kmMin: string;
  /** Borne haute INCLUSE. */
  readonly kmMax: string;
  readonly montant: string | null;
  readonly partSalaireBrut: string | null;
};

/** Ce que le découpage a d'incohérent, compté par le serveur. */
export type CoherenceZones = {
  readonly sansIndemnite: readonly string[];
  readonly chevauchements: readonly { readonly premiere: string; readonly seconde: string }[];
  readonly trous: readonly { readonly apres: string; readonly avant: string }[];
  readonly couverteJusqua: string | null;
};

export type BaremeIndemnites = {
  readonly periodes: readonly PeriodeIndemnite[];
  readonly periode: PeriodeIndemnite | null;
  readonly periodeProposee: { readonly libelle: string; readonly debut: string; readonly fin: string };
  readonly zones: readonly ZoneIndemnite[];
  readonly coherence: CoherenceZones;
  /** Le jour lu par la BASE : l'écran n'invente jamais une date dans le fuseau du poste. */
  readonly aujourdhui: string;
};

export function chargerBaremeIndemnites(periodeId?: string | null): Promise<BaremeIndemnites> {
  const suffixe = periodeId === undefined || periodeId === null ? "" : `?periode=${periodeId}`;
  return obtenir<BaremeIndemnites>(`/api/transport/indemnites${suffixe}`);
}

export type BornesPeriode = { readonly libelle: string; readonly debut: string; readonly fin: string };

export function creerPeriodeIndemnite(demande: BornesPeriode): Promise<PeriodeIndemnite> {
  return envoyer<PeriodeIndemnite>("/api/transport/indemnites/periodes", "POST", demande);
}

/** L'année suivante, avec les zones de celle-ci : le geste qui rend l'historisation tenable. */
export function dupliquerPeriodeIndemnite(
  sourceId: string,
  demande: BornesPeriode,
): Promise<{ periode: PeriodeIndemnite; zones: number }> {
  return envoyer(`/api/transport/indemnites/periodes/${sourceId}/duplication`, "POST", demande);
}

export function modifierPeriodeIndemnite(
  id: string,
  demande: Partial<BornesPeriode>,
): Promise<PeriodeIndemnite> {
  return envoyer<PeriodeIndemnite>(`/api/transport/indemnites/periodes/${id}`, "PATCH", demande);
}

export function supprimerPeriodeIndemnite(id: string): Promise<{ supprimee: boolean }> {
  return envoyer(`/api/transport/indemnites/periodes/${id}`, "DELETE", {});
}

export type EcritureZone = {
  readonly libelle: string;
  readonly kmMin: string;
  readonly kmMax: string;
  readonly montant: string | null;
  readonly partSalaireBrut: string | null;
};

export function ajouterZoneIndemnite(periodeId: string, demande: EcritureZone): Promise<ZoneIndemnite> {
  return envoyer<ZoneIndemnite>(`/api/transport/indemnites/periodes/${periodeId}/zones`, "POST", demande);
}

/**
 * Corrige une zone.
 *
 * **Les deux formes d'indemnité s'excluent, et c'est le SERVEUR qui le tient** : envoyer un
 * montant efface la part de salaire, et réciproquement. L'écran n'a donc pas à vider l'autre
 * champ — une règle recopiée dans le navigateur est une règle qui finira par diverger.
 */
export function modifierZoneIndemnite(
  id: string,
  demande: Partial<EcritureZone>,
): Promise<ZoneIndemnite> {
  return envoyer<ZoneIndemnite>(`/api/transport/indemnites/zones/${id}`, "PATCH", demande);
}

export function supprimerZoneIndemnite(id: string): Promise<void> {
  return sansCorps(`/api/transport/indemnites/zones/${id}`, "DELETE");
}

export function adresseClasseurIndemnites(periodeId: string): string {
  return `/api/transport/indemnites/periodes/${periodeId}/classeur`;
}

export type BilanIndemnites = {
  readonly zonesAvant: number;
  readonly zonesApres: number;
  readonly montantsPoses: number;
  readonly partsPosees: number;
  readonly sansIndemnite: number;
  /** Les cellules d'indemnité qui n'étaient pas un nombre, et ce qu'on en a fait. */
  readonly interpretations: readonly {
    readonly zone: string;
    readonly lu: string;
    readonly compris: string;
  }[];
  readonly refusees: readonly { readonly ligne: number; readonly raison: string }[];
};

export async function importerBaremeIndemnites(
  periodeId: string,
  fichier: File,
): Promise<BilanIndemnites> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/transport/indemnites/periodes/${periodeId}/classeur`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanIndemnites;
}

/* ---------------------------------------------------------------------------
 * Les paniers par zone (054)
 * -------------------------------------------------------------------------
 *
 * *Demande de Chris, 02/09/2026 : « dans le menu paramétrage transport il faut aussi rajouter la
 * table Panier. Même chose que pour les zones transport, il doit être historisé et avoir une date
 * de validité, la première du 01/01/2026 au 31/12/2026. »*
 *
 * Jumeau des indemnités, et volontairement pas leur abstraction : **les bornes ne se lisent pas
 * dans le même sens**. Le classeur des indemnités porte le chevron sur la borne basse (`>10`),
 * celui des paniers sur la borne haute (`<75`). Un module commun aurait porté ce sens en
 * paramètre — le genre de drapeau qu'on inverse un jour sans s'en apercevoir.
 */

export type PeriodePanier = {
  readonly id: string;
  readonly libelle: string;
  readonly debut: string;
  readonly fin: string;
};

export type ZonePanier = {
  readonly id: string;
  readonly libelle: string;
  /** Borne basse **INCLUSE** — l'inverse des zones d'indemnité. */
  readonly kmMin: string;
  /** Borne haute **EXCLUE** : une distance égale à cette borne est dans la zone suivante. */
  readonly kmMax: string;
  /** En euros. `null` = pas renseigné, jamais zéro. */
  readonly montant: string | null;
};

export type CoherencePaniers = {
  readonly sansMontant: readonly string[];
  readonly chevauchements: readonly { readonly premiere: string; readonly seconde: string }[];
  readonly trous: readonly { readonly apres: string; readonly avant: string }[];
  /** En dessous de cette distance, aucun panier. */
  readonly couverteDepuis: string | null;
  readonly couverteJusqua: string | null;
};

export type BaremePaniers = {
  readonly periodes: readonly PeriodePanier[];
  readonly periode: PeriodePanier | null;
  readonly periodeProposee: { readonly libelle: string; readonly debut: string; readonly fin: string };
  readonly zones: readonly ZonePanier[];
  readonly coherence: CoherencePaniers;
  readonly aujourdhui: string;
};

export function chargerBaremePaniers(periodeId?: string | null): Promise<BaremePaniers> {
  const suffixe = periodeId === undefined || periodeId === null ? "" : `?periode=${periodeId}`;
  return obtenir<BaremePaniers>(`/api/transport/paniers${suffixe}`);
}

export function creerPeriodePanier(demande: BornesPeriode): Promise<PeriodePanier> {
  return envoyer<PeriodePanier>("/api/transport/paniers/periodes", "POST", demande);
}

/** L'année suivante, avec les zones de celle-ci : le geste qui rend l'historisation tenable. */
export function dupliquerPeriodePanier(
  sourceId: string,
  demande: BornesPeriode,
): Promise<{ periode: PeriodePanier; zones: number }> {
  return envoyer(`/api/transport/paniers/periodes/${sourceId}/duplication`, "POST", demande);
}

export function modifierPeriodePanier(
  id: string,
  demande: Partial<BornesPeriode>,
): Promise<PeriodePanier> {
  return envoyer<PeriodePanier>(`/api/transport/paniers/periodes/${id}`, "PATCH", demande);
}

export function supprimerPeriodePanier(id: string): Promise<{ supprimee: boolean }> {
  return envoyer(`/api/transport/paniers/periodes/${id}`, "DELETE", {});
}

export type EcritureZonePanier = {
  readonly libelle: string;
  readonly kmMin: string;
  readonly kmMax: string;
  readonly montant: string | null;
};

export function ajouterZonePanier(
  periodeId: string,
  demande: EcritureZonePanier,
): Promise<ZonePanier> {
  return envoyer<ZonePanier>(`/api/transport/paniers/periodes/${periodeId}/zones`, "POST", demande);
}

export function modifierZonePanier(
  id: string,
  demande: Partial<EcritureZonePanier>,
): Promise<ZonePanier> {
  return envoyer<ZonePanier>(`/api/transport/paniers/zones/${id}`, "PATCH", demande);
}

export function supprimerZonePanier(id: string): Promise<void> {
  return sansCorps(`/api/transport/paniers/zones/${id}`, "DELETE");
}

export function adresseClasseurPaniers(periodeId: string): string {
  return `/api/transport/paniers/periodes/${periodeId}/classeur`;
}

export type BilanPaniers = {
  readonly zonesAvant: number;
  readonly zonesApres: number;
  readonly montantsPoses: number;
  readonly sansMontant: number;
  readonly refusees: readonly { readonly ligne: number; readonly raison: string }[];
};

export async function importerBaremePaniers(
  periodeId: string,
  fichier: File,
): Promise<BilanPaniers> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/transport/paniers/periodes/${periodeId}/classeur`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanPaniers;
}

/* ---------------------------------------------------------------------------
 * Les tables de distances (055)
 * -------------------------------------------------------------------------
 *
 * *Demande de Chris, 02/09/2026, classeur `Michelin.xlsx` à l'appui : « il faut créer un menu
 * Distance dans lequel tu dois intégrer les tables de distances, une table par site […] Je dois
 * pouvoir exporter ces tables sous Excel puis les réimporter. »*
 *
 * ## La lecture est BORNÉE par le serveur, et c'est structurant
 *
 * Chaque table porte 4 052 communes, et il y en a sept. Les rapporter toutes ferait une réponse
 * de plusieurs mégaoctets pour un écran où l'on cherche une commune à la fois. Le serveur filtre
 * et borne, et rend toujours le TOTAL à côté du nombre affiché : sans lui, une recherche qui ne
 * rend rien et une table vide se lisent pareil.
 */

/** Un site du référentiel « Carrières et sites du groupe ». */
export type SiteDeDistance = {
  readonly id: string;
  readonly code: string;
  readonly denomination: string;
  readonly nature: string;
  readonly actif: boolean;
};

export type TableDistance = {
  readonly id: string;
  readonly site: SiteDeDistance;
  readonly communes: number;
  /** Combien de ses communes n'ont pas de distance : une réponse qui manque, pas un zéro. */
  readonly sansDistance: number;
};

export type LigneDistance = {
  readonly id: string;
  readonly communeId: string;
  readonly nom: string;
  readonly codePostal: string;
  readonly codeDepartement: string | null;
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly region: string | null;
  readonly distanceKm: string | null;
  readonly prixCarburant: string | null;
};

export type ContenuTableDistance = {
  readonly table: TableDistance;
  /** Ce que le filtre retient — pas ce qui est affiché. */
  readonly total: number;
  readonly lignes: readonly LigneDistance[];
  readonly limite: number;
  readonly decalage: number;
  readonly recherche: string;
};

export type Distancier = {
  readonly tables: readonly TableDistance[];
  /** Les sites du référentiel qui n'ont pas encore de table : c'est là que se fait le choix. */
  readonly sitesSansTable: readonly SiteDeDistance[];
  readonly communes: number;
  /**
   * L'invariant, mesuré : **toutes les tables portent la même liste de communes**.
   *
   * *Consigne de Chris, 02/09/2026 : « il y a effectivement des lignes non encore renseignées sur
   * les distances, mais la liste des communes doit être identique pour tous les sites. »*
   *
   * Une distance qui manque est normale — elle est à calculer. Une **commune** qui manque dans une
   * table et pas dans une autre ne l'est pas : elle rendrait la comparaison entre deux sites
   * fausse sans que rien ne le dise, et c'est exactement ce que l'organisation du transport fera
   * de ces tables.
   */
  readonly listesAlignees: boolean;
  /** Par table, combien de communes du référentiel lui manquent. Zéro partout, normalement. */
  readonly manquantes: readonly {
    readonly table: string;
    readonly site: string;
    readonly combien: number;
  }[];
};

export function chargerDistancier(): Promise<Distancier> {
  return obtenir<Distancier>("/api/distances");
}

export function chargerTableDistance(
  tableId: string,
  options: { recherche?: string; limite?: number; decalage?: number } = {},
): Promise<ContenuTableDistance> {
  const parametres = new URLSearchParams();
  if (options.recherche !== undefined && options.recherche !== "") {
    parametres.set("recherche", options.recherche);
  }
  if (options.limite !== undefined) parametres.set("limite", String(options.limite));
  if (options.decalage !== undefined && options.decalage > 0) {
    parametres.set("decalage", String(options.decalage));
  }
  const suffixe = parametres.toString() === "" ? "" : `?${parametres.toString()}`;
  return obtenir<ContenuTableDistance>(`/api/distances/tables/${tableId}${suffixe}`);
}

/** La table d'un site du référentiel. Le site doit y exister : le serveur le vérifie. */
export function creerTableDistance(siteId: string): Promise<TableDistance> {
  return envoyer<TableDistance>("/api/distances/tables", "POST", { siteId });
}

/**
 * Supprime une table et ses distances.
 *
 * Le nombre de communes est **confirmé** dans la demande : 4 052 lignes ne s'effacent pas d'un
 * clic distrait, et le serveur refuse si le compte ne correspond plus — même parti pris que la
 * suppression d'un chantier, qui demande son n° d'affaire.
 */
export function supprimerTableDistance(
  tableId: string,
  communes: number,
): Promise<{ supprimee: boolean; communes: number }> {
  return envoyer(`/api/distances/tables/${tableId}`, "DELETE", { communes });
}

export type EcritureCommuneDistance = {
  readonly nom: string;
  readonly codePostal: string;
  readonly codeDepartement?: string | null;
  readonly latitude?: string | null;
  readonly longitude?: string | null;
  readonly region?: string | null;
  readonly distanceKm?: string | null;
  readonly prixCarburant?: string | null;
};

/**
 * Ajoute une commune — **à TOUTES les tables à la fois**.
 *
 * *Consigne de Chris : « la liste des communes doit être identique pour tous les sites ».* La
 * distance saisie ne vaut que pour la table depuis laquelle on l'ajoute ; ailleurs, elle reste à
 * calculer. Le retour dit combien de lignes le geste a créées en tout.
 */
export function ajouterCommuneALaTable(
  tableId: string,
  demande: EcritureCommuneDistance,
): Promise<{ ligne: LigneDistance; tablesTouchees: number }> {
  return envoyer(`/api/distances/tables/${tableId}/communes`, "POST", demande);
}

/** La distance et le prix : les deux seules valeurs qui appartiennent au couple site/commune. */
export function fixerDistance(
  ligneId: string,
  demande: { distanceKm?: string | null; prixCarburant?: string | null },
): Promise<LigneDistance> {
  return envoyer<LigneDistance>(`/api/distances/communes/${ligneId}`, "PATCH", demande);
}

/**
 * Retire une commune — **de TOUTES les tables, et du référentiel**.
 *
 * La retirer d'une seule ferait diverger les listes, et la comparaison entre deux sites
 * deviendrait fausse sans que rien ne le dise. Le retour dit combien de distances sont parties.
 */
export function retirerCommuneDeLaTable(
  ligneId: string,
): Promise<{ commune: string; distances: number }> {
  return envoyer(`/api/distances/communes/${ligneId}`, "DELETE", {});
}

/**
 * Réaligne toutes les tables sur le référentiel des communes. **Idempotent.**
 *
 * Chaque geste tient déjà l'invariant ; ce bouton est là pour le jour où il ne l'est plus — un
 * import partiel, une correction faite en base.
 */
export function alignerLesTablesDeDistances(): Promise<{ lignesAjoutees: number }> {
  return envoyer("/api/distances/alignement", "POST", {});
}

/**
 * Corrige la commune elle-même — **partagée par toutes les tables**.
 *
 * C'est le but : un code postal faux se corrige une fois. L'écran le dit, parce que la surprise
 * serait mauvaise.
 */
export function modifierCommuneDistance(
  communeId: string,
  demande: {
    nom?: string;
    codePostal?: string;
    codeDepartement?: string | null;
    latitude?: string | null;
    longitude?: string | null;
    region?: string | null;
  },
): Promise<{ id: string }> {
  return envoyer(`/api/distances/referentiel/communes/${communeId}`, "PATCH", demande);
}

/** Sans `tableId`, toutes les tables sortent — un onglet par site, la forme du fichier d'origine. */
export function adresseClasseurDistances(tableId?: string | null): string {
  return tableId === undefined || tableId === null
    ? "/api/distances/classeur"
    : `/api/distances/classeur?table=${tableId}`;
}

export type BilanOngletDistance = {
  readonly onglet: string;
  readonly site: string | null;
  readonly rattachePar: "code" | "nom" | null;
  readonly tableCreee: boolean;
  readonly lignesLues: number;
  readonly communesAjoutees: number;
  readonly communesCreees: number;
  readonly distancesPosees: number;
  readonly distancesEffacees: number;
  /** Des communes déjà connues dont le classeur dit autre chose, et qu'on n'a PAS réécrites. */
  readonly ecartsDeCommune: readonly {
    readonly commune: string;
    readonly champ: string;
    readonly base: string;
    readonly classeur: string;
  }[];
  readonly refusees: readonly { readonly ligne: number; readonly raison: string }[];
  readonly refus: string | null;
};

export type BilanImportDistances = { readonly onglets: readonly BilanOngletDistance[] };

export async function importerDistances(fichier: File): Promise<BilanImportDistances> {
  const corps = new FormData();
  corps.append("fichier", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch("/api/distances/classeur", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été importé.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé le classeur (${reponse.status}).`);
  }
  return (await reponse.json()) as BilanImportDistances;
}

/* ---------------------------------------------------------------------------
 * Les réglages du contrôle de transport
 * ------------------------------------------------------------------------- */

/** Un réglage de l'installation : vitesse moyenne, manœuvre, arrimage. */
export type ReglageTransport = {
  readonly cle: string;
  /** `null` quand l'installation ne l'a jamais réglé — le contrôle retombe sur son défaut. */
  readonly valeur: string | null;
  readonly modifieLe: string | null;
};

export function chargerReglagesTransport(): Promise<{ reglages: readonly ReglageTransport[] }> {
  return obtenir<{ reglages: readonly ReglageTransport[] }>("/api/transport/reglages");
}

export function reglerTransport(
  cle: string,
  valeur: number,
): Promise<{ cle: string; valeur: string }> {
  return envoyer("/api/transport/reglages", "PATCH", { cle, valeur });
}

/* ---------------------------------------------------------------------------
 * Le stock du magasin (066/067)
 * ------------------------------------------------------------------------- */

/** L'état d'un stock à un instant donné. Les nombres sont des chaînes : ce sont des décimales. */
export type EtatDuStock = {
  readonly quantite: string;
  /** Le coût moyen unitaire pondéré. Zéro quand il ne reste rien. */
  readonly cmup: string;
  readonly valeur: string;
  readonly mouvements: number;
  /** La quantité est passée sous zéro. À montrer, jamais à corriger en silence. */
  readonly negatif: boolean;
};

export type OrigineEntree = "livraison_fournisseur" | "retour_chantier";

/**
 * Ce qu'une ligne de mouvement peut PORTER, y compris ce qu'on ne saisit pas.
 *
 * `inventaire` naît de la validation d'un comptage (080) et n'apparaît dans aucun formulaire :
 * une régularisation saisie à la main serait une correction de stock qu'aucun comptage ne
 * justifie. L'écran doit pourtant savoir l'afficher, sans quoi le journal du magasin montrerait
 * des lignes dont il ne sait pas dire d'où elles viennent.
 */
export type OrigineLue = OrigineEntree | "inventaire";

/** Un magasin déclaré au référentiel des sites (078). */
export type SiteDeStockage = {
  readonly id: string;
  readonly code: string;
  readonly denomination: string;
  readonly commune: string | null;
};

export type MouvementStock = {
  /** Le coût auquel ce mouvement a compté : le sien pour une entrée, le CMUP pour une sortie.
   * DÉDUIT à chaque affichage, jamais stocké — une valeur recopiée serait fausse dès qu'un
   * mouvement antérieur est corrigé, et corriger est le geste le plus fréquent. */
  readonly coutRetenu?: string;
  /** `quantite × coutRetenu`. */
  readonly valeur?: string;
  readonly id: string;
  readonly articleId: string;
  readonly sens: "entree" | "sortie";
  readonly origine: OrigineLue | null;
  readonly quantite: string;
  readonly coutUnitaire: string;
  readonly survenuLe: string;
  readonly chantierId: string | null;
  /** `null` uniquement sur les mouvements d'essai antérieurs à la 078. L'écran le nomme. */
  readonly siteId: string | null;
  readonly remarque: string | null;
};

/** Ce qu'un magasin détient d'un article, avec son adresse de rangement (078). */
export type PartDeSite = {
  readonly siteId: string | null;
  readonly denomination: string;
  readonly quantite: string;
  readonly valeur: string;
  readonly emplacement: string | null;
};

export type ArticleEnStock = {
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly unite: string | null;
  readonly coutUnitaire: string | null;
  readonly photos: { readonly 1: string | null; readonly 2: string | null };
};

export type FicheStock = {
  readonly article: ArticleEnStock;
  readonly etat: EtatDuStock;
  /** Où se trouve ce stock, magasin par magasin. Seuls ceux qui ont bougé y figurent. */
  readonly parSite: readonly PartDeSite[];
  /** Tous les magasins déclarés : c'est la liste où l'on choisit avant de saisir. */
  readonly sites: readonly SiteDeStockage[];
  readonly mouvements: readonly MouvementStock[];
  /** Les deux origines qui se SAISISSENT. */
  readonly origines: Record<OrigineEntree, string>;
  /** Toutes celles qu'on peut LIRE, la régularisation d'inventaire comprise. */
  readonly originesLues: Record<OrigineLue, string>;
};

/**
 * L'état du stock d'un article, ses mouvements, et la fiche.
 *
 * `date` coupe la suite — l'interrogation « où en était le stock à cette date ». Le même
 * paramètre borne l'état ET la liste : sans ça l'écran montrerait un total à une date et des
 * lignes à une autre, et personne ne saurait laquelle croire.
 */
export function chargerStockArticle(articleId: string, date?: string): Promise<FicheStock> {
  const suffixe = date === undefined ? "" : `?date=${encodeURIComponent(date)}`;
  return obtenir<FicheStock>(`/api/stocks/articles/${articleId}${suffixe}`);
}

/**
 * Une ligne de l'état du stock.
 *
 * *Nommée ainsi et non « ligne d'inventaire » depuis la 080* : un inventaire est désormais un
 * document qu'on ouvre, imprime, compte et valide. Garder le mot pour désigner la valorisation
 * du stock aurait fait deux sens pour un même mot dans le même module — exactement ce qui fait
 * qu'on finit par ouvrir le mauvais écran.
 */
export type LigneEtatDuStock = {
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly unite: string | null;
  readonly etat: EtatDuStock;
  readonly parSite: readonly { readonly siteId: string | null; readonly quantite: string }[];
};

export type EtatDuStockParArticle = {
  readonly articles: readonly LigneEtatDuStock[];
  readonly date: string | null;
  readonly site: string | null;
  readonly sites: readonly SiteDeStockage[];
};

/**
 * L'état du stock, valorisé. Seuls les articles qui ont bougé y figurent.
 *
 * `site` borne l'affichage à un magasin : la quantité devient celle de ce magasin, et le coût
 * moyen reste celui de l'article — c'est la décision de Chris du 15/09/2026, et c'est ce qui rend
 * les valeurs des magasins additives.
 */
export function chargerEtatDuStock(date?: string, site?: string): Promise<EtatDuStockParArticle> {
  const parametres = new URLSearchParams();
  if (date !== undefined && date !== "") parametres.set("date", date);
  if (site !== undefined && site !== "") parametres.set("site", site);
  const suffixe = parametres.toString() === "" ? "" : `?${parametres.toString()}`;
  return obtenir<EtatDuStockParArticle>(`/api/stocks${suffixe}`);
}

/** Les magasins déclarés, pour toutes les listes déroulantes du module. */
export function chargerSitesDeStockage(): Promise<{ sites: readonly SiteDeStockage[] }> {
  return obtenir<{ sites: readonly SiteDeStockage[] }>("/api/stocks/sites");
}

/**
 * Le chantier qui porte un numéro d'affaire (079).
 *
 * Sert à confirmer à la saisie ce que le magasinier vient de taper : voir le nom du chantier
 * apparaître sous le champ est le seul contrôle possible sur un numéro recopié d'un bon de
 * livraison. Rend `null` si personne ne le porte — l'écran doit le dire, pas rester muet.
 */
export function chercherChantierParApo(
  apo: string,
): Promise<{ readonly id: string; readonly apo: string; readonly nom: string } | null> {
  return obtenir<{ chantier: { id: string; apo: string; nom: string } }>(
    `/api/stocks/chantiers/${encodeURIComponent(apo)}`,
  )
    .then((r) => r.chantier)
    .catch(() => null);
}

/**
 * Pose ou retire l'adresse de rangement d'un article dans un magasin (078).
 *
 * Un texte vide efface : la règle du produit depuis le 20/08/2026, qui évite deux façons de dire
 * « je ne sais pas où il est rangé ».
 */
export function poserEmplacement(
  articleId: string,
  siteId: string,
  emplacement: string,
): Promise<{ emplacement: string | null }> {
  return envoyer<{ emplacement: string | null }>(
    `/api/stocks/articles/${articleId}/emplacements/${siteId}`,
    "PUT",
    { emplacement },
  );
}

/** La saisie d'un mouvement de STOCK. À ne pas confondre avec `corrigerMouvement`, qui porte
 * sur un mouvement de MATÉRIEL (060) — deux objets sans rapport qui partagent un mot. */
export type SaisieMouvement = {
  readonly articleId: string;
  readonly sens: "entree" | "sortie";
  readonly origine?: OrigineEntree | null;
  readonly quantite: string;
  /** Absent, le serveur prend le coût unitaire de l'article — la règle de Chris. */
  readonly coutUnitaire?: string;
  readonly survenuLe?: string;
  /** Le magasin. Obligatoire depuis la 078 : le serveur refuse une saisie qui n'en dit rien. */
  readonly siteId: string;
  /**
   * Le numéro d'affaire du chantier d'où revient la marchandise (079).
   *
   * Obligatoire sur un retour chantier, sans objet ailleurs. C'est le serveur qui le traduit en
   * chantier : le magasinier n'a pas accès à la liste des chantiers.
   */
  readonly apo?: string | null;
  readonly remarque?: string | null;
};

export function poserMouvement(
  saisie: SaisieMouvement,
): Promise<{ mouvement: MouvementStock; etat: EtatDuStock }> {
  return envoyer<{ mouvement: MouvementStock; etat: EtatDuStock }>(
    "/api/stocks/mouvements",
    "POST",
    saisie,
  );
}

export function corrigerMouvementStock(
  id: string,
  saisie: Omit<SaisieMouvement, "articleId">,
): Promise<{ mouvement: MouvementStock; etat: EtatDuStock }> {
  return envoyer<{ mouvement: MouvementStock; etat: EtatDuStock }>(
    `/api/stocks/mouvements/${id}`,
    "PATCH",
    saisie,
  );
}

export function supprimerMouvement(id: string): Promise<{ etat: EtatDuStock }> {
  return envoyer<{ etat: EtatDuStock }>(`/api/stocks/mouvements/${id}`, "DELETE", {});
}

/* ---------------------------------------------------------------------------
 * Les demandes de création d'article (068)
 * ------------------------------------------------------------------------- */

export type StatutDemandeArticle = "en_attente" | "validee" | "refusee";

export type OrigineDemandeArticle = "livraison_fournisseur" | "retour_chantier";

export const LIBELLES_ORIGINE_DEMANDE: Record<OrigineDemandeArticle, string> = {
  livraison_fournisseur: "Livraison fournisseur",
  retour_chantier: "Retour chantier",
};

export type DemandeArticle = {
  readonly id: string;
  readonly description: string;
  readonly uniteBase: string | null;
  /** La quantité reçue, que la validation entre en stock (069). Le prix n'est pas ici. */
  readonly quantite: string | null;
  readonly origine: OrigineDemandeArticle | null;
  /** Le magasin où la quantité a été rangée, et où la validation la fera entrer (078). */
  readonly siteId: string | null;
  readonly siteDenomination: string | null;
  readonly fournisseurNom: string | null;
  readonly remarque: string | null;
  readonly statut: StatutDemandeArticle;
  /** L'article né de la validation, avec le prix saisi par celle qui valide. */
  readonly article: {
    readonly id: string;
    readonly code: string;
    readonly description: string;
    readonly coutUnitaire: string | null;
  } | null;
  /** L'entrée posée par la validation — ce que le magasinier n'a plus à ressaisir. */
  readonly entreeEnStock: { readonly quantite: string; readonly valeur: string } | null;
  /**
   * L'adresse de la photo prise au téléphone, ou `null` (071).
   *
   * Elle porte une version tirée du nom de fichier : remplacer la photo change l'adresse, donc
   * le navigateur ne peut pas servir l'ancienne depuis son cache.
   */
  readonly photo: string | null;
  readonly motifRefus: string | null;
  readonly demandePar: string | null;
  readonly demandeurNom: string | null;
  readonly decideurNom: string | null;
  readonly decideLe: string | null;
  readonly creeLe: string;
};

export type TableauDemandesArticles = {
  readonly demandes: readonly DemandeArticle[];
  /** Qui tient la fonction, ou `null` — l'écran doit pouvoir dire qu'elle est vacante. */
  readonly titulaire: { readonly utilisateurId: string; readonly nom: string } | null;
  /** Le serveur tranche, l'écran n'en décide pas : les boutons de décision suivent ce drapeau. */
  readonly jePeuxDecider: boolean;
};

/** Les demandes. Un magasinier ne reçoit que les siennes — le serveur borne la liste. */
export function chargerDemandesArticles(
  statut?: StatutDemandeArticle,
): Promise<TableauDemandesArticles> {
  const suffixe = statut === undefined ? "" : `?statut=${statut}`;
  return obtenir<TableauDemandesArticles>(`/api/demandes-articles${suffixe}`);
}

export type SaisieDemandeArticle = {
  readonly description: string;
  readonly uniteBase?: string | null;
  /** La quantité reçue. Facultative — on peut demander un article qu'on n'a pas encore. */
  readonly quantite?: string | null;
  /** Obligatoire dès qu'il y a une quantité : un mouvement ne naît pas sans origine. */
  readonly origine?: OrigineDemandeArticle | null;
  /**
   * Le magasin où la marchandise a été rangée (078).
   *
   * Obligatoire si et seulement si la demande porte une quantité : c'est la validation qui posera
   * l'entrée en stock, et un mouvement doit dire dans quel magasin il entre.
   */
  readonly siteId?: string | null;
  readonly fournisseurNom?: string | null;
  readonly remarque?: string | null;
};

export type DemandeArticlePosee = {
  readonly demande: DemandeArticle;
  /** Faux quand personne ne tient la fonction : la demande existe, mais personne n'a été prévenu. */
  readonly destinatairePrevenu: boolean;
  readonly destinataireNom: string | null;
};

export function demanderUnArticle(saisie: SaisieDemandeArticle): Promise<DemandeArticlePosee> {
  return envoyer<DemandeArticlePosee>("/api/demandes-articles", "POST", saisie);
}

/**
 * La validation CRÉE l'article, et pose l'entrée en stock si la demande porte une quantité.
 *
 * Deux champs sont exigés par le serveur : le **code** — le pivot du référentiel, que le
 * demandeur n'a pas — et le **prix**, dès qu'il y a une quantité, parce que c'est lui qui
 * valorise l'entrée. Sans lui, le stock afficherait la bonne quantité et une valeur nulle.
 */
export function validerDemandeArticle(
  id: string,
  decision: {
    readonly code: string;
    readonly description?: string;
    readonly uniteBase?: string | null;
    /** Le prix d achat : il valorise l entree en stock, et il est exige des qu il y a une quantite. */
    readonly coutUnitaire?: string | null;
    /** Le cout d ETUDE : celui qu une ligne de contre-etude prendra (regle du 21/08/2026). */
    readonly coutEtude?: string | null;
    readonly prixUnitaire?: string | null;
    readonly codeFamille?: string | null;
    readonly procedureAppro?: string | null;
    readonly fournisseurNumero?: string | null;
    readonly fournisseurNom?: string | null;
  },
): Promise<{ demande: DemandeArticle }> {
  return envoyer<{ demande: DemandeArticle }>(
    `/api/demandes-articles/${id}/validation`,
    "POST",
    decision,
  );
}

/** Le motif est obligatoire : un refus muet fait refaire la même demande. */
export function refuserDemandeArticle(
  id: string,
  motif: string,
): Promise<{ demande: DemandeArticle }> {
  return envoyer<{ demande: DemandeArticle }>(`/api/demandes-articles/${id}/refus`, "POST", {
    motif,
  });
}

/* ---------------------------------------------------------------------------
 * La réservation du stock, et la préparation par le magasin (070)
 * ------------------------------------------------------------------------- */

export type StatutReservation = "demandee" | "preparee";

export type Reservation = {
  readonly id: string;
  readonly chantierId: string;
  readonly apo: string;
  readonly nomChantier: string;
  readonly articleId: string;
  readonly articleCode: string;
  readonly articleDescription: string;
  readonly unite: string | null;
  readonly quantite: string;
  /** Pour quand le magasin doit avoir préparé. Date civile, ou `null`. */
  readonly pourLe: string | null;
  readonly remarque: string | null;
  readonly statut: StatutReservation;
  readonly demandeurNom: string | null;
  readonly demandeLe: string;
  readonly preparateurNom: string | null;
  readonly prepareLe: string | null;
  /**
   * La sortie de stock née de la préparation.
   *
   * `null` sur une réservation PRÉPARÉE veut dire que le magasinier a supprimé ce mouvement
   * depuis son écran : à nommer, jamais à taire.
   */
  readonly mouvementId: string | null;
  /** Le magasin où préparer (078). `null` sur les deux réservations d'essai antérieures. */
  readonly siteId: string | null;
  readonly siteDenomination: string | null;
};

/** Ce que le panneau d'une ligne de contre-étude affiche. */
export type StockPourLaLigne = {
  readonly article: {
    readonly id: string;
    readonly code: string;
    readonly description: string;
    readonly unite: string | null;
  };
  readonly etat: EtatDuStock;
  /** `stock − réservé non préparé`, tous chantiers confondus. Peut être négatif. */
  readonly disponible: string;
  readonly reserveAilleurs: string;
  /** Les réservations de CE chantier pour cet article, préparées comprises. */
  readonly reservations: readonly Reservation[];
  /**
   * Le même stock, magasin par magasin (078).
   *
   * *Demande de Chris du 15/09/2026 : voir le stock d'un magasin ou de tous.* Les deux arrivent
   * ensemble : changer de magasin à l'écran ne doit pas recharger des nombres déjà reçus.
   *
   * **Le disponible est calculé par magasin**, pas seulement en tout — promettre sur le
   * disponible d'ensemble ferait réserver ici ce qui est en stock là-bas.
   */
  readonly parSite: readonly {
    readonly siteId: string | null;
    readonly denomination: string;
    readonly quantite: string;
    readonly valeur: string;
    readonly reserveAilleurs: string;
    readonly disponible: string;
    readonly emplacement: string | null;
  }[];
  /** Les magasins déclarés, y compris ceux où l'article n'a jamais rien eu : on peut y réserver. */
  readonly sites: readonly SiteDeStockage[];
};

/**
 * Le stock d'un article et les réservations de ce chantier.
 *
 * Lu au moment où le panneau s'ouvre, et non porté par la contre-étude : le magasin bouge
 * pendant qu'on travaille, et un stock recopié dans la lecture d'une version serait déjà faux.
 */
export function chargerStockPourLaLigne(
  chantierId: string,
  code: string,
): Promise<StockPourLaLigne> {
  return obtenir<StockPourLaLigne>(
    `/api/chantiers/${chantierId}/stock-article/${encodeURIComponent(code)}`,
  );
}

export type ReservationPosee = {
  readonly reservation: Reservation;
  /** Combien de comptes de magasin ont été prévenus. Zéro se dit, il ne se tait pas. */
  readonly magasinsPrevenus: number;
  /** Le disponible AVANT : ce qui permet à l'écran de signaler un dépassement. */
  readonly disponibleAvant: string;
};

export function reserverDuStock(
  chantierId: string,
  saisie: {
    readonly articleCode: string;
    readonly quantite: string;
    readonly pourLe?: string | null;
    readonly remarque?: string | null;
    /**
     * Le magasin où préparer (078). Obligatoire, même quand on CONSULTE tous les magasins :
     * réserver dans « tous » n'a aucun sens — le magasinier doit savoir dans quel rayonnage aller.
     */
    readonly siteId: string;
  },
): Promise<ReservationPosee> {
  return envoyer<ReservationPosee>(`/api/chantiers/${chantierId}/reservations`, "POST", saisie);
}

/** Retirer une réservation pas encore préparée. Une préparée est refusée : le stock est sorti. */
export function retirerReservation(
  chantierId: string,
  reservationId: string,
): Promise<{ retiree: true }> {
  return envoyer<{ retiree: true }>(
    `/api/chantiers/${chantierId}/reservations/${reservationId}`,
    "DELETE",
    {},
  );
}

/** Ce que le magasin doit préparer, tous chantiers confondus. */
export function chargerLesPreparations(
  statut?: StatutReservation,
): Promise<{ preparations: readonly Reservation[] }> {
  const suffixe = statut === undefined ? "" : `?statut=${statut}`;
  return obtenir<{ preparations: readonly Reservation[] }>(`/api/stocks/preparations${suffixe}`);
}

/** Le magasin a préparé : la marchandise SORT du stock. */
export function marquerPreparationFaite(
  reservationId: string,
): Promise<{ reservation: Reservation; etat: EtatDuStock }> {
  return envoyer<{ reservation: Reservation; etat: EtatDuStock }>(
    `/api/stocks/preparations/${reservationId}/faite`,
    "POST",
    {},
  );
}

/* ---------------------------------------------------------------------------
 * La photo d'une demande de création d'article (071)
 * ------------------------------------------------------------------------- */

/**
 * Dépose ou remplace la photo d'une demande.
 *
 * En multipart, comme celle d'une fiche article. La demande doit exister : le magasinier choisit
 * son fichier dans le formulaire, et l'écran l'envoie juste après avoir créé la demande — c'est
 * le seul ordre possible, l'identifiant n'existant pas avant.
 */
export async function deposerPhotoDemande(
  id: string,
  fichier: File,
): Promise<{ demande: DemandeArticle }> {
  const corps = new FormData();
  corps.append("photo", fichier, fichier.name);

  let reponse: Response;
  try {
    reponse = await fetch(`/api/demandes-articles/${id}/photo`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: corps,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : la photo n'a pas été déposée.");
  }
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as { erreur?: string } | null;
    throw new Error(erreur?.erreur ?? `Le serveur a refusé la photo (${reponse.status}).`);
  }
  return (await reponse.json()) as { demande: DemandeArticle };
}

/* ---------------------------------------------------------------------------
 * Le matériel attaché à une personne (077)
 * ------------------------------------------------------------------------- */

/**
 * Une association salarié → matériel, résolue sur les deux référentiels.
 *
 * `etat` dit d'où viennent les libellés, et l'écran doit le montrer : une association qui pointe
 * vers une fiche disparue et une association lue sur une copie parce que le référentiel se tait
 * ne demandent pas la même chose — la première se corrige, la seconde s'attend.
 */
export type Association = {
  readonly id: string;
  readonly salarieId: string;
  readonly salarieLibelle: string;
  readonly salariePoste: string | null;
  readonly referentiel: "materiel";
  readonly ressourceId: string;
  readonly ressourceLibelle: string;
  readonly ressourceDetail: string | null;
  readonly codeGenerique: string | null;
  readonly etat: "resolue" | "hors_referentiel" | "referentiel_muet";
  readonly creeLe: string;
};

export function chargerAssociations(): Promise<{ associations: readonly Association[] }> {
  return obtenir<{ associations: readonly Association[] }>("/api/associations");
}

export function associerMateriel(
  salarieId: string,
  ressourceId: string,
): Promise<{ id: string | null }> {
  return envoyer<{ id: string | null }>("/api/associations", "POST", {
    salarieId,
    ressourceId,
    referentiel: "materiel",
  });
}

export function retirerAssociation(id: string): Promise<{ retire: boolean }> {
  return envoyer<{ retire: boolean }>(`/api/associations/${id}`, "DELETE", {});
}

/* ---------------------------------------------------------------------------
 * L'inventaire d'un magasin (080)
 * ------------------------------------------------------------------------- */

export type StatutInventaire = "ouvert" | "valide";

export type LigneComptage = {
  readonly id: string;
  readonly articleId: string;
  readonly code: string;
  readonly description: string;
  readonly unite: string | null;
  readonly emplacement: string | null;
  /** Figée à l'ouverture, et jamais relue : c'est ce qui rend l'écart reproductible. */
  readonly quantiteTheorique: string;
  /** `null` = pas encore compté. Zéro = compté vide. Les deux ne se confondent jamais. */
  readonly quantiteComptee: string | null;
  /** `compté − théorique`, ou `null` tant que rien n'est compté. */
  readonly ecart: string | null;
  readonly remarque: string | null;
  /** La régularisation posée par la validation, quand l'écart n'était pas nul. */
  readonly mouvementId: string | null;
};

export type Inventaire = {
  readonly id: string;
  readonly siteId: string;
  readonly siteDenomination: string;
  readonly compteLe: string;
  readonly statut: StatutInventaire;
  readonly note: string | null;
  readonly ouvertParNom: string | null;
  readonly ouvertLe: string;
  readonly valideParNom: string | null;
  readonly valideLe: string | null;
  readonly lignes: readonly LigneComptage[];
  /** Ce que l'écran dit avant de laisser valider. Déduit à chaque lecture, jamais stocké. */
  readonly compte: {
    readonly lignes: number;
    readonly comptees: number;
    readonly restantes: number;
    readonly ecarts: number;
  };
};

/** L'en-tête seul, tel que la liste le rend. */
export type InventaireEnTete = Omit<Inventaire, "lignes" | "compte">;

export function chargerInventaires(): Promise<{
  inventaires: readonly InventaireEnTete[];
  sites: readonly SiteDeStockage[];
}> {
  return obtenir<{ inventaires: readonly InventaireEnTete[]; sites: readonly SiteDeStockage[] }>(
    "/api/stocks/inventaires",
  );
}

export function chargerInventaire(id: string): Promise<{ inventaire: Inventaire }> {
  return obtenir<{ inventaire: Inventaire }>(`/api/stocks/inventaires/${id}`);
}

/**
 * Ouvre un inventaire : fige la liste des articles du magasin et leur quantité théorique.
 *
 * Un seul inventaire ouvert par magasin — deux figeraient deux théoriques et régulariseraient
 * deux fois les mêmes articles.
 */
export function ouvrirUnInventaire(saisie: {
  readonly siteId: string;
  readonly compteLe?: string;
  readonly note?: string | null;
}): Promise<{ inventaire: Inventaire }> {
  return envoyer<{ inventaire: Inventaire }>("/api/stocks/inventaires", "POST", saisie);
}

/**
 * Saisit le comptage d'une ligne.
 *
 * Une quantité vide EFFACE le comptage et rend la ligne à « pas encore compté » : c'est le seul
 * moyen de revenir en arrière après une frappe de travers sans laisser un zéro qui régulariserait
 * tout le stock de cet article.
 */
export function compterUneLigne(
  inventaireId: string,
  ligneId: string,
  saisie: { readonly quantiteComptee?: string | null; readonly remarque?: string | null },
): Promise<{ inventaire: Inventaire }> {
  return envoyer<{ inventaire: Inventaire }>(
    `/api/stocks/inventaires/${inventaireId}/lignes/${ligneId}`,
    "PATCH",
    saisie,
  );
}

/** Ajoute un article trouvé dans le rayonnage et absent de la feuille. */
export function ajouterUneLigneDInventaire(
  inventaireId: string,
  articleId: string,
): Promise<{ inventaire: Inventaire }> {
  return envoyer<{ inventaire: Inventaire }>(
    `/api/stocks/inventaires/${inventaireId}/lignes`,
    "POST",
    { articleId },
  );
}

export type InventaireValide = {
  readonly inventaire: Inventaire;
  readonly regularisations: number;
  /** Les lignes non comptées, qui n'ont rien régularisé. Dit, jamais tu. */
  readonly nonComptees: number;
};

/** Valide l'inventaire : chaque écart pose son mouvement de régularisation. */
export function validerUnInventaire(inventaireId: string): Promise<InventaireValide> {
  return envoyer<InventaireValide>(
    `/api/stocks/inventaires/${inventaireId}/validation`,
    "POST",
    {},
  );
}

/** Abandonne un inventaire ouvert. Un validé ne s'efface pas : ses écarts sont sortis en stock. */
export function abandonnerUnInventaire(inventaireId: string): Promise<{ supprime: true }> {
  return envoyer<{ supprime: true }>(`/api/stocks/inventaires/${inventaireId}`, "DELETE", {});
}

/**
 * L'adresse de la feuille à imprimer.
 *
 * Une adresse et non un appel : la feuille s'ouvre dans un onglet, et c'est le navigateur qui
 * l'imprime — comme les planches d'étiquettes, et pour la même raison (aucun service de rendu
 * PDF côté serveur, Docker ayant été écarté du produit le 07/08/2026).
 */
export function adresseFeuilleDInventaire(inventaireId: string): string {
  return `/api/stocks/inventaires/${inventaireId}/feuille`;
}

/* ---------------------------------------------------------------------------
 * La saisie des heures réalisées (16/09/2026)
 * ------------------------------------------------------------------------- */

export type EtatDuMoment = "present" | "intemperie" | "absence" | "maladie" | "formation";

export type MomentDeSaisie = {
  readonly position: "matin" | "apres_midi";
  readonly propose: {
    readonly ouvrageId: string;
    readonly ligneId: string | null;
    readonly numero: string;
    readonly heuresChiffrees: string;
    readonly aussiPrevuSur: readonly string[];
  } | null;
  readonly saisi: {
    readonly id: string;
    readonly ouvrageId: string | null;
    readonly heures: string;
    readonly etat: EtatDuMoment;
    readonly remarque: string | null;
  } | null;
};

/** Ce qu'une personne retient comme zone de transport : « comme le chantier », une zone, ou rien. */
export type ZoneDeLaPersonne = {
  /** La zone choisie à la main. **Nulle = comme le chantier**, et la déduction reste vivante. */
  readonly zoneId: string | null;
  /** Vrai = aucune zone pour cette personne, décidé à la main. Exclusif de `zoneId` (086). */
  readonly sansObjet: boolean;
};

export type RessourceDeSaisie = {
  readonly nature: "salarie" | "materiel";
  readonly ressourceId: string;
  readonly libelle: string;
  readonly detail: string | null;
  readonly moments: readonly MomentDeSaisie[];
  readonly panier: boolean | null;
  /** La zone de transport de cette personne ce jour-là (086). Nulle pour une machine. */
  readonly zoneIndemnite: ZoneDeLaPersonne | null;
  /**
   * Vrai si le plan ne place PAS cette ressource sur ce chantier ce jour-là : une ressource
   * **ajoutée** par le responsable. C'est la seule que l'écran accepte de retirer — faire
   * disparaître une ligne du plan demanderait de corriger le plan.
   */
  readonly horsPlan: boolean;
};

export type LivraisonDeSaisie = {
  readonly livraisonId: string;
  readonly ouvrageId: string;
  readonly numero: string;
  readonly libelle: string;
  readonly unite: string;
  readonly quantitePrevue: string;
  readonly provenance: string;
  readonly quantiteRecue: string | null;
  readonly remarque: string | null;
  /**
   * Ce que les chauffeurs ont pointé — le total du convoi.
   *
   * **Il PRÉ-REMPLIT la case du chef, il ne la remplace pas** (082) : confirmer suppose deux
   * affirmations distinctes, et une valeur unique rendrait l'écart invisible. Nul quand personne
   * n'a pointé — il n'y a alors rien à confirmer.
   */
  readonly quantiteChauffeurs: string | null;
  readonly pointages: readonly {
    readonly libelle: string;
    readonly voyages: number | null;
    readonly quantite: string;
    readonly sourceQuantite: "bons" | "declaree";
    readonly bons: readonly BonDeLivraison[];
  }[];
};

/* ---------------------------------------------------------------------------
 * Le circuit de validation des heures (084, Chris 17/09/2026)
 * ------------------------------------------------------------------------- */

export type StatutJournee =
  | "ouverte"
  | "close"
  | "validee_conducteur"
  | "validee_assistante"
  | "validee_rh";

export type EtapeHeures = "conducteur" | "assistante" | "rh";

/** Une étape du circuit, telle que l'écran doit la montrer : prévue ou non, franchie ou non. */
export type EtatEtapeHeures = {
  readonly etape: EtapeHeures;
  readonly libelle: string;
  /** Faux = cette installation n'a pas ce maillon, la journée le saute. */
  readonly prevue: boolean;
  readonly nomme: string | null;
  readonly utilisateurId: string | null;
  /** Ce qui empêche l'étape de s'ouvrir, en clair. */
  readonly empechement: string | null;
  readonly franchie: {
    readonly le: string;
    readonly parId: string | null;
    readonly par: string | null;
    readonly commentaire: string | null;
  } | null;
};

export type CircuitDeLaJournee = {
  readonly statut: StatutJournee;
  readonly etapes: readonly EtatEtapeHeures[];
  readonly attendue: EtatEtapeHeures | null;
  readonly terminee: boolean;
};

export type ChantierDeSaisie = {
  readonly chantierId: string;
  readonly apo: string;
  readonly nom: string;
  readonly jourRelatif: number;
  readonly statut: "aucune" | StatutJournee;
  readonly circuit: CircuitDeLaJournee | null;
  /** Faux = l'écran se lit, il ne s'écrit plus — le circuit a dépassé ce compte (084), ou la journée revient à un autre responsable (087). */
  readonly modifiable: boolean;
  /**
   * Le responsable désigné pour saisir cette journée (087), ou nul.
   *
   * Nul = aucune hiérarchie ne s'applique : soit l'installation n'en a pas déclaré, soit personne
   * de rangé n'est sur le chantier ce jour-là. L'écran reste alors ouvert à tout compte affecté.
   */
  readonly saisiePar: {
    readonly salarieId: string;
    readonly libelle: string;
    readonly codeMo: string;
    readonly libelleCode: string;
    readonly rang: number;
    /** Faux = il n'a pas de compte, et la saisie est descendue d'un rang. L'écran le dit. */
    readonly peutSeConnecter: boolean;
  } | null;
  /** L'étape que ce compte peut franchir maintenant, ou nul. */
  readonly aValider: EtapeHeures | null;
  readonly journeeId: string | null;
  readonly noeuds: readonly { ouvrageId: string; numero: string; libelle: string }[];
  readonly hommes: readonly RessourceDeSaisie[];
  readonly materiels: readonly RessourceDeSaisie[];
  readonly livraisons: readonly LivraisonDeSaisie[];
};

export type JourneeDeSaisie = {
  readonly jour: string;
  readonly pour: {
    /**
     * La fiche dont on regarde la journée. **Nulle pour un valideur sans fiche** : il n'est pas
     * sur le chantier, il vient contrôler une journée qu'on lui a passée (084).
     */
    readonly salarieId: string | null;
    readonly nom: string | null;
    readonly prenom: string | null;
    readonly poste: string | null;
  };
  readonly estAdmin: boolean;
  readonly chantiers: readonly ChantierDeSaisie[];
  readonly zones: Record<string, ZonesChantier | null>;
  /**
   * Le barème des indemnités de transport, pour la colonne « Zone transport » (086).
   *
   * L'écran y prend les tranches de LA PÉRIODE que le chantier retient, jamais toutes : proposer
   * les zones de 2026 sur un chantier de 2027 ferait choisir un montant qui n'a plus cours. Celui
   * des paniers ne traverse pas — le panier se coche, il ne se choisit pas.
   */
  readonly baremeIndemnites: readonly BaremePeriode[];
  /**
   * Ce qu'on peut ajouter à la journée : le référentiel maître tel qu'il est aujourd'hui.
   *
   * **`referentielLu` porte la différence entre « il ne répond pas » et « il n'y a personne ».**
   * Sans elle, une liste déroulante vide se lit comme une panne de l'écran, et on cherche au
   * mauvais bout.
   */
  readonly ajoutables: {
    readonly salaries: readonly RessourceAjoutable[];
    readonly materiels: readonly RessourceAjoutable[];
    readonly referentielLu: boolean;
  };
  readonly personnesPossibles: readonly { salarieId: string; libelle: string }[];
};

/** Une ressource du référentiel qu'on peut ajouter à une journée de saisie. */
export type RessourceAjoutable = {
  readonly ressourceId: string;
  readonly libelle: string;
  readonly detail: string | null;
};

/**
 * La journée de saisie : ses chantiers, ses nœuds, ses équipes et ses livraisons.
 *
 * `pour` n'est accepté que d'un administrateur — c'est le sélecteur « je saisis pour… ». Le
 * serveur refuse explicitement plutôt que d'ignorer : un paramètre ignoré aurait ouvert la
 * journée du demandeur en lui faisant croire qu'il regarde celle d'un autre.
 */
export function chargerJourneeDeSaisie(options?: {
  jour?: string;
  pour?: string;
  /**
   * Ouvrir la journée d'un chantier où l'on n'est PAS affecté ce jour-là.
   *
   * C'est le chemin du valideur (084) : le conducteur, l'assistante référente et la RH ne sont
   * pas sur le chantier le mercredi. Le serveur n'ouvre que si le circuit les y autorise, et il
   * rend alors la journée entière — tous les nœuds, pas les leurs.
   */
  chantier?: string;
}): Promise<JourneeDeSaisie> {
  const parametres = new URLSearchParams();
  if (options?.jour !== undefined && options.jour !== "") parametres.set("jour", options.jour);
  if (options?.pour !== undefined && options.pour !== "") parametres.set("pour", options.pour);
  if (options?.chantier !== undefined && options.chantier !== "") {
    parametres.set("chantier", options.chantier);
  }
  const requete = parametres.toString();
  return obtenir<JourneeDeSaisie>(`/api/saisie${requete === "" ? "" : `?${requete}`}`);
}

/** Une journée qui attend MA validation, telle que la pile du valideur la montre. */
export type JourneeAValider = {
  readonly journeeId: string;
  readonly chantierId: string;
  readonly apo: string;
  readonly nom: string;
  readonly jour: string;
  readonly statut: StatutJournee;
  readonly etape: EtapeHeures;
  readonly libelleEtape: string;
  readonly closeLe: string | null;
  readonly heures: string;
  readonly personnes: number;
};

export function chargerCeQuiAttendMaValidation(): Promise<{
  readonly aValider: readonly JourneeAValider[];
}> {
  return obtenir<{ aValider: readonly JourneeAValider[] }>("/api/saisie/a-valider");
}

export function validerLaJournee(envoi: {
  chantierId: string;
  jour: string;
  commentaire?: string | null;
}): Promise<{ statut: StatutJournee; etape: EtapeHeures }> {
  return envoyer<{ statut: StatutJournee; etape: EtapeHeures }>(
    "/api/saisie/validation",
    "POST",
    envoi,
  );
}

export function defaireLaValidation(envoi: {
  chantierId: string;
  jour: string;
}): Promise<{ statut: StatutJournee; etape: EtapeHeures }> {
  return envoyer<{ statut: StatutJournee; etape: EtapeHeures }>(
    "/api/saisie/validation",
    "DELETE",
    envoi,
  );
}

export type EnvoiDeSaisie = {
  readonly chantierId: string;
  readonly jour: string;
  readonly jourRelatif: number;
  readonly moments: readonly {
    nature: "salarie" | "materiel";
    ressourceId: string;
    libelle: string;
    detail: string | null;
    position: "matin" | "apres_midi";
    ouvrageId: string | null;
    ligneId: string | null;
    heures: string;
    etat: EtatDuMoment;
    remarque: string | null;
  }[];
  /**
   * Le panier ET la zone de transport, par personne présente — une ligne de `saisie_personne`.
   *
   * `zoneIndemniteId` nul avec `zoneIndemniteSansObjet` faux veut dire « comme le chantier », et
   * c'est l'état normal : le serveur efface alors toute correction, donc la zone se déduit à
   * nouveau de la distance à chaque affichage. Les deux autres états sont des corrections (086).
   */
  readonly paniers: readonly {
    salarieId: string;
    panier: boolean;
    zoneIndemniteId?: string | null;
    zoneIndemniteSansObjet?: boolean;
  }[];
  /**
   * Les ressources à effacer de la journée — le pendant de l'ajout (18/09/2026).
   *
   * Un geste explicite, parce que l'effacement de cette route ne porte que sur ce que l'envoi
   * nomme : une ressource simplement disparue de l'écran garderait ses heures en base.
   */
  readonly retirees?: readonly { nature: "salarie" | "materiel"; ressourceId: string }[];
  readonly livraisons: readonly {
    livraisonId: string;
    ouvrageId: string;
    libelle: string;
    unite: string;
    quantitePrevue: string;
    quantiteRecue: string | null;
    remarque: string | null;
  }[];
  readonly clore?: boolean;
  /**
   * Ramener une journée close à « en cours de saisie ». **Geste explicite depuis la 084.**
   *
   * Jusque-là, `clore: false` suffisait — donc un simple enregistrement rouvrait la journée. Le
   * valideur qui corrige avant de valider envoie exactement cela, et sa journée redescendait au
   * chantier au lieu de monter.
   */
  readonly rouvrir?: boolean;
};

/** Enregistrer une journée entière. Rejouable : deux envois identiques ne dupliquent rien. */
export function enregistrerLaSaisie(envoi: EnvoiDeSaisie): Promise<{ journeeId: string }> {
  return envoyer<{ journeeId: string }>("/api/saisie/journee", "PUT", envoi);
}

/* ---------------------------------------------------------------------------
 * L'application du chauffeur (16/09/2026)
 * ------------------------------------------------------------------------- */

/** Un bon de carrière photographié : la preuve, et la pesée qu'il annonce. */
export type BonDeLivraison = {
  readonly id: string;
  readonly quantite: string | null;
  readonly codeErp: string | null;
  readonly libelle: string | null;
};

export type RotationDuChauffeur = {
  readonly apo: string;
  readonly ouvrage: string;
  readonly libelle: string;
  readonly heures: string | null;
  readonly heureLimite: string | null;
  readonly trajet: string | null;
  readonly km: string | null;
};

export type LivraisonAPointer = {
  readonly livraisonId: string;
  readonly chantierId: string;
  readonly apo: string;
  readonly nomChantier: string;
  readonly ouvrage: string;
  readonly codeErp: string;
  readonly libelle: string;
  readonly unite: string;
  readonly quantitePrevue: string;
  readonly provenance: string;
  readonly heureLimite: string | null;
  readonly villeLivraison: string | null;
  /** La charge utile d'un voyage. Nulle = la ligne ne se compte pas en voyages. */
  readonly chargeUtile: string | null;
  readonly monPointage: {
    /** L'identifiant du pointage : c'est sur lui que se dépose la photo d'un bon. */
    readonly id: string;
    readonly voyages: number | null;
    readonly quantite: string;
    readonly remarque: string | null;
    readonly bons: readonly BonDeLivraison[];
    /** Ce que la journée retient : la somme des bons pesés, sinon la quantité déclarée. */
    readonly quantiteRetenue: string;
    readonly sourceQuantite: "bons" | "declaree";
  } | null;
  /** Ce que TOUS les chauffeurs ont pointé : un convoi se partage une pastille. */
  readonly totalPointe: string;
  readonly autresChauffeurs: number;
};

export type JourneeDuChauffeur = {
  readonly jour: string;
  readonly pour: { readonly salarieId: string; readonly nom: string | null; readonly prenom: string | null };
  readonly estAdmin: boolean;
  readonly rotations: readonly RotationDuChauffeur[];
  readonly livraisons: readonly LivraisonAPointer[];
  readonly chauffeursPossibles: readonly { salarieId: string; libelle: string }[];
};

/** La journée du chauffeur : ses rotations, et les livraisons qu'il peut pointer. */
export function chargerJourneeChauffeur(options?: {
  jour?: string;
  pour?: string;
}): Promise<JourneeDuChauffeur> {
  const parametres = new URLSearchParams();
  if (options?.jour !== undefined && options.jour !== "") parametres.set("jour", options.jour);
  if (options?.pour !== undefined && options.pour !== "") parametres.set("pour", options.pour);
  const requete = parametres.toString();
  return obtenir<JourneeDuChauffeur>(`/api/chauffeur${requete === "" ? "" : `?${requete}`}`);
}

/**
 * Pointer une livraison — une pastille à la fois.
 *
 * `voyages` l'emporte sur `quantite` quand une charge utile est connue : c'est le serveur qui
 * convertit, pour qu'une seule arithmétique existe.
 */
export function pointerUneLivraison(envoi: {
  livraisonId: string;
  jour: string;
  pour?: string;
  libelle: string;
  voyages?: number;
  quantite?: string;
  chargeUtile?: string;
  remarque?: string;
}): Promise<{ pointageId: string | null; quantite: string }> {
  return envoyer<{ pointageId: string | null; quantite: string }>(
    "/api/chauffeur/pointage",
    "PUT",
    envoi,
  );
}

/**
 * Annuler son pointage.
 *
 * Supprimé et non mis à zéro : **zéro est une déclaration** (« rien n'est arrivé »), l'absence de
 * pointage en est une autre (« je n'ai rien déclaré »).
 */
export function annulerLePointage(demande: {
  livraisonId: string;
  jour: string;
  pour?: string;
}): Promise<{ supprime: boolean }> {
  const parametres = new URLSearchParams({
    livraison: demande.livraisonId,
    jour: demande.jour,
  });
  if (demande.pour !== undefined) parametres.set("pour", demande.pour);
  return envoyer<{ supprime: boolean }>(`/api/chauffeur/pointage?${parametres.toString()}`, "DELETE", {});
}

/**
 * Déposer la photo d'un bon sur un pointage, avec sa pesée.
 *
 * **Tout part dans le même envoi** : la photo, le tonnage lu sur le bon, le code ERP et le
 * libellé du matériau. Un second appel pour la quantité aurait laissé, entre les deux, un bon
 * sans pesée — et sur un téléphone au bord d'une piste, « entre les deux » veut dire « pour
 * toujours » dès que le réseau lâche.
 */
export async function deposerUnBon(
  pointageId: string,
  fichier: File,
  champs: { quantite?: string; codeErp?: string; libelle?: string },
): Promise<{ bonId: string | null; octets: number }> {
  const corps = new FormData();
  corps.append("fichier", fichier);
  if (champs.quantite !== undefined && champs.quantite !== "") {
    corps.append("quantite", champs.quantite);
  }
  if (champs.codeErp !== undefined) corps.append("codeErp", champs.codeErp);
  if (champs.libelle !== undefined) corps.append("libelle", champs.libelle);

  const reponse = await fetch(`/api/chauffeur/pointage/${pointageId}/bon`, {
    method: "POST",
    body: corps,
    credentials: "same-origin",
  });
  if (!reponse.ok) {
    const corpsErreur: unknown = await reponse.json().catch(() => null);
    const message =
      typeof corpsErreur === "object" &&
      corpsErreur !== null &&
      "erreur" in corpsErreur &&
      typeof (corpsErreur as { erreur: unknown }).erreur === "string"
        ? (corpsErreur as { erreur: string }).erreur
        : `Le bon n'a pas pu être envoyé (${String(reponse.status)}).`;
    throw new Error(message);
  }
  return (await reponse.json()) as { bonId: string | null; octets: number };
}

/** L'adresse de la photo d'un bon. Servie avec un cache d'un an : son nom est unique. */
export function adresseDuBon(bonId: string): string {
  return `/api/bons/${bonId}`;
}

/** Retirer un bon — une photo ratée, un bon photographié deux fois. */
export function supprimerUnBon(bonId: string): Promise<{ supprime: boolean }> {
  return envoyer<{ supprime: boolean }>(`/api/bons/${bonId}`, "DELETE", {});
}

/* ---------------------------------------------------------------------------
 * Les relevés de kilométrage et de compteur horaire (091)
 * ------------------------------------------------------------------------- */

export type Releve = {
  readonly id: string;
  readonly materielId: string;
  readonly materielCode: string;
  /** En CHAÎNE, comme tous les nombres du produit : un flottant perdrait la décimale. */
  readonly valeur: string;
  readonly unite: string;
  /** Ce que l'OCR avait cru lire, gardé à côté de la valeur validée. */
  readonly valeurLue: string | null;
  /** `ocr | corrige | saisie` — si la lecture automatique a servi. */
  readonly origine: string;
  readonly aPhoto: boolean;
  readonly saisiParLibelle: string;
  readonly releveLe: string;
};

/**
 * Dépose la photo du compteur et se fait proposer une lecture — **sans rien enregistrer**.
 *
 * Deux temps, et c'est la règle de Chris : on ne peut pas enregistrer avant que la personne ait vu
 * ce que l'OCR a lu. `priseLe` vient du serveur et ne se modifie pas — ni l'horloge du téléphone
 * ni la date EXIF ne prouvent quoi que ce soit.
 */
export async function deposerPhotoCompteur(fichier: File): Promise<{
  readonly photo: string;
  readonly priseLe: string;
  readonly lecture: number | null;
  readonly lectureBrute: string | null;
}> {
  const corps = new FormData();
  corps.append("photo", fichier);
  const reponse = await fetch("/api/compteurs/photo", {
    method: "POST",
    body: corps,
    credentials: "same-origin",
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => ({}))) as { erreur?: string };
    throw new Error(erreur.erreur ?? `La photo n'a pas pu être déposée (${String(reponse.status)}).`);
  }
  return (await reponse.json()) as {
    photo: string;
    priseLe: string;
    lecture: number | null;
    lectureBrute: string | null;
  };
}

export function enregistrerReleve(demande: {
  materielId: string;
  valeur: string;
  unite: "km" | "heures";
  photo?: string;
  lecture?: number | null;
  lectureBrute?: string | null;
}): Promise<{ readonly releve: Releve }> {
  return envoyer("/api/compteurs", "POST", demande);
}

export function chargerReleves(materielId?: string): Promise<{ readonly releves: readonly Releve[] }> {
  return obtenir(`/api/compteurs${materielId === undefined ? "" : `?materielId=${materielId}`}`);
}

/* ---------------------------------------------------------------------------
 * La facturation prévisionnelle — le carnet de commandes daté
 * ------------------------------------------------------------------------- */

/** Une échéance : un poste porteur d'une version, son montant, et le mois où il se facture. */
export type LigneFacturation = {
  readonly numero: string;
  readonly designation: string;
  /** Le chapitre qui contient la tâche, ou `null` à la racine : la clé du regroupement du carnet. */
  readonly chapitre: { readonly numero: string; readonly designation: string } | null;
  /** Une tâche hachée se facture une fois par période (25/09/2026) : sa place, et leur nombre. */
  readonly rang: number;
  readonly parts: number;
  readonly montant: string;
  /** `2026-09`, ou `null` quand rien ne permet de le dater. */
  readonly mois: string | null;
  readonly sansMois: "non_planifie" | "sans_date_de_demarrage" | null;
  readonly jourRelatif: number | null;
};

export type ChantierFacture = {
  readonly id: string;
  readonly apo: string;
  readonly nom: string;
  readonly dateDebut: string | null;
  readonly dateDebutStatut: string;
  /** Le chantier porte-t-il les DEUX versions ? Faux tant qu'il n'est pas validé. */
  readonly comparable: boolean;
  readonly prevu: readonly LigneFacturation[];
  readonly enCours: readonly LigneFacturation[];
};

export type CarnetDeFacturation = {
  readonly mois: readonly string[];
  readonly chantiers: readonly ChantierFacture[];
  readonly aujourdhui: string;
};

/**
 * Le carnet de commandes daté, mois par mois.
 *
 * Sans bornes, le serveur applique le mois en cours → +6 mois et **rend les mois qu'il a
 * retenus**. L'écran remplit ses champs avec ceux-là : il n'y a qu'un seul endroit qui sache
 * quel mois on est, et le lire dans le navigateur le lirait dans le fuseau du poste.
 */
export function chargerFacturation(bornes?: {
  debut?: string;
  fin?: string;
}): Promise<CarnetDeFacturation> {
  const parametres = new URLSearchParams();
  if (bornes?.debut !== undefined && bornes.debut !== "") parametres.set("debut", bornes.debut);
  if (bornes?.fin !== undefined && bornes.fin !== "") parametres.set("fin", bornes.fin);
  const requete = parametres.toString();
  return obtenir<CarnetDeFacturation>(
    `/api/facturation-previsionnelle${requete === "" ? "" : `?${requete}`}`,
  );
}

/* ---------------------------------------------------------------------------
 * Déplacer un bloc de la contre-étude (23/09/2026)
 * ------------------------------------------------------------------------- */

export type BlocDeplace = {
  readonly bloc: string;
  readonly ancienParent: string | null;
  readonly nouveauParent: string | null;
  /** Le montant annoncé qui a suivi le bloc. */
  readonly montantDeplace: string;
  /** Les chapitres dont l'écart au fichier vient de changer, du plus proche à la racine. */
  readonly ancetresCorriges: readonly string[];
};

/**
 * Déplace un bloc — chapitre ou tâche — avec tout ce qu'il contient.
 *
 * Le numéro du bloc ne change pas : c'est lui qui apparie la version au devis figé, et le
 * déplacement n'y touche jamais. Seuls le rattachement, l'ordre du parcours et les montants
 * annoncés des chapitres traversés bougent.
 */
export function deplacerUnBloc(
  versionId: string,
  bloc: string,
  pose: { parent: string | null; avant: string | null },
): Promise<BlocDeplace> {
  return envoyer<BlocDeplace>(`/api/versions/${versionId}/deplacer`, "POST", {
    bloc,
    parent: pose.parent,
    avant: pose.avant,
  });
}

/* ---------------------------------------------------------------------------
 * L'assistant de saisie du planning (094, docs/assistant-planning.md)
 * ------------------------------------------------------------------------- */

export type EtatAssistant = {
  readonly configure: boolean;
  readonly modifiable: boolean;
  readonly modele: string | null;
  readonly limite: number;
  readonly utilisees: number;
};

export type ApercuAssistant = {
  readonly lignes: readonly {
    readonly ou: string;
    readonly quoi: string;
    readonly avant: string | null;
    readonly apres: string | null;
  }[];
  readonly totaux: readonly { readonly libelle: string; readonly avant: string; readonly apres: string }[];
};

export type ReponseAssistant =
  | {
      readonly id: string;
      readonly statut: "proposee";
      readonly message: string;
      readonly actions: readonly string[];
      readonly apercu: ApercuAssistant;
    }
  | { readonly id: string; readonly statut: "question"; readonly message: string; readonly candidats: readonly string[] }
  | { readonly id: string; readonly statut: "incomprise" | "refusee"; readonly message: string };

export type ConfirmationAssistant =
  | { readonly statut: "confirmee"; readonly apercu: ApercuAssistant }
  | { readonly statut: "perime"; readonly erreur: string; readonly apercu: ApercuAssistant }
  | { readonly statut: "refusee"; readonly erreur: string };

export function etatAssistant(versionId: string): Promise<EtatAssistant> {
  return obtenir<EtatAssistant>(`/api/versions/${versionId}/assistant`);
}

export function envoyerCommande(versionId: string, texte: string): Promise<ReponseAssistant> {
  return envoyer<ReponseAssistant>(`/api/versions/${versionId}/assistant/commandes`, "POST", { texte });
}

/**
 * Confirmer une commande.
 *
 * Écrit à part de `envoyer` : un 409 n'est pas ici une simple erreur. Quand le planning a bougé
 * depuis l'aperçu, le serveur n'écrit rien et rend L'APERÇU À JOUR dans le corps du refus — il
 * faut le lire, pas seulement en afficher le message.
 */
export async function confirmerCommande(versionId: string, commandeId: string): Promise<ConfirmationAssistant> {
  const { headers, body } = envoiJson({});
  let reponse: Response;
  try {
    reponse = await fetch(`/api/versions/${versionId}/assistant/commandes/${commandeId}/confirmer`, {
      method: "POST",
      headers,
      body,
    });
  } catch {
    throw new Error("Le serveur ne répond pas : rien n'a été écrit.");
  }
  const corps = (await reponse.json().catch(() => null)) as
    | (Partial<ConfirmationAssistant> & { erreur?: string; apercu?: ApercuAssistant })
    | null;
  if (reponse.ok && corps?.statut === "confirmee" && corps.apercu !== undefined) {
    return { statut: "confirmee", apercu: corps.apercu };
  }
  if (reponse.status === 409 && corps?.statut === "perime" && corps.apercu !== undefined) {
    return { statut: "perime", erreur: corps.erreur ?? "", apercu: corps.apercu };
  }
  return { statut: "refusee", erreur: corps?.erreur ?? `Le serveur a refusé (${String(reponse.status)}).` };
}

export function abandonnerCommande(versionId: string, commandeId: string): Promise<unknown> {
  return envoyer<unknown>(`/api/versions/${versionId}/assistant/commandes/${commandeId}/abandonner`, "POST", {});
}
